import * as React from "react";
import { MessageDescriptor } from "react-intl";
import { WrappedMessage, uuid4 } from "../utils";
import Skeleton from "react-loading-skeleton";

interface IconPathProps {
  fill: string;
  outlineColor?: string;
}

interface IconProperties {
  height: number;
  // Paths can be functions in case we need to do anything dynamic to
  // generate the path. For example, creating unique mask IDs.
  // Outline path is an optional alternative for the icon; designed to be solely the outline
  // (for example, a filled star vs just the outline to denote favourited/unfavourited).
  pathOutline?:
    | React.ReactElement
    | ((props: IconPathProps) => React.ReactElement);
  pathSolid:
    | React.ReactElement
    | ((props: IconPathProps) => React.ReactElement);
  viewBox: string;
  width: number;
}

// These are the icons available for use in the UI
// Some are Octicons, some are LabXchange-specific.
/* tslint:disable:max-line-length */
export const ICONS = {
  about: {
    width: 22,
    height: 22,
    viewBox: "0 0 22 22",
    pathSolid: (props: IconPathProps) => (
      <>
        <circle cx="11" cy="11" r="8.5" fill="none" stroke={props.fill} />
        <rect x="10" y="8" width="2" height="9" rx="1" />
        <rect x="10" y="5" width="2" height="2" rx="1" />
      </>
    ),
  },
  academia: {
    height: 36,
    width: 36,
    viewBox: "0 0 36 36",
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M16.4138 13.6282L19.4587 19.8265H13.94032L16.4138 13.6282ZM10.24702 25.0353C9.87454 25.5307 9.38205 25.7983 8.76953 25.8383V26.4615H13.95936V25.8383C13.37995 25.8303 12.94539 25.7824 12.65569 25.6945C12.16734 25.5347 11.92316 25.279 11.92316 24.9274C11.92316 24.6398 12.0349 24.1803 12.25838 23.5491C12.36599 23.2535 12.60603 22.6183 12.9785 21.6435H19.8436L20.4893 23.1296C20.63 23.4572 20.7417 23.7369 20.8245 23.9686C20.9569 24.3601 21.0231 24.6518 21.0231 24.8435C21.0231 25.267 20.8907 25.5307 20.6258 25.6346C20.3692 25.7384 19.8271 25.8063 18.9994 25.8383V26.4615H27.2311V25.8383C26.6517 25.8543 26.2047 25.6545 25.8902 25.239C25.5756 24.8156 25.0997 23.8807 24.4623 22.4345L18.7883 9.53845H16.9267L16.9267 9.53846H15.7498C15.1862 9.53846 14.8139 10.12468 15.0537 10.63484L15.7989 12.22079L11.27753 22.9738C10.83056 24.0365 10.48706 24.7237 10.24702 25.0353Z",
    }),
  },
  alert: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M8.893 1.5c-.183-.31-.52-.5-.887-.5s-.703.19-.886.5L.138 13.499a.98.98 0 0 0 0 1.001c.193.31.53.501.886.501h13.964c.367 0 .704-.19.877-.5a1.03 1.03 0 0 0 .01-1.002L8.893 1.5zm.133 11.497H6.987v-2.003h2.039v2.003zm0-3.004H6.987V5.987h2.039v4.006z",
    }),
    viewBox: "0 0 16 16",
    width: 16,
  },
  "alert-correct-hex": {
    height: 32,
    width: 29,
    viewBox: "0 0 29 32",
    pathSolid: (
      <>
        <path d="M14 0L28 8L28 24L14 32L0 24V8L14 0Z" fill="#84BE60" />
        <path d="M14 0V8V24V32L0 24V8L14 0Z" fill="#84BE60" />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M19.9636 10.8935C20.5355 11.3895 20.5957 12.2557 20.0979 12.8261L13.3735 20.5308C12.8919 21.0826 12.0597 21.1581 11.4862 20.7019L8.07308 17.9872C7.48095 17.5163 7.38398 16.6539 7.85671 16.0632C8.32745 15.475 9.18534 15.3785 9.77495 15.8475L12.1685 17.7513L18.0369 11.0274C18.5325 10.4595 19.3942 10.3996 19.9636 10.8935Z"
          fill="white"
        />
      </>
    ),
  },
  "alert-correct-round": {
    height: 20,
    width: 20,
    viewBox: "0 0 20 20",
    pathSolid: (
      <>
        <rect width="20" height="20" rx="10" fill="#84BE60" />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M15.483 5.962C16.0511 6.45844 16.1257 7.31485 15.6522 7.90209L9.53621 15.4861C9.04786 16.0917 8.18168 16.1737 7.59635 15.6699L4.53116 13.0314C3.9552 12.5356 3.87356 11.6735 4.34623 11.0784V11.0784C4.84604 10.4492 5.76885 10.3618 6.37789 10.886L8.30081 12.5413L13.4646 6.13796C13.9654 5.5169 14.8823 5.43697 15.483 5.962V5.962Z"
          fill="white"
        />
      </>
    ),
  },
  "alert-fail-hex": {
    height: 32,
    width: 29,
    viewBox: "0 0 29 32",
    pathSolid: (
      <>
        <path d="M14 0L28 8L28 24L14 32L0 24V8L14 0Z" fill="#FAAC48" />
        <path d="M14 0L28 8L28 24L14 32L0 24V8L14 0Z" fill="#ED3431" />
        <rect
          x="9.6001"
          y="22"
          width="2.26274"
          height="14.7078"
          rx="1.13137"
          transform="rotate(-135 9.6001 22)"
          fill="white"
        />
        <rect
          x="8"
          y="11.6"
          width="2.26274"
          height="14.7078"
          rx="1.13137"
          transform="rotate(-45 8 11.6)"
          fill="white"
        />
      </>
    ),
  },
  "alert-fail-round": {
    height: 20,
    width: 20,
    viewBox: "0 0 20 20",
    pathSolid: (
      <>
        <rect width="20" height="20" rx="10" fill="#ED3431" />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M13.2931 15.1172C13.7981 15.622 14.6169 15.622 15.1219 15.1172C15.627 14.6123 15.627 13.7937 15.1219 13.2889L11.8313 9.99929L15.1205 6.71114C15.6255 6.20627 15.6255 5.38771 15.1205 4.88284C14.6155 4.37796 13.7967 4.37796 13.2916 4.88284L10.0024 8.17099L6.70908 4.87865C6.20404 4.37378 5.38523 4.37378 4.88019 4.87866C4.37516 5.38353 4.37516 6.20209 4.88019 6.70696L8.17356 9.99929L4.87877 13.293C4.37374 13.7979 4.37374 14.6165 4.87877 15.1214C5.38381 15.6262 6.20263 15.6262 6.70766 15.1214L10.0024 11.8276L13.2931 15.1172Z"
          fill="white"
        />
      </>
    ),
  },
  "alert-info-hex": {
    height: 32,
    width: 29,
    viewBox: "0 0 29 32",
    pathSolid: (
      <>
        <path d="M14 0L28 8L28 24L14 32L0 24V8L14 0Z" fill="#0063C3" />
        <path
          d="M12.5001 17.6667C12.5277 18.1997 13.2992 19 14 19C14.7009 19 15.4724 18.1997 15.5 17.6667L15.9999 12.5346L15.9999 9C16.0107 8.24429 14.9931 7 14 7C13.007 7 11.9893 8.24429 12.0001 9L12.0002 12.5346L12.5001 17.6667Z"
          fill="white"
        />
        <circle cx="14" cy="23" r="2" fill="white" />
      </>
    ),
  },
  "alert-info-hex-2": {
    height: 20,
    width: 20,
    viewBox: "0 0 20 20",
    pathSolid: (
      <>
        <path d="M10 0L18.6603 5V15L10 20L1.33975 15V5L10 0Z" fill="#ED3431" />
        <circle cx="10" cy="15" r="2" fill="white" />
        <path
          d="M8.22086 4.98777C8.10304 3.92738 8.93309 3 10 3C11.0669 3 11.897 3.92738 11.7791 4.98777L11.1104 11.0061C11.0476 11.5719 10.5693 12 10 12C9.4307 12 8.95244 11.5719 8.88957 11.0061L8.22086 4.98777Z"
          fill="white"
        />
      </>
    ),
  },
  "apple-educator": {
    width: 22,
    height: 22,
    viewBox: "0 0 22 22",
    pathSolid: (
      <>
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M10.9996 15.8044L11.6745 16.4217C11.8823 16.6117 12.1275 16.7546 12.3937 16.841L12.406 16.845L12.406 16.8451C13.1676 17.1035 14.0006 17.0364 14.7115 16.6599C15.4314 16.189 16.0225 15.5424 16.4292 14.7793L16.439 14.761L16.4495 14.7432C17.2026 13.4612 17.8043 11.9248 17.9604 10.4392C18.1153 8.9658 17.8294 7.61269 16.9049 6.54481L16.9044 6.54422C16.1205 5.63724 14.9944 5 13.9527 5C13.0367 5 12.4377 5.1834 12.0934 5.33737C11.9194 5.41516 11.805 5.48791 11.7435 5.53161C11.7176 5.54999 11.7009 5.56336 11.6929 5.56997L10.9996 6.26761L10.3063 5.56998C10.2983 5.56336 10.2816 5.54999 10.2557 5.53161C10.1942 5.48791 10.0798 5.41516 9.90582 5.33737C9.56153 5.1834 8.96254 5 8.04645 5C7.00447 5 5.87735 5.63757 5.0958 6.54351L5.09481 6.54467C4.17068 7.61259 3.88478 8.9657 4.03953 10.4391C4.19556 11.9247 4.79706 13.461 5.55017 14.7432L5.56066 14.7611L5.57039 14.7794C5.97708 15.5424 6.56801 16.189 7.28772 16.66C7.99867 17.0364 8.83165 17.1035 9.59324 16.8451L9.60542 16.841L9.60545 16.8411C9.87172 16.7545 10.117 16.6117 10.3247 16.4217L10.9996 15.8044ZM6.77786 17.5212C5.89996 16.9559 5.18048 16.1739 4.6879 15.2497C3.08017 12.5125 2.031 8.55702 4.33862 5.89031C5.24608 4.8384 6.62817 4 8.04645 4C10.1559 4 10.9996 4.84899 10.9996 4.84899C10.9996 4.84899 11.8433 4 13.9527 4C15.371 4 16.7518 4.8384 17.661 5.89031C19.9695 8.55702 18.9195 12.5129 17.3117 15.2497C16.8191 16.174 16.0994 16.956 15.2213 17.5212C14.2585 18.046 13.1222 18.1441 12.0847 17.7921C11.6823 17.6614 11.3124 17.4457 10.9996 17.1596C10.6868 17.4457 10.3169 17.6613 9.91455 17.7921C8.87697 18.1441 7.74066 18.046 6.77786 17.5212Z"
          fill="#003E6B"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M4 17H17V22H4V17ZM5 21H16V18H5V21Z"
          fill="#003E6B"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M4 21V18H2V21H4ZM5 17H1.66667C1 17 1 17.3182 1 17.625V21.4444C1 21.7513 1.11938 22 1.26666 22H5V17Z"
          fill="#003E6B"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M20 18H17V21H20V18ZM21 21.4444V17.5556C21 17.2487 20.8278 17 20.6154 17H16V22H20.6154C20.8278 22 21 21.7513 21 21.4444Z"
          fill="#003E6B"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M12.0024 4.9601C11.4279 5 11 5 11 5C11 5 11 4.57275 11.1735 3.99751C11.531 2.81267 12.6246 1 15.9708 1C16.0111 1.27924 16.0083 1.56532 15.9708 1.8494C15.9642 1.89969 15.9565 1.94992 15.9477 2.00003C15.7962 2.86577 15.3329 3.69808 14.7914 4.244C14.3228 4.71642 12.9593 4.89363 12.0024 4.9601ZM12.2582 3.93642C12.339 3.92902 12.4214 3.92077 12.5045 3.91157C12.9064 3.8671 13.2998 3.80332 13.62 3.71617C13.9283 3.63228 14.0528 3.55665 14.0773 3.54174C14.0816 3.53912 14.0829 3.53837 14.0815 3.53977C14.453 3.16521 14.7598 2.62563 14.9066 2.07804C13.4916 2.30189 12.8197 2.9661 12.4707 3.52777C12.3857 3.66462 12.3157 3.80239 12.2582 3.93642Z"
          fill="#003E6B"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M13.0914 19C13.2568 19 13.3908 19.2239 13.3908 19.5ZM8.29949 20H13.0914C13.2567 20 13.3909 19.7761 13.3908 19.5M8.29949 20C8.13406 20 8 19.7761 8 19.5ZM8 19.5C8 19.2239 8.13406 19 8.29949 19ZM8.29949 19H13.0914Z"
          fill="#003E6B"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M6.5 1C6.5 0.723858 6.72386 0.5 7 0.5C8.20379 0.5 9.33366 0.868204 10.1677 1.6386C11.0082 2.41496 11.5 3.55555 11.5 5C11.5 5.27614 11.2761 5.5 11 5.5C10.7239 5.5 10.5 5.27614 10.5 5C10.5 3.79348 10.0964 2.93407 9.48916 2.37317C8.87548 1.80631 8.00535 1.5 7 1.5C6.72386 1.5 6.5 1.27614 6.5 1Z"
          fill="#003E6B"
        />
      </>
    ),
  },
  "apple-educator-white": {
    width: 22,
    height: 22,
    viewBox: "0 0 22 22",
    pathSolid: (
      <>
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M10.9996 15.8044L11.6745 16.4217C11.8823 16.6117 12.1275 16.7546 12.3937 16.841L12.406 16.845L12.406 16.8451C13.1676 17.1035 14.0006 17.0364 14.7115 16.6599C15.4314 16.189 16.0225 15.5424 16.4292 14.7793L16.439 14.761L16.4495 14.7432C17.2026 13.4612 17.8043 11.9248 17.9604 10.4392C18.1153 8.9658 17.8294 7.61269 16.9049 6.54481L16.9044 6.54422C16.1205 5.63724 14.9944 5 13.9527 5C13.0367 5 12.4377 5.1834 12.0934 5.33737C11.9194 5.41516 11.805 5.48791 11.7435 5.53161C11.7176 5.54999 11.7009 5.56336 11.6929 5.56997L10.9996 6.26761L10.3063 5.56998C10.2983 5.56336 10.2816 5.54999 10.2557 5.53161C10.1942 5.48791 10.0798 5.41516 9.90582 5.33737C9.56153 5.1834 8.96254 5 8.04645 5C7.00447 5 5.87735 5.63757 5.0958 6.54351L5.09481 6.54467C4.17068 7.61259 3.88478 8.9657 4.03953 10.4391C4.19556 11.9247 4.79706 13.461 5.55017 14.7432L5.56066 14.7611L5.57039 14.7794C5.97708 15.5424 6.56801 16.189 7.28772 16.66C7.99867 17.0364 8.83165 17.1035 9.59324 16.8451L9.60542 16.841L9.60545 16.8411C9.87172 16.7545 10.117 16.6117 10.3247 16.4217L10.9996 15.8044ZM6.77786 17.5212C5.89996 16.9559 5.18048 16.1739 4.6879 15.2497C3.08017 12.5125 2.031 8.55702 4.33862 5.89031C5.24608 4.8384 6.62817 4 8.04645 4C10.1559 4 10.9996 4.84899 10.9996 4.84899C10.9996 4.84899 11.8433 4 13.9527 4C15.371 4 16.7518 4.8384 17.661 5.89031C19.9695 8.55702 18.9195 12.5129 17.3117 15.2497C16.8191 16.174 16.0994 16.956 15.2213 17.5212C14.2585 18.046 13.1222 18.1441 12.0847 17.7921C11.6823 17.6614 11.3124 17.4457 10.9996 17.1596C10.6868 17.4457 10.3169 17.6613 9.91455 17.7921C8.87697 18.1441 7.74066 18.046 6.77786 17.5212Z"
          fill="white"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M4 17H17V22H4V17ZM5 21H16V18H5V21Z"
          fill="white"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M4 21V18H2V21H4ZM5 17H1.66667C1 17 1 17.3182 1 17.625V21.4444C1 21.7513 1.11938 22 1.26666 22H5V17Z"
          fill="white"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M20 18H17V21H20V18ZM21 21.4444V17.5556C21 17.2487 20.8278 17 20.6154 17H16V22H20.6154C20.8278 22 21 21.7513 21 21.4444Z"
          fill="white"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M12.0024 4.9601C11.4279 5 11 5 11 5C11 5 11 4.57275 11.1735 3.99751C11.531 2.81267 12.6246 1 15.9708 1C16.0111 1.27924 16.0083 1.56532 15.9708 1.8494C15.9642 1.89969 15.9565 1.94992 15.9477 2.00003C15.7962 2.86577 15.3329 3.69808 14.7914 4.244C14.3228 4.71642 12.9593 4.89363 12.0024 4.9601ZM12.2582 3.93642C12.339 3.92902 12.4214 3.92077 12.5045 3.91157C12.9064 3.8671 13.2998 3.80332 13.62 3.71617C13.9283 3.63228 14.0528 3.55665 14.0773 3.54174C14.0816 3.53912 14.0829 3.53837 14.0815 3.53977C14.453 3.16521 14.7598 2.62563 14.9066 2.07804C13.4916 2.30189 12.8197 2.9661 12.4707 3.52777C12.3857 3.66462 12.3157 3.80239 12.2582 3.93642Z"
          fill="white"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M13.0914 19C13.2568 19 13.3908 19.2239 13.3908 19.5ZM8.29949 20H13.0914C13.2567 20 13.3909 19.7761 13.3908 19.5M8.29949 20C8.13406 20 8 19.7761 8 19.5ZM8 19.5C8 19.2239 8.13406 19 8.29949 19ZM8.29949 19H13.0914Z"
          fill="white"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M6.5 1C6.5 0.723858 6.72386 0.5 7 0.5C8.20379 0.5 9.33366 0.868204 10.1677 1.6386C11.0082 2.41496 11.5 3.55555 11.5 5C11.5 5.27614 11.2761 5.5 11 5.5C10.7239 5.5 10.5 5.27614 10.5 5C10.5 3.79348 10.0964 2.93407 9.48916 2.37317C8.87548 1.80631 8.00535 1.5 7 1.5C6.72386 1.5 6.5 1.27614 6.5 1Z"
          fill="white"
        />
      </>
    ),
  },
  archive: {
    width: 12,
    height: 10,
    pathSolid: (
      <path
        fillRule="evenodd"
        d="M0.5 0C0.223858 0 0 0.223858 0 0.5C0 0.776142 0.223858 1 0.5 1H11.5C11.7761 1 12 0.776142 12 0.5C12 0.223858 11.7761 0 11.5 0H0.5ZM1 2H11V9C11 9.55228 10.5523 10 10 10H2C1.44772 10 1 9.55228 1 9V2ZM4 4H8V6H4V4Z"
      />
    ),
    viewBox: "0 0 12 10",
  },
  "arrow-down": {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M7 7V3H3v4H0l5 6 5-6H7z",
    }),
    viewBox: "0 0 10 16",
    width: 10,
  },
  "arrow-left": {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M6 3L0 8l6 5v-3h4V6H6V3z",
    }),
    viewBox: "0 0 10 16",
    width: 10,
  },
  "arrow-link-tilted": {
    height: 20,
    pathSolid: (
      <path
        fill="#003E6B"
        d="M6.576 12.576a.6.6 0 00.849.848l-.849-.848zM13.6 7a.6.6 0 00-.6-.6H7.6a.6.6 0 100 1.2h4.8v4.8a.6.6 0 001.2 0V7zm-6.175 6.424l6-6-.849-.848-6 6 .849.848z"
      />
    ),
    viewBox: "0 0 20 20",
    width: 20,
  },
  "arrow-right": {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M10 8L4 3v3H0v4h4v3l6-5z",
    }),
    viewBox: "0 0 10 16",
    width: 10,
  },
  "arrow-right-long": {
    height: 24,
    pathSolid: (
      <path
        fill="#003E6B"
        d="M51.06 13.06a1.5 1.5 0 000-2.12l-9.545-9.547a1.5 1.5 0 10-2.122 2.122L47.88 12l-8.486 8.485a1.5 1.5 0 102.122 2.122l9.546-9.546zM0 13.5h50v-3H0v3z"
      />
    ),
    viewBox: "0 0 52 24",
    width: 52,
  },
  "arrow-right-thin": {
    height: 16,
    pathSolid: (
      <path
        d="M14.7071 8.70711C15.0976 8.31658 15.0976 7.68342 14.7071 7.29289L8.34315 0.928932C7.95262 0.538408 7.31946 0.538408 6.92893 0.928932C6.53841 1.31946 6.53841 1.95262 6.92893 2.34315L12.5858 8L6.92893 13.6569C6.53841 14.0474 6.53841 14.6805 6.92893 15.0711C7.31946 15.4616 7.95262 15.4616 8.34315 15.0711L14.7071 8.70711ZM0.999999 7C0.447714 7 -9.53674e-07 7.44772 -9.53674e-07 8C-9.53674e-07 8.55228 0.447714 9 0.999999 9V7ZM14 7L0.999999 7V9L14 9V7Z"
        fill="white"
      />
    ),
    viewBox: "0 0 15 16",
    width: 15,
  },
  "arrow-small-down": {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M4 7V5H2v2H0l3 4 3-4H4z",
    }),
    viewBox: "0 0 6 16",
    width: 6,
  },
  "arrow-small-left": {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M4 7V5L0 8l4 3V9h2V7H4z",
    }),
    viewBox: "0 0 6 16",
    width: 6,
  },
  "arrow-small-right": {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M6 8L2 5v2H0v2h2v2l4-3z",
    }),
    viewBox: "0 0 6 16",
    width: 6,
  },
  "arrow-small-up": {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M3 5L0 9h2v2h2V9h2L3 5z",
    }),
    viewBox: "0 0 6 16",
    width: 6,
  },
  "arrow-up": {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M5 3L0 9h3v4h4V9h3L5 3z",
    }),
    viewBox: "0 0 10 16",
    width: 10,
  },
  "arrow-up-tailed": {
    width: 16,
    height: 16,
    pathSolid: (
      <path
        d="M14.863 6.076l-5.63-4.252a.317.317 0 00-.487 0L3.117 6.076a.365.365 0 00.244.634H6v4.5a.56.56 0 00.487.463h5.27c.194 0 .243-.244.243-.463v-4.5h2.62a.365.365 0 00.243-.634zM11.605 16.71h-5.21c-.218 0-.395.224-.395.5s.177.5.395.5h5.21c.218 0 .395-.224.395-.5s-.177-.5-.395-.5zM11.605 14.71h-5.21c-.218 0-.395.224-.395.5s.177.5.395.5h5.21c.218 0 .395-.224.395-.5s-.177-.5-.395-.5zM11.605 12.71h-5.21c-.218 0-.395.224-.395.5s.177.5.395.5h5.21c.218 0 .395-.224.395-.5s-.177-.5-.395-.5z"
        fill="#333"
      />
    ),
    viewBox: "0 0 16 18",
  },
  assessment: {
    height: 16,
    width: 16,
    viewBox: "0 0 16 16",
    pathSolid: (
      <path
        fillRule="evenodd"
        d="M13 6C13 2.85955 10.4684 1 7.66207 1C4.32117 1 1.64844 3.67273 1.64844 7.08045C1.64844 9.21864 2.39107 10.9309 3.99471 12V14.65C3.99471 14.8505 4.11824 15 4.31869 15H10.5C11 15 11 14.7005 11 14.5V13H12.0002C12.5525 13 13 12.5525 13 12.0002V10C13.3341 10 14.5 9.66818 14.5 9C14.4614 8.34428 14.1011 7.82443 13.7263 7.28358C13.4518 6.88751 13.1695 6.48019 13 6ZM4.5 4C4.22386 4 4 4.22386 4 4.5V9.5C4 9.77614 4.22386 10 4.5 10H9.5C9.77614 10 10 9.77614 10 9.5V7.12404L8.54373 8.95331C8.14994 9.44797 7.43832 9.52028 6.95935 9.10264L5.30289 7.65824C4.84464 7.25867 4.77949 6.57025 5.15465 6.09181C5.55851 5.57674 6.31164 5.50547 6.80495 5.93563L7.536 6.57309L9.58441 4H9.5H4.5ZM10.6541 3.8605C10.7916 3.68776 11.0458 3.66561 11.2111 3.81196C11.3647 3.94788 11.3849 4.1802 11.2572 4.34062L7.95696 8.48619C7.82484 8.65216 7.60366 8.66937 7.45226 8.53736L5.79579 7.09296C5.6383 6.95563 5.61591 6.71903 5.74484 6.55459C5.88365 6.37757 6.1425 6.35307 6.31205 6.50091L7.33868 7.39611C7.41583 7.46338 7.51714 7.49619 7.61908 7.48693C7.72103 7.47766 7.81476 7.42711 7.87852 7.34703L10.6541 3.8605Z"
      />
    ),
  },
  assign: {
    height: 18,
    width: 19,
    viewBox: "0 0 19 18",
    pathSolid: (
      <>
        <path
          d="M7.97953 11.664C8.96189 11.1275 9.64107 9.97633 9.64107 8.64281C9.64107 6.79661 8.33925 5.29996 6.73337 5.29996C5.1275 5.29996 3.82568 6.79661 3.82568 8.64281C3.82568 9.97633 4.50486 11.1275 5.48722 11.664V11.9857C5.32357 12.125 4.7697 12.4035 3.29701 12.8214C2.11883 13.1557 1.33337 13.6571 1.33337 14.4928V16.1642C1.33337 16.6258 1.70532 17 2.16414 17L11.5026 17.0072C11.9614 17.0072 12.3334 16.633 12.3334 16.1715V14.5C12.3334 13.6643 11.3479 13.1557 10.1697 12.8214C8.69704 12.4035 8.14318 12.125 7.97953 11.9857L7.97953 11.664Z"
          fill="#003E6B"
        />
        <rect x="11.3334" y="1" width="6" height="8" rx="1" fill="#003E6B" />
        <rect x="12.3334" y="2" width="3" height="1" rx="0.5" fill="white" />
        <rect x="12.3334" y="4" width="4" height="1" rx="0.5" fill="white" />
        <rect x="12.3334" y="6" width="4" height="1" rx="0.5" fill="white" />
      </>
    ),
  },
  audio: {
    height: 16,
    width: 16,
    viewBox: "0 0 16 16",
    pathSolid: () => {
      const maskId = uuid4();
      return (
        <>
          <mask id={maskId} fill="none">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M5 3L15 0V2V3V11C15 12.6569 13.6569 14 12 14C10.3431 14 9 12.6569 9 11C9 9.34315 10.3431 8 12 8C12.3506 8 12.6872 8.06015 13 8.17071V3.6L7 5.4V13C7 14.6569 5.65685 16 4 16C2.34315 16 1 14.6569 1 13C1 11.3431 2.34315 10 4 10C4.35064 10 4.68722 10.0602 5 10.1707V6V4V3Z"
            />
          </mask>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M5 3L15 0V2V3V11C15 12.6569 13.6569 14 12 14C10.3431 14 9 12.6569 9 11C9 9.34315 10.3431 8 12 8C12.3506 8 12.6872 8.06015 13 8.17071V3.6L7 5.4V13C7 14.6569 5.65685 16 4 16C2.34315 16 1 14.6569 1 13C1 11.3431 2.34315 10 4 10C4.35064 10 4.68722 10.0602 5 10.1707V6V4V3Z"
          />
          <path
            mask={`url(#${maskId})`}
            d="M15 0H16V-1.34403L14.7127 -0.957826L15 0ZM5 3L4.71265 2.04217L4 2.25597V3H5ZM13 8.17071L12.6668 9.11355L14 9.58478V8.17071H13ZM13 3.6H14V2.25597L12.7127 2.64217L13 3.6ZM7 5.4L6.71265 4.44217L6 4.65597V5.4H7ZM5 10.1707L4.66675 11.1135L6 11.5848V10.1707H5ZM14.7127 -0.957826L4.71265 2.04217L5.28735 3.95783L15.2873 0.957826L14.7127 -0.957826ZM16 2V0H14V2H16ZM14 2V3H16V2H14ZM14 3V11H16V3H14ZM12 15C14.2091 15 16 13.2091 16 11H14C14 12.1046 13.1046 13 12 13V15ZM8 11C8 13.2091 9.79086 15 12 15V13C10.8954 13 10 12.1046 10 11H8ZM12 7C9.79086 7 8 8.79086 8 11H10C10 9.89543 10.8954 9 12 9V7ZM13.3332 7.22787C12.9149 7.08001 12.4657 7 12 7V9C12.2355 9 12.4595 9.0403 12.6668 9.11355L13.3332 7.22787ZM14 8.17071V3.6H12V8.17071H14ZM7.28735 6.35783L13.2873 4.55783L12.7127 2.64217L6.71265 4.44217L7.28735 6.35783ZM8 13V5.4H6V13H8ZM4 17C6.20914 17 8 15.2091 8 13H6C6 14.1046 5.10457 15 4 15V17ZM0 13C0 15.2091 1.79086 17 4 17V15C2.89543 15 2 14.1046 2 13H0ZM4 9C1.79086 9 0 10.7909 0 13H2C2 11.8954 2.89543 11 4 11V9ZM5.33325 9.22787C4.91492 9.08001 4.46573 9 4 9V11C4.23555 11 4.45952 11.0403 4.66675 11.1135L5.33325 9.22787ZM4 6V10.1707H6V6H4ZM4 4V6H6V4H4ZM4 3V4H6V3H4Z"
          />
        </>
      );
    },
  },
  "background-knowledge": {
    height: 16,
    width: 16,
    viewBox: "0 0 16 16",
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M7 1.00003H1L2.33332 5H7V1.00003ZM3.99999 10L2.99999 7H13L12 10H3.99999ZM4.66666 12L5.66667 15H10.3333L11.3333 12H4.66666ZM15 1.00003L13.6667 5H9V1.00003H15Z",
    }),
  },
  badge: {
    height: 16,
    pathSolid: (
      <>
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M7.09392 12.0757C10.3612 12.0757 13.0099 9.37247 13.0099 6.03785C13.0099 2.70324 10.3612 0 7.09392 0C3.82663 0 1.17798 2.70324 1.17798 6.03785C1.17798 9.37247 3.82663 12.0757 7.09392 12.0757ZM6.73891 6.09522L4.68967 9.58949H6.38029L8.03774 6.7633L9.09086 8.56134H10.2925L8.83937 6.08041L10.2925 3.51462H9.09086L8.02894 5.3897L6.38029 2.48613H4.68967L6.73891 6.09522Z"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M7.98989 12.9828L9.69682 16.0002L11.0362 13.5377L13.7955 13.585L12.1837 10.7358C11.1169 11.9391 9.64621 12.7623 7.98989 12.9828ZM6.49176 13.0154C4.81511 12.8666 3.31109 12.1026 2.19768 10.9456L0.70459 13.585L3.46384 13.5377L4.80327 16.0002L6.49176 13.0154Z"
        />
      </>
    ),
    viewBox: "0 0 14 16",
    width: 14,
  },
  beaker: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M14.38 14.59L11 7V3h1V2H3v1h1v4L.63 14.59A1 1 0 0 0 1.54 16h11.94c.72 0 1.2-.75.91-1.41h-.01zM3.75 10L5 7V3h5v4l1.25 3h-7.5zM8 8h1v1H8V8zM7 7H6V6h1v1zm0-3h1v1H7V4zm0-3H6V0h1v1z",
    }),
    viewBox: "0 0 16 16",
    width: 16,
  },
  bell: {
    height: 16,
    pathOutline: React.createElement("path", {
      d: "M15.2586 4.36572L14.7708 4.25613L14.6723 4.6946L15.0978 4.83915L15.2586 4.36572ZM11.1922 4.23026L11.319 4.7139L11.7523 4.60026L11.6868 4.15716L11.1922 4.23026ZM6.20588 9.71L6.69564 9.8107L6.6957 9.81042L6.20588 9.71ZM5.08356 15.1685L4.5938 15.0678L4.59376 15.068L5.08356 15.1685ZM3.68666 16.9786L3.43484 16.5466L3.43474 16.5467L3.68666 16.9786ZM3.63771 17.0071L3.38579 16.5752L3.38574 16.5753L3.63771 17.0071ZM3.50542 17.0834L3.25669 16.6497L3.25398 16.6512L3.50542 17.0834ZM3.50527 17.0835L3.75401 17.5173L3.75671 17.5157L3.50527 17.0835ZM2.57621 17.7539L2.21569 17.4075L2.21554 17.4076L2.57621 17.7539ZM10.0518 21.163L10.549 21.2153L10.6072 20.663H10.0518V21.163ZM10.0532 21.342L10.5525 21.3144L10.5525 21.3144L10.0532 21.342ZM10.0578 21.4459H10.558L10.5577 21.4342L10.0578 21.4459ZM15.9418 21.4459L15.4418 21.4388V21.4459H15.9418ZM15.943 21.384L15.4431 21.3727L15.4431 21.3727L15.943 21.384ZM15.9363 21.163V20.663H15.349L15.4428 21.2428L15.9363 21.163ZM23.4238 17.7539L23.7845 17.4076L23.7841 17.4073L23.4238 17.7539ZM22.4945 17.0836L22.7433 16.6498L22.7433 16.6498L22.4945 17.0836ZM22.362 17.0071L22.11 17.439L22.11 17.439L22.362 17.0071ZM22.3124 16.9782L22.5644 16.5464L22.5643 16.5463L22.3124 16.9782ZM20.9164 15.1685L21.4063 15.0682L21.4062 15.0678L20.9164 15.1685ZM19.7941 9.71L19.3043 9.81047L19.3044 9.8107L19.7941 9.71ZM14.8039 3.95651C14.8039 4.05968 14.7924 4.15983 14.7708 4.25613L15.7465 4.47531C15.7841 4.30768 15.8039 4.13404 15.8039 3.95651H14.8039ZM13.2382 2.5C14.1287 2.5 14.8039 3.17716 14.8039 3.95651H15.8039C15.8039 2.57427 14.629 1.5 13.2382 1.5V2.5ZM11.6721 3.95651C11.6721 3.17723 12.3472 2.5 13.2382 2.5V1.5C11.8471 1.5 10.6721 2.57419 10.6721 3.95651H11.6721ZM11.6868 4.15716C11.6771 4.09181 11.6721 4.02485 11.6721 3.95651H10.6721C10.6721 4.07398 10.6807 4.18981 10.6975 4.30337L11.6868 4.15716ZM6.6957 9.81042C7.17665 7.46438 8.83051 5.3666 11.319 4.7139L11.0653 3.74662C8.1482 4.51174 6.26003 6.95624 5.71607 9.60959L6.6957 9.81042ZM5.57331 15.2692L6.69564 9.8107L5.71613 9.60931L4.5938 15.0678L5.57331 15.2692ZM3.93847 17.4105C4.25556 17.2257 4.61661 17.0197 4.9153 16.6894C5.2264 16.3454 5.44374 15.9007 5.57336 15.269L4.59376 15.068C4.49131 15.5673 4.33871 15.8361 4.17363 16.0187C3.99613 16.2149 3.77476 16.3485 3.43484 16.5466L3.93847 17.4105ZM3.88962 17.439L3.93857 17.4105L3.43474 16.5467L3.38579 16.5752L3.88962 17.439ZM3.75415 17.5172C3.79854 17.4917 3.84433 17.4654 3.88967 17.439L3.38574 16.5753C3.34417 16.5995 3.30165 16.6239 3.2567 16.6497L3.75415 17.5172ZM3.75671 17.5157L3.75687 17.5156L3.25398 16.6512L3.25382 16.6513L3.75671 17.5157ZM2.93672 18.1004C3.17524 17.8521 3.44443 17.6948 3.754 17.5172L3.25653 16.6498C2.95575 16.8223 2.56635 17.0426 2.21569 17.4075L2.93672 18.1004ZM2.5 19.2478C2.5 18.7594 2.6879 18.3595 2.93687 18.1002L2.21554 17.4076C1.78898 17.8519 1.5 18.4994 1.5 19.2478H2.5ZM2.5 20.5501V19.2478H1.5V20.5501H2.5ZM2.64706 20.663C2.53978 20.663 2.5 20.5874 2.5 20.5501H1.5C1.5 21.1901 2.03934 21.663 2.64706 21.663V20.663ZM10.0518 20.663H2.64706V21.663H10.0518V20.663ZM10.5525 21.3144C10.5506 21.2797 10.5494 21.2572 10.549 21.2376C10.5486 21.2184 10.5493 21.2129 10.549 21.2153L9.55453 21.1106C9.54394 21.2113 9.55102 21.3157 9.554 21.3696L10.5525 21.3144ZM10.5577 21.4342C10.5567 21.3894 10.5543 21.3468 10.5525 21.3144L9.554 21.3696C9.55591 21.4043 9.55736 21.4313 9.55798 21.4577L10.5577 21.4342ZM10.5578 21.5476V21.4459H9.55785V21.5476H10.5578ZM12.9998 23.5002C11.5931 23.5002 10.5578 22.574 10.5578 21.5476H9.55785C9.55785 23.3059 11.2395 24.5002 12.9998 24.5002V23.5002ZM15.4418 21.5476C15.4418 22.574 14.4065 23.5002 12.9998 23.5002V24.5002C14.7602 24.5002 16.4418 23.3059 16.4418 21.5476H15.4418ZM15.4418 21.4459V21.5476H16.4418V21.4459H15.4418ZM15.4431 21.3727C15.4427 21.3926 15.4422 21.4154 15.4418 21.4388L16.4417 21.4531C16.442 21.4346 16.4424 21.416 16.4429 21.3954L15.4431 21.3727ZM15.4428 21.2428C15.4425 21.2413 15.4439 21.2495 15.4443 21.2748C15.4447 21.3002 15.4441 21.3294 15.4431 21.3727L16.4429 21.3954C16.4444 21.3276 16.449 21.2013 16.4299 21.0832L15.4428 21.2428ZM23.3529 20.663H15.9363V21.663H23.3529V20.663ZM23.5 20.5501C23.5 20.5872 23.4601 20.663 23.3529 20.663V21.663C23.9602 21.663 24.5 21.1903 24.5 20.5501H23.5ZM23.5 19.2478V20.5501H24.5V19.2478H23.5ZM23.0631 18.1002C23.3121 18.3595 23.5 18.7594 23.5 19.2478H24.5C24.5 18.4994 24.211 17.8519 23.7845 17.4076L23.0631 18.1002ZM22.2458 17.5173C22.5553 17.6948 22.8246 17.8522 23.0634 18.1005L23.7841 17.4073C23.4333 17.0426 23.044 16.8223 22.7433 16.6498L22.2458 17.5173ZM22.11 17.439C22.1554 17.4655 22.2013 17.4918 22.2458 17.5173L22.7433 16.6498C22.6982 16.624 22.6556 16.5996 22.6139 16.5753L22.11 17.439ZM22.0604 17.4101L22.11 17.439L22.614 16.5753L22.5644 16.5464L22.0604 17.4101ZM20.4266 15.2689C20.556 15.9005 20.7733 16.3451 21.0843 16.6891C21.3828 17.0194 21.7437 17.2254 22.0605 17.4101L22.5643 16.5463C22.2247 16.3482 22.0035 16.2147 21.8261 16.0185C21.6611 15.836 21.5085 15.5672 21.4063 15.0682L20.4266 15.2689ZM19.3044 9.8107L20.4267 15.2692L21.4062 15.0678L20.2839 9.60931L19.3044 9.8107ZM15.0978 4.83915C17.3525 5.60505 18.8505 7.59785 19.3043 9.81047L20.2839 9.60954C19.7705 7.10633 18.0622 4.79001 15.4195 3.89229L15.0978 4.83915Z",
    }),
    pathSolid: (props: IconPathProps) => {
      const outlineColor = props.outlineColor || props.fill;
      return (
        <>
          <path
            fill={props.fill ? props.fill : "currentColor"}
            fillRule="evenodd"
            clipRule="evenodd"
            d="M15.9099 4.47335C15.9399 4.33536 15.9557 4.19221 15.9557 4.04545C15.9557 2.91552 15.0212 2 13.8689 2C12.7162 2 11.7817 2.91552 11.7817 4.04545C11.7817 4.14271 11.7887 4.23839 11.802 4.33203C9.07194 5.0733 7.28303 7.44757 6.76537 10.0608L5.6316 15.7674C5.39717 16.9498 4.88416 17.2593 4.22046 17.6597L4.17101 17.6896C4.12706 17.7161 4.08239 17.7426 4.03721 17.7694C3.72892 17.9524 3.39628 18.1498 3.09868 18.4703C2.75747 18.8381 2.5166 19.3856 2.5166 20.0321V21.3936C2.5166 21.7476 2.80911 22.0344 3.17026 22.0344H10.6517C10.6461 22.0893 10.6495 22.1528 10.6532 22.2212V22.2212C10.655 22.2563 10.657 22.2926 10.6578 22.3298V22.4362C10.6578 23.8918 12.0301 25.0002 13.6298 25.0002C15.2294 25.0002 16.6017 23.8918 16.6017 22.4362V22.3298C16.6021 22.3079 16.6025 22.2863 16.603 22.2651C16.6049 22.1795 16.6066 22.1005 16.5963 22.0344H24.0873C24.4481 22.0344 24.7409 21.7476 24.7409 21.3936V20.0321C24.7409 19.3856 24.5001 18.8381 24.1588 18.4703C23.861 18.1499 23.5284 17.9525 23.2201 17.7695C23.1749 17.7427 23.1302 17.7161 23.0862 17.6896L23.0362 17.6594C22.373 17.2591 21.86 16.9494 21.6259 15.7674L20.4922 10.0608C20.0035 7.59548 18.3839 5.34284 15.9099 4.47335Z"
          />
          <path
            fill={outlineColor}
            d="M15.9099 4.47335L14.9327 4.26118L14.7454 5.12402L15.5784 5.41678L15.9099 4.47335ZM11.802 4.33203L12.0641 5.29709L12.9171 5.06548L12.792 4.19046L11.802 4.33203ZM6.76537 10.0608L7.7462 10.2556L7.74631 10.2551L6.76537 10.0608ZM5.6316 15.7674L4.65077 15.5725L4.6507 15.5729L5.6316 15.7674ZM4.22046 17.6597L3.70388 16.8035L3.7037 16.8036L4.22046 17.6597ZM4.17101 17.6896L3.65425 16.8334L3.65413 16.8335L4.17101 17.6896ZM4.03721 17.7694L3.52685 16.9095L3.52684 16.9095L4.03721 17.7694ZM3.09868 18.4703L2.36589 17.7899L2.36559 17.7902L3.09868 18.4703ZM10.6517 22.0344L11.6466 22.1348L11.7577 21.0344H10.6517V22.0344ZM10.6532 22.2212H11.6532V22.1945L11.6517 22.1678L10.6532 22.2212ZM10.6532 22.2212H9.65317V22.2479L9.65459 22.2745L10.6532 22.2212ZM10.6578 22.3298H11.6578V22.3185L11.6576 22.3072L10.6578 22.3298ZM16.6017 22.3298L15.6017 22.316V22.3298H16.6017ZM16.603 22.2651L15.6032 22.2432L15.6032 22.2432L16.603 22.2651ZM16.5963 22.0344V21.0344H15.4288L15.6082 22.188L16.5963 22.0344ZM24.1588 18.4703L24.8919 17.7902L24.8913 17.7895L24.1588 18.4703ZM23.2201 17.7695L23.7305 16.9096L23.7305 16.9095L23.2201 17.7695ZM23.0862 17.6896L22.5693 18.5456L22.5693 18.5456L23.0862 17.6896ZM23.0362 17.6594L23.5531 16.8033L23.5529 16.8032L23.0362 17.6594ZM21.6259 15.7674L22.6069 15.5732L22.6068 15.5725L21.6259 15.7674ZM20.4922 10.0608L19.5112 10.2552L19.5113 10.2556L20.4922 10.0608ZM14.9557 4.04545C14.9557 4.12039 14.9476 4.19241 14.9327 4.26118L16.8872 4.68552C16.9322 4.4783 16.9557 4.26403 16.9557 4.04545H14.9557ZM13.8689 3C14.488 3 14.9557 3.48669 14.9557 4.04545H16.9557C16.9557 2.34435 15.5544 1 13.8689 1V3ZM12.7817 4.04545C12.7817 3.48684 13.2493 3 13.8689 3V1C12.1832 1 10.7817 2.3442 10.7817 4.04545H12.7817ZM12.792 4.19046C12.7853 4.14355 12.7817 4.09519 12.7817 4.04545H10.7817C10.7817 4.19024 10.792 4.33323 10.8121 4.4736L12.792 4.19046ZM7.74631 10.2551C8.2062 7.9335 9.77396 5.91889 12.0641 5.29709L11.54 3.36697C8.36991 4.2277 6.35986 6.96163 5.78443 9.86643L7.74631 10.2551ZM6.61243 15.9623L7.7462 10.2556L5.78454 9.86588L4.65077 15.5725L6.61243 15.9623ZM4.73703 18.516C5.04712 18.3289 5.48007 18.0767 5.84 17.6648C6.22236 17.2273 6.47006 16.6804 6.61251 15.9619L4.6507 15.5729C4.55872 16.0368 4.4327 16.2359 4.33402 16.3488C4.21291 16.4874 4.0575 16.5902 3.70388 16.8035L4.73703 18.516ZM4.68777 18.5457L4.73722 18.5159L3.7037 16.8036L3.65425 16.8334L4.68777 18.5457ZM4.54757 18.6294C4.59219 18.6029 4.64013 18.5745 4.68788 18.5456L3.65413 16.8335C3.61398 16.8578 3.5726 16.8823 3.52685 16.9095L4.54757 18.6294ZM3.83148 19.1508C4.01836 18.9495 4.2314 18.817 4.54758 18.6294L3.52684 16.9095C3.22644 17.0878 2.77419 17.3501 2.36589 17.7899L3.83148 19.1508ZM3.5166 20.0321C3.5166 19.635 3.66406 19.3312 3.83177 19.1504L2.36559 17.7902C1.85089 18.345 1.5166 19.1362 1.5166 20.0321H3.5166ZM3.5166 21.3936V20.0321H1.5166V21.3936H3.5166ZM3.17026 21.0344C3.34247 21.0344 3.5166 21.1766 3.5166 21.3936H1.5166C1.5166 22.3187 2.27576 23.0344 3.17026 23.0344V21.0344ZM10.6517 21.0344H3.17026V23.0344H10.6517V21.0344ZM11.6517 22.1678C11.6508 22.15 11.6501 22.1373 11.6496 22.1256C11.6491 22.1142 11.6489 22.1073 11.6488 22.1032C11.6487 22.0991 11.6488 22.1 11.6487 22.1046C11.6485 22.1091 11.6481 22.1197 11.6466 22.1348L9.65673 21.9339C9.64185 22.0813 9.65213 22.2285 9.65459 22.2745L11.6517 22.1678ZM11.6532 22.2212V22.2212H9.65317V22.2212H11.6532ZM11.6576 22.3072C11.6563 22.2522 11.6535 22.2008 11.6517 22.1679L9.65459 22.2745C9.65658 22.3118 9.65764 22.3331 9.65808 22.3525L11.6576 22.3072ZM11.6578 22.4362V22.3298H9.65782V22.4362H11.6578ZM13.6298 24.0002C12.4184 24.0002 11.6578 23.1886 11.6578 22.4362H9.65782C9.65782 24.5949 11.6419 26.0002 13.6298 26.0002V24.0002ZM15.6017 22.4362C15.6017 23.1886 14.8412 24.0002 13.6298 24.0002V26.0002C15.6177 26.0002 17.6017 24.5949 17.6017 22.4362H15.6017ZM15.6017 22.3298V22.4362H17.6017V22.3298H15.6017ZM15.6032 22.2432C15.6028 22.2638 15.6022 22.2893 15.6018 22.316L17.6017 22.3437C17.6019 22.3265 17.6023 22.3089 17.6027 22.2871L15.6032 22.2432ZM15.6082 22.188C15.6056 22.171 15.6046 22.1596 15.6044 22.1556C15.6041 22.1516 15.6042 22.1519 15.6043 22.1583C15.6044 22.1648 15.6045 22.1744 15.6043 22.1892C15.6041 22.2044 15.6037 22.2209 15.6032 22.2432L17.6027 22.2871C17.604 22.2289 17.6112 22.0524 17.5844 21.8807L15.6082 22.188ZM24.0873 21.0344H16.5963V23.0344H24.0873V21.0344ZM23.7409 21.3936C23.7409 21.1763 23.9151 21.0344 24.0873 21.0344V23.0344C24.9811 23.0344 25.7409 22.319 25.7409 21.3936H23.7409ZM23.7409 20.0321V21.3936H25.7409V20.0321H23.7409ZM23.4258 19.1504C23.5935 19.3312 23.7409 19.635 23.7409 20.0321H25.7409C25.7409 19.1362 25.4066 18.345 24.8919 17.7902L23.4258 19.1504ZM22.7098 18.6295C23.0259 18.8171 23.2391 18.9496 23.4264 19.1511L24.8913 17.7895C24.4829 17.3501 24.0309 17.0878 23.7305 16.9096L22.7098 18.6295ZM22.5693 18.5456C22.6171 18.5745 22.6651 18.603 22.7098 18.6295L23.7305 16.9095C23.6847 16.8824 23.6433 16.8578 23.6031 16.8335L22.5693 18.5456ZM22.5192 18.5154L22.5693 18.5456L23.6031 16.8336L23.5531 16.8033L22.5192 18.5154ZM20.645 15.9616C20.7872 16.68 21.0348 17.2269 21.417 17.6645C21.7768 18.0763 22.2096 18.3285 22.5194 18.5155L23.5529 16.8032C23.1996 16.59 23.0443 16.4872 22.9232 16.3487C22.8246 16.2358 22.6987 16.0368 22.6069 15.5732L20.645 15.9616ZM19.5113 10.2556L20.6451 15.9623L22.6068 15.5725L21.473 9.86588L19.5113 10.2556ZM15.5784 5.41678C17.6561 6.14701 19.0774 8.06644 19.5112 10.2552L21.4731 9.86633C20.9297 7.12452 19.1117 4.53868 16.2415 3.52992L15.5784 5.41678Z"
          />
        </>
      );
    },
    viewBox: "0 0 28 28",
    width: 16,
  },
  "bell-alt": {
    height: 19,
    width: 18,
    viewBox: "0 0 18 19",
    pathSolid: () => {
      const maskId1 = uuid4();
      return (
        <>
          <mask
            id={maskId1}
            style={{ maskType: "alpha" }}
            width="18"
            height="19"
            x="0"
            y="0"
            maskUnits="userSpaceOnUse"
          >
            <path fill="#D9D9D9" d="M0 0.5H18V18.5H0z"></path>
          </mask>
          <g mask={`url(#${maskId1})`}>
            <path
              fill="#003E6B"
              d="M3 14.75v-1.5h1.5V8c0-1.037.313-1.96.938-2.766A4.326 4.326 0 017.874 3.65v-.525c0-.313.11-.578.328-.797C8.422 2.11 8.688 2 9 2c.313 0 .578.11.797.328.219.219.328.485.328.797v.525c1 .25 1.813.778 2.438 1.584.624.807.937 1.729.937 2.766v5.25H15v1.5H3zM9 17c-.412 0-.766-.147-1.06-.44a1.445 1.445 0 01-.44-1.06h3c0 .412-.147.766-.44 1.06-.294.293-.648.44-1.06.44z"
            ></path>
          </g>
        </>
      );
    },
  },
  bold: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M1 2h3.83c2.48 0 4.3.75 4.3 2.95 0 1.14-.63 2.23-1.67 2.61v.06c1.33.3 2.3 1.23 2.3 2.86 0 2.39-1.97 3.52-4.61 3.52H1V2zm3.66 4.95c1.67 0 2.38-.66 2.38-1.69 0-1.17-.78-1.61-2.34-1.61H3.13v3.3h1.53zm.27 5.39c1.77 0 2.75-.64 2.75-1.98 0-1.27-.95-1.81-2.75-1.81h-1.8v3.8h1.8v-.01z",
    }),
    viewBox: "0 0 10 16",
    width: 10,
  },
  book: {
    height: 16,
    pathSolid: (
      <path d="M3.92857 2H3C2.42857 2 2 2.4 2 2.93333V13.1333C2 13.6 2.42857 14 3 14H4V2H3.92857Z M13.7037 2H5V14H13.7037C13.8519 14 14 13.8667 14 13.7333V2.33333C14 2.13333 13.8519 2 13.7037 2ZM12 6.66667C12 6.8 11.8519 7 11.6296 7H7.37037C7.22222 7 7 6.86667 7 6.66667V5.33333C7 5.2 7.14815 5 7.37037 5H11.6296C11.7778 5 12 5.13333 12 5.33333V6.66667Z" />
    ),
    viewBox: "0 0 16 16",
    width: 16,
  },
  "career-ellipse": {
    height: "100%",
    width: 145,
    viewBox: "0 0 145 100%",
    pathSolid: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        fill="#E57295"
        d="M63.5701 -64.8062C87.7894 -159.857 79.4987 -277.91 155.852 -331.976C232.049 -385.932 323.043 -319.713 411.473 -311.16C520.221 -300.642 651.794 -363.104 724.826 -276.945C800.712 -187.42 774.371 -40.3694 743.825 80.9267C714.639 196.822 653.225 299.83 562.154 369.012C468.477 440.174 354.899 484.772 246.547 458.684C140.079 433.049 49.789 347.154 11.0486 236.319C-22.9219 139.131 37.4166 37.836 63.5701 -64.8062Z"
      />
    ),
  },
  "case-study": {
    height: 16,
    width: 16,
    viewBox: "0 0 16 16",
    pathSolid: () => {
      const maskId = uuid4();
      return (
        <>
          <mask id={maskId} fill="none">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M8 0C9.10457 0 10 0.895431 10 2H11C11.5523 2 12 2.44772 12 3H13.2759C13.6758 3 14 3.32418 14 3.72408V15.2759C14 15.6758 13.6758 16 13.2759 16H2.72408C2.32418 16 2 15.6758 2 15.2759V3.72408C2 3.32418 2.32418 3 2.72408 3H4C4 2.44772 4.44772 2 5 2H6C6 0.895431 6.89543 0 8 0ZM6 4V5.01777C6.55995 5.01777 6.9861 5.43788 6.9861 6H8C8 4.87576 7.11986 4 6 4ZM6.5 14C6.22386 14 6 13.7761 6 13.5C6 13.2239 6.22386 13 6.5 13H7V9.5C7 9.22386 7.22386 9 7.5 9C7.77614 9 8 9.22386 8 9.5V13H9V10.5C9 10.2239 9.22386 10 9.5 10C9.77614 10 10 10.2239 10 10.5V13H11V5.5C11 5.22386 11.2239 5 11.5 5C11.7761 5 12 5.22386 12 5.5V13H12.5C12.7761 13 13 13.2239 13 13.5C13 13.7761 12.7761 14 12.5 14H11.5H9.5H7.5H6.5ZM3.98224 6.63619L3.97967 6.63614C3.99531 7.142 4.36901 7.51624 4.88575 7.51624C5.4137 7.51624 5.78925 7.12559 5.78925 6.60303H6.74521C6.74521 7.64821 5.94158 8.46252 4.88571 8.46252C3.82991 8.46252 3 7.64821 3 6.60303H3.00046C3.00672 5.56308 3.88611 4.74365 4.93815 4.74365V5.68987C4.4102 5.68987 3.98224 6.11357 3.98224 6.63619Z"
            />
          </mask>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M8 0C9.10457 0 10 0.895431 10 2H11C11.5523 2 12 2.44772 12 3H13.2759C13.6758 3 14 3.32418 14 3.72408V15.2759C14 15.6758 13.6758 16 13.2759 16H2.72408C2.32418 16 2 15.6758 2 15.2759V3.72408C2 3.32418 2.32418 3 2.72408 3H4C4 2.44772 4.44772 2 5 2H6C6 0.895431 6.89543 0 8 0ZM6 4V5.01777C6.55995 5.01777 6.9861 5.43788 6.9861 6H8C8 4.87576 7.11986 4 6 4ZM6.5 14C6.22386 14 6 13.7761 6 13.5C6 13.2239 6.22386 13 6.5 13H7V9.5C7 9.22386 7.22386 9 7.5 9C7.77614 9 8 9.22386 8 9.5V13H9V10.5C9 10.2239 9.22386 10 9.5 10C9.77614 10 10 10.2239 10 10.5V13H11V5.5C11 5.22386 11.2239 5 11.5 5C11.7761 5 12 5.22386 12 5.5V13H12.5C12.7761 13 13 13.2239 13 13.5C13 13.7761 12.7761 14 12.5 14H11.5H9.5H7.5H6.5ZM3.98224 6.63619L3.97967 6.63614C3.99531 7.142 4.36901 7.51624 4.88575 7.51624C5.4137 7.51624 5.78925 7.12559 5.78925 6.60303H6.74521C6.74521 7.64821 5.94158 8.46252 4.88571 8.46252C3.82991 8.46252 3 7.64821 3 6.60303H3.00046C3.00672 5.56308 3.88611 4.74365 4.93815 4.74365V5.68987C4.4102 5.68987 3.98224 6.11357 3.98224 6.63619Z"
          />
          <path
            mask={`url(#${maskId})`}
            d="M10 2H9.27592V2.72408H10V2ZM12 3H11.2759V3.72408H12V3ZM4 3V3.72408H4.72408V3H4ZM6 2V2.72408H6.72408V2H6ZM6 5.01777H5.27592V5.74185H6V5.01777ZM6 4V3.27592H5.27592V4H6ZM6.9861 6H6.26202V6.72408H6.9861V6ZM8 6V6.72408H8.72408V6H8ZM7 13V13.7241H7.72408V13H7ZM8 13H7.27592V13.7241H8V13ZM9 13V13.7241H9.72408V13H9ZM10 13H9.27592V13.7241H10V13ZM11 13V13.7241H11.7241V13H11ZM12 13H11.2759V13.7241H12V13ZM3.97967 6.63614L3.99603 5.91224L3.23233 5.89499L3.25594 6.65851L3.97967 6.63614ZM3.98224 6.63619L3.96589 7.36009L4.70632 7.37682V6.63619H3.98224ZM5.78925 6.60303V5.87895H5.06517V6.60303H5.78925ZM6.74521 6.60303H7.46929V5.87895H6.74521V6.60303ZM3 6.60303V5.87895H2.27592V6.60303H3ZM3.00046 6.60303V7.32711H3.7202L3.72453 6.60738L3.00046 6.60303ZM4.93815 4.74365H5.66223V4.01957H4.93815V4.74365ZM4.93815 5.68987V6.41395H5.66223V5.68987H4.93815ZM10.7241 2C10.7241 0.495533 9.50447 -0.724078 8 -0.724078V0.724078C8.70467 0.724078 9.27592 1.29533 9.27592 2H10.7241ZM11 1.27592H10V2.72408H11V1.27592ZM12.7241 3C12.7241 2.04782 11.9522 1.27592 11 1.27592V2.72408C11.1524 2.72408 11.2759 2.84761 11.2759 3H12.7241ZM13.2759 2.27592H12V3.72408H13.2759V2.27592ZM14.7241 3.72408C14.7241 2.92428 14.0757 2.27592 13.2759 2.27592V3.72408L13.2759 3.72408H14.7241ZM14.7241 15.2759V3.72408H13.2759V15.2759H14.7241ZM13.2759 16.7241C14.0757 16.7241 14.7241 16.0757 14.7241 15.2759H13.2759L13.2759 15.2759V16.7241ZM2.72408 16.7241H13.2759V15.2759H2.72408V16.7241ZM1.27592 15.2759C1.27592 16.0757 1.92428 16.7241 2.72408 16.7241V15.2759L2.72408 15.2759H1.27592ZM1.27592 3.72408V15.2759H2.72408V3.72408H1.27592ZM2.72408 2.27592C1.92428 2.27592 1.27592 2.92428 1.27592 3.72408H2.72408L2.72408 3.72408V2.27592ZM4 2.27592H2.72408V3.72408H4V2.27592ZM5 1.27592C4.04782 1.27592 3.27592 2.04782 3.27592 3H4.72408C4.72408 2.84761 4.84761 2.72408 5 2.72408V1.27592ZM6 1.27592H5V2.72408H6V1.27592ZM8 -0.724078C6.49553 -0.724078 5.27592 0.495533 5.27592 2H6.72408C6.72408 1.29533 7.29533 0.724078 8 0.724078V-0.724078ZM6.72408 5.01777V4H5.27592V5.01777H6.72408ZM7.71018 6C7.71018 5.03318 6.95502 4.29369 6 4.29369V5.74185C6.09452 5.74185 6.15594 5.77525 6.19284 5.81189C6.22928 5.84808 6.26202 5.90731 6.26202 6H7.71018ZM8 5.27592H6.9861V6.72408H8V5.27592ZM6 4.72408C6.721 4.72408 7.27592 5.27669 7.27592 6H8.72408C8.72408 4.47482 7.51871 3.27592 6 3.27592V4.72408ZM5.27592 13.5C5.27592 14.176 5.82396 14.7241 6.5 14.7241V13.2759C6.62376 13.2759 6.72408 13.3762 6.72408 13.5H5.27592ZM6.5 12.2759C5.82396 12.2759 5.27592 12.824 5.27592 13.5H6.72408C6.72408 13.6238 6.62376 13.7241 6.5 13.7241V12.2759ZM7 12.2759H6.5V13.7241H7V12.2759ZM6.27592 9.5V13H7.72408V9.5H6.27592ZM7.5 8.27592C6.82396 8.27592 6.27592 8.82396 6.27592 9.5H7.72408C7.72408 9.62375 7.62376 9.72408 7.5 9.72408V8.27592ZM8.72408 9.5C8.72408 8.82396 8.17604 8.27592 7.5 8.27592V9.72408C7.37624 9.72408 7.27592 9.62376 7.27592 9.5H8.72408ZM8.72408 13V9.5H7.27592V13H8.72408ZM9 12.2759H8V13.7241H9V12.2759ZM8.27592 10.5V13H9.72408V10.5H8.27592ZM9.5 9.27592C8.82396 9.27592 8.27592 9.82396 8.27592 10.5H9.72408C9.72408 10.6238 9.62376 10.7241 9.5 10.7241V9.27592ZM10.7241 10.5C10.7241 9.82396 10.176 9.27592 9.5 9.27592V10.7241C9.37624 10.7241 9.27592 10.6238 9.27592 10.5H10.7241ZM10.7241 13V10.5H9.27592V13H10.7241ZM11 12.2759H10V13.7241H11V12.2759ZM10.2759 5.5V13H11.7241V5.5H10.2759ZM11.5 4.27592C10.824 4.27592 10.2759 4.82396 10.2759 5.5H11.7241C11.7241 5.62376 11.6238 5.72408 11.5 5.72408V4.27592ZM12.7241 5.5C12.7241 4.82396 12.176 4.27592 11.5 4.27592V5.72408C11.3762 5.72408 11.2759 5.62376 11.2759 5.5H12.7241ZM12.7241 13V5.5H11.2759V13H12.7241ZM12.5 12.2759H12V13.7241H12.5V12.2759ZM13.7241 13.5C13.7241 12.824 13.176 12.2759 12.5 12.2759V13.7241C12.3762 13.7241 12.2759 13.6238 12.2759 13.5H13.7241ZM12.5 14.7241C13.176 14.7241 13.7241 14.176 13.7241 13.5H12.2759C12.2759 13.3762 12.3762 13.2759 12.5 13.2759V14.7241ZM11.5 14.7241H12.5V13.2759H11.5V14.7241ZM9.5 14.7241H11.5V13.2759H9.5V14.7241ZM7.5 14.7241H9.5V13.2759H7.5V14.7241ZM6.5 14.7241H7.5V13.2759H6.5V14.7241ZM3.96331 7.36003L3.96589 7.36009L3.9986 5.9123L3.99603 5.91224L3.96331 7.36003ZM4.88575 6.79216C4.80774 6.79216 4.76985 6.76663 4.75163 6.74877C4.73241 6.72993 4.70577 6.69051 4.7034 6.61376L3.25594 6.65851C3.28344 7.54808 3.97056 8.24031 4.88575 8.24031V6.79216ZM5.06517 6.60303C5.06517 6.68736 5.03704 6.72971 5.01858 6.74853C5.00146 6.76599 4.96488 6.79216 4.88575 6.79216V8.24031C5.82665 8.24031 6.51332 7.51231 6.51332 6.60303H5.06517ZM6.74521 5.87895H5.78925V7.32711H6.74521V5.87895ZM4.88571 9.1866C6.34421 9.1866 7.46929 8.04537 7.46929 6.60303H6.02114C6.02114 7.25105 5.53896 7.73845 4.88571 7.73845V9.1866ZM2.27592 6.60303C2.27592 8.05866 3.44064 9.1866 4.88571 9.1866V7.73845C4.21917 7.73845 3.72408 7.23776 3.72408 6.60303H2.27592ZM3.00046 5.87895H3V7.32711H3.00046V5.87895ZM4.93815 4.01957C3.51839 4.01957 2.28523 5.13072 2.2764 6.59867L3.72453 6.60738C3.72821 5.99543 4.25382 5.46773 4.93815 5.46773V4.01957ZM5.66223 5.68987V4.74365H4.21408V5.68987H5.66223ZM4.70632 6.63619C4.70632 6.52032 4.80321 6.41395 4.93815 6.41395V4.9658C4.01719 4.9658 3.25817 5.70682 3.25817 6.63619H4.70632Z"
          />
        </>
      );
    },
  },
  check: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M12 5l-8 8-4-4 1.5-1.5L4 10l6.5-6.5L12 5z",
    }),
    viewBox: "0 0 12 16",
    width: 12,
  },
  "check-small": {
    height: 9,
    pathSolid: (
      <path
        fillRule="evenodd"
        d="M11.6517 1.75856L4.02295 8.31931L0.347656 5.15856L1.65173 3.64219L4.02295 5.68143L10.3477 0.242188L11.6517 1.75856Z"
      />
    ),
    viewBox: "0 0 12 9",
    width: 12,
  },
  checklist: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M16 8.5l-6 6-3-3L8.5 10l1.5 1.5L14.5 7 16 8.5zM5.7 12.2l.8.8H2c-.55 0-1-.45-1-1V3c0-.55.45-1 1-1h7c.55 0 1 .45 1 1v6.5l-.8-.8c-.39-.39-1.03-.39-1.42 0L5.7 10.8a.996.996 0 0 0 0 1.41v-.01zM4 4h5V3H4v1zm0 2h5V5H4v1zm0 2h3V7H4v1zM3 9H2v1h1V9zm0-2H2v1h1V7zm0-2H2v1h1V5zm0-2H2v1h1V3z",
    }),
    viewBox: "0 0 16 16",
    width: 16,
  },
  "chevron-down": {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M5 11L0 6l1.5-1.5L5 8.25 8.5 4.5 10 6l-5 5z",
    }),
    viewBox: "0 0 10 16",
    width: 10,
  },
  "chevron-down-tabs": {
    height: 10,
    pathSolid: React.createElement("path", {
      fillRule: "none",
      d: "M8 9.52223L-2.9918e-07 2.18889L1.86667 0.477784L8 6.10001L14.1333 0.477786L16 2.1889L8 9.52223Z",
    }),
    viewBox: "0 0 16 10",
    width: 16,
  },
  "chevron-left": {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M5.5 3L7 4.5 3.25 8 7 11.5 5.5 13l-5-5 5-5z",
    }),
    viewBox: "0 0 8 16",
    width: 8,
  },
  "chevron-left-thin": {
    height: 36,
    pathSolid: React.createElement("path", {
      fillRule: "none",
      d: "M12.478 18l7.333-8 1.711 1.867L15.9 18l5.622 6.133L19.812 26l-7.334-8z",
    }),
    viewBox: "0 0 36 36",
    width: 36,
  },
  "chevron-right": {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M7.5 8l-5 5L1 11.5 4.75 8 1 4.5 2.5 3l5 5z",
    }),
    viewBox: "0 0 8 16",
    width: 8,
  },
  "chevron-right-thin": {
    height: 36,
    pathSolid: React.createElement("path", {
      fillRule: "none",
      d: "M23.522 18l-7.333 8-1.711-1.867L20.1 18l-5.622-6.133L16.188 10l7.334 8z",
    }),
    viewBox: "0 0 36 36",
    width: 36,
  },
  "chevron-up": {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M10 10l-1.5 1.5L5 7.75 1.5 11.5 0 10l5-5 5 5z",
    }),
    viewBox: "0 0 10 16",
    width: 10,
  },
  "chevron-up-tabs": {
    height: 10,
    pathSolid: React.createElement("path", {
      fillRule: "none",
      d: "M8 0.47777L16 7.81111L14.1333 9.52222L8 3.89999L1.86667 9.52221L4.4877e-07 7.8111L8 0.47777Z",
    }),
    viewBox: "0 0 16 10",
    width: 16,
  },
  "circle-slash": {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M7 1C3.14 1 0 4.14 0 8s3.14 7 7 7 7-3.14 7-7-3.14-7-7-7zm0 1.3c1.3 0 2.5.44 3.47 1.17l-8 8A5.755 5.755 0 0 1 1.3 8c0-3.14 2.56-5.7 5.7-5.7zm0 11.41c-1.3 0-2.5-.44-3.47-1.17l8-8c.73.97 1.17 2.17 1.17 3.47 0 3.14-2.56 5.7-5.7 5.7z",
    }),
    viewBox: "0 0 14 16",
    width: 14,
  },
  classlink: {
    height: 20,
    width: 20,
    viewBox: "0 0 20 20",
    pathSolid: (
      <>
        <g clip-path="url(#clip0_13179_162002)">
          <path
            d="M11.8878 3C12.1826 3 12.4775 3 12.7723 3C13.0042 3.04639 13.236 3.08698 13.465 3.14207C14.4984 3.40012 15.4143 3.85533 16.0727 4.74255C16.9572 5.93131 17.1175 7.24475 16.7282 8.64806C16.6853 8.79883 16.7139 8.84522 16.8542 8.90031C18.8636 9.68895 20.0343 11.4431 20 13.6206C19.9828 14.8035 19.5019 15.7603 18.5401 16.4388C17.5927 17.1086 16.5307 17.4159 15.3685 17.4101C13.631 17.4014 11.8935 17.4246 10.156 17.4304C8.58451 17.4362 7.01302 17.442 5.44153 17.4478C5.02648 17.4507 4.60856 17.4913 4.19923 17.4478C3.35194 17.355 2.55904 17.0709 1.82911 16.6186C0.847288 16.0097 0.211822 15.1486 0.0400744 13.9685C0.0343495 13.9279 0.0486618 13.8786 0 13.8525C0 13.4959 0 13.1393 0 12.7826C0.0429369 12.7594 0.0286246 12.7159 0.0343495 12.6811C0.0830113 12.3477 0.148848 12.0172 0.269071 11.6982C0.787176 10.3297 1.78331 9.4628 3.10004 8.9351C3.22313 8.88581 3.25175 8.83652 3.22313 8.70895C3.12008 8.23924 3.09432 7.76374 3.18305 7.28824C3.43781 5.91101 4.93202 4.9687 5.99113 4.88172C6.88421 4.80924 7.65708 5.10498 8.33834 5.67326C8.42708 5.74575 8.46143 5.75155 8.51868 5.63847C8.69329 5.29054 8.90225 4.96001 9.13983 4.65267C9.76957 3.84083 10.551 3.26965 11.5701 3.06959C11.6788 3.04929 11.7905 3.06089 11.8878 3ZM8.19522 11.3213C8.25819 11.3358 8.31831 11.3532 8.38128 11.3677C8.75054 11.4518 9.11121 11.5562 9.42608 11.7794C9.6837 11.9621 9.8211 12.2143 9.83827 12.5362C9.84113 12.6087 9.81537 12.6493 9.74953 12.6783C8.95663 13.0175 8.12652 13.1885 7.26778 13.2436C7.18477 13.2494 7.15042 13.2233 7.13325 13.1393C7.076 12.8406 7.04451 12.5391 7.07027 12.2346C7.14183 11.3677 7.48819 10.6081 7.98912 9.91511C8.64176 9.01049 9.5005 8.34652 10.4566 7.80143C10.5138 7.76954 10.5796 7.74924 10.6254 7.68836C10.6054 7.67676 10.5911 7.66516 10.5739 7.66226C10.3049 7.60137 10.0386 7.52019 9.76385 7.4883C9.48619 7.4564 9.18277 7.4796 8.94518 7.62747C7.64277 8.4393 6.68098 9.54398 6.1686 11.0227C6.14284 11.0981 6.10276 11.159 6.04838 11.214C5.80793 11.4547 5.61614 11.7301 5.4902 12.0462C5.16674 12.8551 5.37284 13.3886 6.15142 13.7568C6.21154 13.7858 6.25161 13.8206 6.28023 13.8815C6.58938 14.6093 7.1447 15.0674 7.86604 15.3312C8.63318 15.6125 9.42035 15.6444 10.2161 15.5168C11.384 15.3312 12.4059 14.818 13.3276 14.0845C13.6568 13.8206 13.9631 13.5307 14.2465 13.2175C14.3295 13.1248 14.3123 13.0871 14.2007 13.0668C14.1692 13.061 14.1348 13.0523 14.1062 13.0378C13.9946 12.9798 13.9144 13.0146 13.8371 13.1103C13.7427 13.2233 13.6253 13.319 13.528 13.4321C13.445 13.5307 13.3763 13.522 13.2732 13.4669C12.8267 13.232 12.4288 12.9247 12.0538 12.5913C11.9594 12.5072 11.9622 12.4695 12.0653 12.397C12.6206 11.9998 13.133 11.5562 13.5595 11.0169C13.8343 10.6719 14.069 10.3065 14.1863 9.87452C14.318 9.39901 14.2751 8.9641 13.8801 8.62197C13.6024 8.38132 13.2732 8.24504 12.9211 8.15806C12.8496 8.14066 12.8066 8.17256 12.7637 8.22475C12.6406 8.36972 12.5118 8.51469 12.3859 8.65676C12.2341 8.82783 12.2399 8.82493 12.3343 9.03368C12.3859 9.14386 12.4488 9.17866 12.5633 9.16706C12.7351 9.15256 12.9068 9.17866 13.0671 9.24824C13.2389 9.32363 13.2761 9.38741 13.2503 9.57298C13.2102 9.86292 13.0872 10.1152 12.9211 10.35C12.5433 10.8835 12.0567 11.2981 11.5386 11.678C11.4527 11.7388 11.4327 11.6895 11.4126 11.6229C11.3496 11.4083 11.3697 11.1938 11.4183 10.9792C11.5472 10.4312 11.8105 9.947 12.1025 9.4744C12.1311 9.42801 12.1826 9.39031 12.1769 9.32073C11.9594 9.30623 11.7647 9.32363 11.6473 9.55268C11.5901 9.66576 11.4928 9.76144 11.4098 9.86002C10.9231 10.4457 10.365 10.927 9.62645 11.1619C9.15987 11.3097 8.67898 11.3271 8.19522 11.3213ZM9.7982 10.7124C10.1503 10.7153 10.4451 10.4196 10.4451 10.0601C10.448 9.70925 10.1446 9.40191 9.79533 9.40481C9.4547 9.40771 9.15701 9.70925 9.15414 10.0543C9.15128 10.4109 9.44325 10.7095 9.7982 10.7124Z"
            fill="#00AED2"
          />
          <path
            d="M7.46238 13.9105C7.71714 13.9134 7.9719 13.8989 8.2238 13.8699C8.6961 13.8119 9.15982 13.7046 9.61781 13.5655C9.718 13.5365 9.75235 13.5365 9.72372 13.6611C9.63499 14.0526 9.52621 14.4353 9.38595 14.8093C9.34015 14.9282 9.3745 14.9369 9.48328 14.9456C9.64644 14.9572 9.73804 14.9108 9.80674 14.7455C9.99566 14.3077 10.2304 13.8931 10.5166 13.5104C10.5939 13.406 10.6798 13.3103 10.7685 13.2146C10.9603 13.0059 11.1721 12.9218 11.467 13.0059C12.0423 13.1653 12.6062 13.3451 13.1444 13.6119C13.2131 13.6466 13.2646 13.6698 13.1816 13.7481C12.486 14.4034 11.7246 14.9688 10.7771 15.1543C9.84395 15.337 8.93655 15.2065 8.12361 14.6585C7.87458 14.4904 7.6742 14.2729 7.50818 14.0236C7.48242 13.9975 7.45093 13.9685 7.46238 13.9105Z"
            fill="#00AED2"
          />
          <path
            d="M5.95679 11.9824C5.92817 12.3419 5.95107 12.6986 5.99973 13.0552C6.00831 13.1132 5.98828 13.1306 5.92817 13.1132C5.75928 13.061 5.67913 12.9595 5.68486 12.7798C5.69631 12.4869 5.80222 12.2289 5.95679 11.9824Z"
            fill="#00AED2"
          />
        </g>
        <defs>
          <clipPath id="clip0_13179_162002">
            <rect width="20" height="20" fill="white" />
          </clipPath>
        </defs>
      </>
    ),
  },
  classroom: {
    height: 16,
    width: 16,
    viewBox: "0 0 16 16",
    pathSolid: (
      <>
        <path d="M13.8909 12.9455C14.4 12.5819 14.6909 12 14.6909 11.3455C14.6909 10.2546 13.8182 9.38185 12.7273 9.38185C11.6364 9.38185 10.7636 10.2546 10.7636 11.3455C10.7636 12 11.0545 12.5818 11.5636 12.8728C11.0545 13.0909 10.6182 13.4546 10.3273 13.9637C10.0364 13.4546 9.52727 13.0909 9.01818 12.8728C9.52727 12.5091 9.81818 11.9273 9.81818 11.3455C9.81818 10.2546 8.94545 9.38185 7.85454 9.38185C6.76363 9.38185 5.8909 10.2546 5.8909 11.3455C5.8909 12 6.18182 12.5818 6.69091 12.8728C6.18182 13.0909 5.74545 13.4546 5.38181 13.9637C5.0909 13.4546 4.58181 13.0909 4.07272 12.8728C4.50908 12.5091 4.87273 11.9273 4.87273 11.3455C4.87273 10.2546 4 9.38185 2.90909 9.38185C1.81818 9.38185 0.945446 10.2546 0.945446 11.3455C0.945446 12 1.23636 12.5819 1.74545 12.9455C0.727268 13.3819 0 14.4727 0 15.6364C0 15.7818 0.145455 16 0.363636 16H15.4182C15.5636 16 15.7818 15.8546 15.7818 15.6364C15.6364 14.4727 14.9091 13.4546 13.8909 12.9455Z" />
        <path d="M4 0C2.89543 0 2 0.895431 2 2V6C2 7.10457 2.89543 8 4 8H12C13.1046 8 14 7.10457 14 6V2C14 0.895431 13.1046 0 12 0H4ZM10 3.25C10 4.08282 9.5978 4.80995 9 5.19899V5.5C9.1726 5.55753 9.40479 5.57535 9.65543 5.59457C10.1308 5.63104 10.6726 5.6726 11 6C11.3536 6.35355 11.5 6.66667 11.5 7H4.5C4.5 6.66667 4.71716 6.28284 5 6C5.3274 5.6726 5.86917 5.63104 6.34457 5.59457C6.59521 5.57535 6.8274 5.55753 7 5.5V5.19899C6.4022 4.80995 6 4.08282 6 3.25C6 2.00736 6.89543 1 8 1C9.10457 1 10 2.00736 10 3.25Z" />
      </>
    ),
  },
  "classroom-alt": {
    height: 22,
    width: 22,
    viewBox: "0 0 22 22",
    pathSolid: (
      <>
        <g clipPath="url(#clip0_16650_24655)">
          <path
            fill="#003E6B"
            fillRule="evenodd"
            d="M4.233 1.23C4.107 1.354 4 1.58 4 2v8c0 .42.107.646.233.77.126.126.353.23.767.23h1.031c0-.72.54-1.424 1.26-1.584l1.894-.585v-.618c-.843-.619-1.38-1.663-1.38-2.813 0-1.828 1.358-3.39 3.145-3.4 1.58-.098 2.8 1.088 3.104 2.64a.501.501 0 01.056.39c.012.122.017.245.017.37 0 1.104-.495 2.111-1.28 2.738v.693l1.893.585c.392.089.7.355.9.62.203.27.36.623.36.964h1c.414 0 .641-.104.767-.23.126-.124.233-.35.233-.77V2c0-.436-.1-.662-.212-.782-.107-.114-.31-.218-.723-.218H5c-.414 0-.641.105-.767.23zM15 11a.719.719 0 00-.158-.362c-.115-.153-.241-.23-.329-.248l-.024-.005-2.643-.816v-.9a2.986 2.986 0 01-1.66.028v.872l-2.644.816-.024.005c-.252.051-.487.34-.487.61H15zm-4.926-3.414c.275.138.578.214.892.214 1.08 0 2.02-.894 2.147-2.118a2.146 2.146 0 01-1.732-.571c-.46.309-.983.468-1.48.512-.37.033-.75.003-1.093-.093.038.803.423 1.48.976 1.88a.5.5 0 01.29.176zm-1.123-3.06c.227.085.528.13.863.1.471-.04.944-.227 1.289-.577l.472-.479.323.59c.16.29.455.468.768.522.159.028.284.02.377 0-.265-1.079-1.118-1.746-2.043-1.683L10.983 3h-.017c-.896 0-1.697.616-2.015 1.526zM3.528.52C3.906.145 4.428 0 5 0h12.065c.572 0 1.087.146 1.45.532.36.38.485.904.485 1.468v8c0 .58-.15 1.104-.528 1.48-.378.374-.9.52-1.472.52H5c-.572 0-1.094-.146-1.472-.52C3.15 11.104 3 10.58 3 10V2c0-.58.15-1.104.528-1.48zM6.195 15.7c0-1.057-.783-1.773-1.626-1.702L4.548 14h-.022c-.914 0-1.668.684-1.668 1.7 0 1.045.783 1.8 1.668 1.8.872 0 1.669-.84 1.669-1.8zM4.505 13c1.512-.115 2.69 1.164 2.69 2.7 0 .857-.417 1.686-1.069 2.217A4.028 4.028 0 017.9 19.39a4.03 4.03 0 011.721-1.452A2.839 2.839 0 018.56 15.7c0-1.577 1.206-2.69 2.647-2.7 1.512-.115 2.69 1.164 2.69 2.7 0 .857-.417 1.686-1.069 2.217a4.028 4.028 0 011.773 1.474 4.034 4.034 0 011.659-1.424 2.836 2.836 0 01-1.097-2.267c0-1.577 1.206-2.69 2.648-2.7 1.511-.115 2.689 1.164 2.689 2.7 0 .852-.412 1.677-1.057 2.208C20.955 18.555 22 20.127 22 21.7v.5H.5a.5.5 0 01-.5-.5c0-1.089.62-2.115 1.454-2.85a5.244 5.244 0 011.462-.912A2.839 2.839 0 011.858 15.7c0-1.577 1.206-2.69 2.647-2.7zm8.392 2.7c0-1.057-.783-1.773-1.627-1.702l-.02.002h-.022c-.914 0-1.668.684-1.668 1.7 0 1.045.783 1.8 1.668 1.8.872 0 1.669-.84 1.669-1.8zm6.603 0c0-1.057-.783-1.773-1.626-1.702l-.021.002h-.021c-.915 0-1.669.684-1.669 1.7 0 1.045.783 1.8 1.669 1.8.872 0 1.668-.84 1.668-1.8zM1.065 21.2h6.55l-.188-.534C7 19.453 5.9 18.6 4.548 18.6c-.84 0-1.74.389-2.432 1-.537.473-.908 1.04-1.05 1.6zm7.118 0h6.134l-.188-.534c-.427-1.213-1.528-2.066-2.879-2.066s-2.452.853-2.88 2.066l-.187.534zm6.702 0h6.068c-.254-1.366-1.51-2.6-3.001-2.6-1.351 0-2.452.853-2.88 2.066l-.187.534z"
            clipRule="evenodd"
          ></path>
        </g>
        <defs>
          <clipPath id="clip0_16650_24655">
            <path fill="#fff" d="M0 0H22V22H0z"></path>
          </clipPath>
        </defs>
      </>
    ),
  },
  "classroom-bold": {
    height: 18,
    width: 18,
    fill: "none",
    viewBox: "0 0 18 18",
    pathSolid: () => {
      const maskId1 = uuid4();
      return (
        <>
          <mask
            id={maskId1}
            style={{ maskType: "alpha" }}
            width="18"
            height="18"
            x="0"
            y="0"
            maskUnits="userSpaceOnUse"
          >
            <path fill="#D9D9D9" d="M0 0H18V18H0z"></path>
          </mask>
          <g mask={`url(#${maskId1})`}>
            <path
              fill="#003E6B"
              d="M9 16.875c-.9-.85-1.931-1.5-3.094-1.95a10.04 10.04 0 00-3.656-.675V6c1.263 0 2.475.228 3.638.684A9.595 9.595 0 019 8.662a9.595 9.595 0 013.113-1.978A9.869 9.869 0 0115.75 6v8.25c-1.287 0-2.51.225-3.666.675A9.432 9.432 0 009 16.875zM9 6.75a2.889 2.889 0 01-2.119-.881A2.889 2.889 0 016 3.75c0-.825.294-1.531.881-2.119A2.889 2.889 0 019 .75c.825 0 1.531.294 2.119.881.587.588.881 1.294.881 2.119 0 .825-.294 1.531-.881 2.119A2.889 2.889 0 019 6.75z"
            ></path>
          </g>
        </>
      );
    },
  },
  "classroom-notification": {
    height: 12,
    width: 12,
    viewBox: "0 0 12 12",
    pathSolid: (
      <>
        <path d="M6.53488 6.79243C6.69043 6.89614 6.84598 6.99984 7.36449 6.99984C8.4015 6.99984 9.23112 5.18506 9.23112 4.04434C9.23112 2.85177 8.45336 1.91846 7.46819 1.91846C7.00154 1.91846 6.74228 2.02216 6.58672 2.07401C6.3 2.29988 6.12007 2.12586 6.12007 2.12586C5.80896 1.97031 5.54971 1.91846 5.18675 1.91846C5.08305 1.91846 4.9275 2.02216 4.9275 2.12586C4.9275 2.79992 4.77195 3.31843 4.46084 3.57768C4.14974 3.88879 3.78678 3.83694 3.73493 3.83694C3.68308 3.83694 3.57938 3.83694 3.52753 3.88879C3.47568 3.94064 3.42383 3.99249 3.42383 4.04434C3.42383 5.18506 4.30529 6.99984 5.29045 6.99984C5.80896 6.99984 5.96452 6.89614 6.12007 6.79243C6.17192 6.74058 6.17192 6.74058 6.27562 6.74058C6.53488 6.74058 6.53488 6.74058 6.53488 6.79243Z" />
        <path d="M7.05409 1.71108C7.8837 1.71108 8.50591 1.03702 8.50591 0.259254C8.50591 0.155552 8.40221 0 8.2985 0C7.46889 0 6.84668 0.674061 6.84668 1.45182C6.84668 1.60738 6.95039 1.71108 7.05409 1.71108Z" />
        <path d="M5 8H3V12H5V8Z" />
        <path d="M9 8H6V12H9V8Z" />
        <path d="M10.9273 8H10V12H10.9273C11.4939 12 12 11.2655 12 10.4V9.6C12 8.73455 11.4939 8 10.9273 8Z" />
        <rect y="11" width="5" height="1" rx="0.5" />
        <rect y="8" width="4" height="1" rx="0.5" />
        <path d="M3 10H2C2 10.8 1.33333 11 1 11V12.0001H3V11.0001V10Z" />
        <path d="M1 8L1 9C1.8 9 2 9.66667 2 10L3.00005 10L3.00005 8L2.00005 8L1 8Z" />
      </>
    ),
  },
  clear: {
    height: 20,
    width: 20,
    pathSolid: (
      <path
        fill="#003E6B"
        d="M5.33317 15.8333L4.1665 14.6667L8.83317 9.99999L4.1665 5.33332L5.33317 4.16666L9.99984 8.83332L14.6665 4.16666L15.8332 5.33332L11.1665 9.99999L15.8332 14.6667L14.6665 15.8333L9.99984 11.1667L5.33317 15.8333Z"
      />
    ),
    viewBox: "0 0 20 20",
  },
  clever: {
    height: 20,
    pathSolid: (
      <>
        <path
          d="M0 9.95701C0.00426545 10.7652 0.0810435 11.5565 0.264458 12.3393C0.810435 14.7088 2.03888 16.6383 4.00526 18.0855C5.592 19.2533 7.39202 19.85 9.35839 19.9727C10.6338 20.0531 11.8921 19.96 13.1248 19.6426C15.057 19.1475 16.6224 18.0643 17.9746 16.6426C18.0428 16.5749 18.0556 16.5326 17.9789 16.4564C17.1172 15.6017 16.2556 14.7385 15.3983 13.8795C15.3343 13.816 15.2959 13.8118 15.2276 13.8753C15.0442 14.0403 14.8523 14.1968 14.6646 14.3576C13.1248 15.6651 11.3589 16.2702 9.32427 15.9952C7.99345 15.8132 6.8887 15.1997 6.01855 14.1884C5.1356 13.1686 4.67493 11.9669 4.54697 10.6298C4.4318 9.42385 4.58536 8.26023 5.11854 7.16853C6.02281 5.30673 7.50719 4.20657 9.58446 3.96538C10.6082 3.84691 11.6148 4.00347 12.5745 4.40122C13.4831 4.77781 14.2722 5.32789 14.9973 5.97105C15.1466 6.10646 15.2191 6.09376 15.3343 5.94143C15.4622 5.77218 15.6115 5.61985 15.748 5.46329C16.4305 4.68472 17.113 3.90191 17.804 3.12334C17.9021 3.01333 17.8978 2.94562 17.7869 2.85253C17.5694 2.67058 17.3646 2.47594 17.1514 2.29399C15.6457 1.04151 13.9011 0.339099 11.9475 0.106374C10.5698 -0.0586498 9.20484 -0.0459557 7.85269 0.246009C6.05267 0.635295 4.45313 1.42656 3.11378 2.69174C1.02371 4.66779 0.0554508 7.12199 0 9.95701Z"
          fill="#0068B1"
        />
      </>
    ),
    viewBox: "0 0 19 20",
    width: 19,
  },
  clip: {
    height: 18,
    width: 18,
    pathSolid: (
      <path
        fill="#003E6B"
        d="M17 4.461c-.74-1.056-2.21-1.334-3.265-.595l-5.43 3.802L6.33 6.286a2.856 2.856 0 10-1.201 1.13l2.418 1.693-2.418 1.693a2.857 2.857 0 101.202 1.13l1.974-1.383 5.43 3.802c1.056.74 2.526.461 3.265-.595L10.362 9.11 17 4.46zM3.863 6.344a1.481 1.481 0 110-2.962 1.481 1.481 0 010 2.962zm0 8.491a1.481 1.481 0 110-2.962 1.481 1.481 0 010 2.962z"
      />
    ),
    viewBox: "0 0 18 18",
  },
  "clip-active": {
    height: 18,
    width: 18,
    pathSolid: (
      <>
        <rect width="18" height="18" fill="#003E6B" rx="2" />,
        <path
          fill="#fff"
          d="M17 4.461c-.74-1.056-2.21-1.334-3.265-.595l-5.43 3.802L6.33 6.286a2.856 2.856 0 10-1.201 1.13l2.418 1.693-2.418 1.693a2.857 2.857 0 101.202 1.13l1.974-1.383 5.43 3.802c1.056.74 2.526.461 3.265-.595L10.362 9.11 17 4.46zM3.863 6.344a1.481 1.481 0 110-2.962 1.481 1.481 0 010 2.962zm0 8.491a1.481 1.481 0 110-2.962 1.481 1.481 0 010 2.962z"
        />
      </>
    ),
    viewBox: "0 0 18 18",
  },
  clippy: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M2 13h4v1H2v-1zm5-6H2v1h5V7zm2 3V8l-3 3 3 3v-2h5v-2H9zM4.5 9H2v1h2.5V9zM2 12h2.5v-1H2v1zm9 1h1v2c-.02.28-.11.52-.3.7-.19.18-.42.28-.7.3H1c-.55 0-1-.45-1-1V4c0-.55.45-1 1-1h3c0-1.11.89-2 2-2 1.11 0 2 .89 2 2h3c.55 0 1 .45 1 1v5h-1V6H1v9h10v-2zM2 5h8c0-.55-.45-1-1-1H8c-.55 0-1-.45-1-1s-.45-1-1-1-1 .45-1 1-.45 1-1 1H3c-.55 0-1 .45-1 1z",
    }),
    viewBox: "0 0 14 16",
    width: 14,
  },
  clock: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M8 8h3v2H7c-.55 0-1-.45-1-1V4h2v4zM7 2.3c3.14 0 5.7 2.56 5.7 5.7s-2.56 5.7-5.7 5.7A5.71 5.71 0 0 1 1.3 8c0-3.14 2.56-5.7 5.7-5.7zM7 1C3.14 1 0 4.14 0 8s3.14 7 7 7 7-3.14 7-7-3.14-7-7-7z",
    }),
    viewBox: "0 0 14 16",
    width: 14,
  },
  "clock-request-pending": {
    height: 16,
    pathSolid: (
      <>
        <circle
          cx="7.5"
          cy="7.5"
          r="7"
          transform="matrix(-1 0 0 1 15 0)"
          stroke="#111111"
        />
        <rect
          width="1"
          height="6"
          rx="0.5"
          transform="matrix(-1 0 0 1 8 2)"
          fill="#111111"
        />
        <rect
          width="1"
          height="5"
          rx="0.5"
          transform="matrix(4.37114e-08 1 1 -4.37114e-08 3 7)"
          fill="#111111"
        />
      </>
    ),
    viewBox: "0 0 16 16",
    width: 16,
  },
  clone: {
    width: 10,
    height: 10,
    pathSolid: (
      <path
        fillRule="evenodd"
        d="M8 2V4.76837e-07L0 0V2V8H2V2H8ZM3 3H10V10H3V3Z"
      />
    ),
    viewBox: "0 0 10 10",
  },
  "cloud-download": {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M9 12h2l-3 3-3-3h2V7h2v5zm3-8c0-.44-.91-3-4.5-3C5.08 1 3 2.92 3 5 1.02 5 0 6.52 0 8c0 1.53 1 3 3 3h3V9.7H3C1.38 9.7 1.3 8.28 1.3 8c0-.17.05-1.7 1.7-1.7h1.3V5c0-1.39 1.56-2.7 3.2-2.7 2.55 0 3.13 1.55 3.2 1.8v1.2H12c.81 0 2.7.22 2.7 2.2 0 2.09-2.25 2.2-2.7 2.2h-2V11h2c2.08 0 4-1.16 4-3.5C16 5.06 14.08 4 12 4z",
    }),
    viewBox: "0 0 16 16",
    width: 16,
  },
  "cloud-upload": {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M7 9H5l3-3 3 3H9v5H7V9zm5-4c0-.44-.91-3-4.5-3C5.08 2 3 3.92 3 6 1.02 6 0 7.52 0 9c0 1.53 1 3 3 3h3v-1.3H3c-1.62 0-1.7-1.42-1.7-1.7 0-.17.05-1.7 1.7-1.7h1.3V6c0-1.39 1.56-2.7 3.2-2.7 2.55 0 3.13 1.55 3.2 1.8v1.2H12c.81 0 2.7.22 2.7 2.2 0 2.09-2.25 2.2-2.7 2.2h-2V12h2c2.08 0 4-1.16 4-3.5C16 6.06 14.08 5 12 5z",
    }),
    viewBox: "0 0 16 16",
    width: 16,
  },
  cluster: {
    height: 16,
    pathSolid: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M13.369 1.05184C13.4262 1.01967 13.496 1.01967 13.5532 1.05184L14.8265 1.76804C14.8856 1.80131 14.9222 1.8639 14.9222 1.93177V3.35579C14.9222 3.42365 14.8856 3.48624 14.8265 3.51952L13.5532 4.23572C13.496 4.26789 13.4262 4.26789 13.369 4.23572L13.0327 4.04654L11.8551 5.22409V8.19764L12.5334 8.53678L13.4036 8.0473C13.4558 8.01795 13.5195 8.01795 13.5717 8.0473L14.7336 8.70088C14.7876 8.73124 14.821 8.78836 14.821 8.85029V10.1498C14.821 10.2117 14.7876 10.2688 14.7336 10.2992L13.5717 10.9528C13.5195 10.9821 13.4558 10.9821 13.4036 10.9528L12.2417 10.2992C12.1877 10.2688 12.1543 10.2117 12.1543 10.1498V9.46526L11.1912 8.98372L9 10.2163V12.25L10.0525 12.8421C10.0887 12.8624 10.1111 12.9007 10.1111 12.9423V14.5578C10.1111 14.5993 10.0887 14.6376 10.0525 14.658L8.61194 15.4683C8.57693 15.488 8.53418 15.488 8.49918 15.4683L7.05862 14.658C7.02241 14.6376 7 14.5993 7 14.5578V12.9423C7 12.9007 7.02241 12.8624 7.05862 12.8421L8 12.3125V9.97917L7.15133 9.50179L4 12.6531V13.5594C4 13.6 3.97841 13.6376 3.9433 13.6581L2.55759 14.4664C2.522 14.4872 2.478 14.4872 2.44241 14.4664L1.0567 13.6581C1.02159 13.6376 1 13.6 1 13.5594V11.9407C1 11.9 1.02159 11.8624 1.0567 11.842L2.44241 11.0336C2.478 11.0129 2.522 11.0129 2.55759 11.0336L3.59824 11.6407L6.24623 8.99267L5.64777 8.65604C5.59751 8.62777 5.56641 8.57459 5.56641 8.51693V6.46218L3.27601 5.44423L2.78569 5.72003C2.71994 5.75701 2.63967 5.75701 2.57392 5.72003L1.11009 4.89663C1.04209 4.85837 1 4.78641 1 4.70839V3.07123C1 2.99321 1.04209 2.92125 1.11009 2.88299L2.57392 2.05959C2.63967 2.02261 2.71994 2.02261 2.78569 2.05959L4.24952 2.88299C4.31752 2.92125 4.35961 2.99321 4.35961 3.07123V4.70839C4.35961 4.7477 4.34892 4.78548 4.32962 4.81818L5.56641 5.36786V5.16622C5.56641 5.10855 5.59751 5.05537 5.64777 5.0271L8.63251 3.34818C8.6811 3.32085 8.74043 3.32085 8.78901 3.34818L11.0468 4.61819L12.1276 3.53742L12.0958 3.51952C12.0366 3.48624 12 3.42365 12 3.35579V1.93177C12 1.8639 12.0366 1.80131 12.0958 1.76804L13.369 1.05184Z"
      />
    ),
    viewBox: "0 0 16 16",
    width: 16,
  },
  code: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M9.5 3L8 4.5 11.5 8 8 11.5 9.5 13 14 8 9.5 3zm-5 0L0 8l4.5 5L6 11.5 2.5 8 6 4.5 4.5 3z",
    }),
    viewBox: "0 0 14 16",
    width: 14,
  },
  "collaborator-badge": {
    height: 22,
    width: 18,
    viewBox: "0 0 18 22",
    pathSolid: (
      <>
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M8.78517 16.6041C13.2777 16.6041 16.9196 12.8871 16.9196 8.30205C16.9196 3.71695 13.2777 0 8.78517 0C4.29266 0 0.650757 3.71695 0.650757 8.30205C0.650757 12.8871 4.29266 16.6041 8.78517 16.6041ZM8.29704 8.38093L5.47933 13.1855H7.80394L10.0829 9.29953L11.531 11.7718H13.1832L11.1852 8.36056L13.1832 4.8326H11.531L10.0708 7.41083L7.80394 3.41843H5.47933L8.29704 8.38093Z"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M10.0173 17.8511L12.3643 22L14.206 18.6141L18 18.6792L15.7838 14.7616C14.3169 16.4161 12.2947 17.548 10.0173 17.8511ZM7.95736 17.8959C5.65197 17.6914 3.58395 16.6408 2.053 15.05L0 18.6792L3.79397 18.6141L5.63569 22L7.95736 17.8959Z"
        />
      </>
    ),
  },
  comment: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M14 1H2c-.55 0-1 .45-1 1v8c0 .55.45 1 1 1h2v3.5L7.5 11H14c.55 0 1-.45 1-1V2c0-.55-.45-1-1-1zm0 9H7l-2 2v-2H2V2h12v8z",
    }),
    viewBox: "0 0 16 16",
    width: 16,
  },
  "comment-discussion": {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M15 1H6c-.55 0-1 .45-1 1v2H1c-.55 0-1 .45-1 1v6c0 .55.45 1 1 1h1v3l3-3h4c.55 0 1-.45 1-1V9h1l3 3V9h1c.55 0 1-.45 1-1V2c0-.55-.45-1-1-1zM9 11H4.5L3 12.5V11H1V5h4v3c0 .55.45 1 1 1h3v2zm6-3h-2v1.5L11.5 8H6V2h9v6z",
    }),
    viewBox: "0 0 16 16",
    width: 16,
  },
  "content-note": {
    height: 36,
    width: 36,
    viewBox: "0 0 36 36",
    pathSolid: (
      <>
        <circle cx="18" cy="18" r="18" fill="#336589" />
        <path
          d="M10.2031 28H8.64431L9.74656 26.9188C10.3409 26.3359 10.712 25.5776 10.8118 24.7551C8.26668 23.1168 7 20.7434 7 18.2552C7 13.6649 11.3012 9 18.0373 9C25.1735 9 29 13.2926 29 17.8531C29 22.4436 25.133 26.7333 18.0373 26.7333C16.7943 26.7333 15.4972 26.5706 14.3392 26.2719C13.243 27.3748 11.7577 28 10.2031 28Z"
          fill="#E5ECF0"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M15 18.5C15 19.3284 14.3284 20 13.5 20C12.6716 20 12 19.3284 12 18.5C12 17.6716 12.6716 17 13.5 17C14.3284 17 15 17.6716 15 18.5ZM20 18.5C20 19.3284 19.3284 20 18.5 20C17.6716 20 17 19.3284 17 18.5C17 17.6716 17.6716 17 18.5 17C19.3284 17 20 17.6716 20 18.5ZM23.5 20C24.3284 20 25 19.3284 25 18.5C25 17.6716 24.3284 17 23.5 17C22.6716 17 22 17.6716 22 18.5C22 19.3284 22.6716 20 23.5 20Z"
          fill="#336589"
        />
      </>
    ),
  },
  copy: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "m7.45,1.96l3.55,3.54l0,9.5c0,0.55 -0.45,1 -1,1l-9,0c-0.55,0 -1,-0.45 -1,-1l0,-12c0,-0.55 0.45,-1 1,-1l6.5,0l-0.05,-0.04zm2.55,4.04l-3,-3l-6,0l0,12l9,0l0,-9zm-1.5,-6l-5.5,0l0,1l5,0l4,4l0,8l1,0l0,-8.5l-4.5,-4.5z",
    }),
    viewBox: "0 0 13 16",
    width: 13,
  },
  dash: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M0 7v2h8V7H0z",
    }),
    viewBox: "0 0 8 16",
    width: 8,
  },
  dashboard: {
    viewBox: "0 0 22 22",
    width: 22,
    height: 22,
    pathSolid: (props: IconPathProps) => (
      <>
        <rect
          x="2.5"
          y="2.5"
          width="7"
          height="7"
          stroke={props.fill}
          strokeLinejoin="round"
          fill="none"
        />
        <rect
          x="2.5"
          y="12.5"
          width="7"
          height="7"
          stroke={props.fill}
          strokeLinejoin="round"
          fill="none"
        />
        <rect
          x="12.5"
          y="2.5"
          width="7"
          height="7"
          stroke={props.fill}
          strokeLinejoin="round"
          fill="none"
        />
        <rect
          x="12.5"
          y="12.5"
          width="7"
          height="7"
          stroke={props.fill}
          strokeLinejoin="round"
          fill="none"
        />
      </>
    ),
  },
  demand: {
    viewBox: "0 0 32 32",
    width: 32,
    height: 32,
    pathSolid: () => {
      const maskId = uuid4();
      return (
        <>
          <mask
            id={maskId}
            style={{ maskType: "alpha" }}
            maskUnits="userSpaceOnUse"
            x="0"
            y="0"
            width="32"
            height="32"
          >
            <rect width="32" height="32" fill="#ECF8F6" />
          </mask>
          <g mask={`url(#${maskId})`}>
            <path
              d="M26.667 12.5667L20.7003 18.5C19.9225 19.2778 18.9781 19.6667 17.867 19.6667C16.7559 19.6667 15.8114 19.2778 15.0336 18.5L13.467 16.9333C13.2225 16.6889 12.9114 16.5667 12.5336 16.5667C12.1559 16.5667 11.8448 16.6889 11.6003 16.9333L5.46698 23.0667C5.22253 23.3111 4.91142 23.4333 4.53364 23.4333C4.15586 23.4333 3.84475 23.3111 3.60031 23.0667C3.35586 22.8222 3.23364 22.5111 3.23364 22.1333C3.23364 21.7556 3.35586 21.4444 3.60031 21.2L9.73364 15.0667C10.5114 14.2889 11.4559 13.9 12.567 13.9C13.6781 13.9 14.6225 14.2889 15.4003 15.0667L16.9336 16.6C17.2003 16.8667 17.517 17 17.8836 17C18.2503 17 18.567 16.8667 18.8336 16.6L24.767 10.6667H22.667C22.2892 10.6667 21.9725 10.5389 21.717 10.2833C21.4614 10.0278 21.3336 9.71111 21.3336 9.33333C21.3336 8.95556 21.4614 8.63889 21.717 8.38333C21.9725 8.12778 22.2892 8 22.667 8H28.0003C28.3781 8 28.6948 8.12778 28.9503 8.38333C29.2059 8.63889 29.3336 8.95556 29.3336 9.33333V14.6667C29.3336 15.0444 29.2059 15.3611 28.9503 15.6167C28.6948 15.8722 28.3781 16 28.0003 16C27.6225 16 27.3059 15.8722 27.0503 15.6167C26.7948 15.3611 26.667 15.0444 26.667 14.6667V12.5667Z"
              fill="#5AC5B8"
            />
          </g>
        </>
      );
    },
  },
  "desktop-download": {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M4 6h3V0h2v6h3l-4 4-4-4zm11-4h-4v1h4v8H1V3h4V2H1c-.55 0-1 .45-1 1v9c0 .55.45 1 1 1h5.34c-.25.61-.86 1.39-2.34 2h8c-1.48-.61-2.09-1.39-2.34-2H15c.55 0 1-.45 1-1V3c0-.55-.45-1-1-1z",
    }),
    viewBox: "0 0 16 16",
    width: 16,
  },
  "device-camera": {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M15 3H7c0-.55-.45-1-1-1H2c-.55 0-1 .45-1 1-.55 0-1 .45-1 1v9c0 .55.45 1 1 1h14c.55 0 1-.45 1-1V4c0-.55-.45-1-1-1zM6 5H2V4h4v1zm4.5 7C8.56 12 7 10.44 7 8.5S8.56 5 10.5 5 14 6.56 14 8.5 12.44 12 10.5 12zM13 8.5c0 1.38-1.13 2.5-2.5 2.5S8 9.87 8 8.5 9.13 6 10.5 6 13 7.13 13 8.5z",
    }),
    viewBox: "0 0 16 16",
    width: 16,
  },
  "device-camera-video": {
    height: 16,
    pathSolid: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8 5L11 2H9L6 5H8ZM14 6V12.8571C14 13.4883 13.4883 14 12.8571 14H3.14286C2.51168 14 2 13.4883 2 12.8571V6H14ZM8 2L5 5H3L6 2H8ZM3.03703 2.00403C2.44444 2.00403 2 2.31562 2 2.73107V4.9999L5 2.00413L3.03703 2.00403ZM11 5L13.7072 2.2928C13.5262 2.11189 13.2763 2 13.0002 2H12L9 5H11ZM14 5H12L14 3V5ZM7 8.3737V11.6263C7 11.786 7.17803 11.8813 7.31094 11.7927L9.75039 10.1664C9.86913 10.0872 9.86913 9.91275 9.75038 9.83359L7.31094 8.20729C7.17803 8.11869 7 8.21396 7 8.3737Z"
      />
    ),
    viewBox: "0 0 16 16",
    width: 16,
  },
  "device-desktop": {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M15 2H1c-.55 0-1 .45-1 1v9c0 .55.45 1 1 1h5.34c-.25.61-.86 1.39-2.34 2h8c-1.48-.61-2.09-1.39-2.34-2H15c.55 0 1-.45 1-1V3c0-.55-.45-1-1-1zm0 9H1V3h14v8z",
    }),
    viewBox: "0 0 16 16",
    width: 16,
  },
  "device-mobile": {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M9 0H1C.45 0 0 .45 0 1v14c0 .55.45 1 1 1h8c.55 0 1-.45 1-1V1c0-.55-.45-1-1-1zM5 15.3c-.72 0-1.3-.58-1.3-1.3 0-.72.58-1.3 1.3-1.3.72 0 1.3.58 1.3 1.3 0 .72-.58 1.3-1.3 1.3zM9 12H1V2h8v10z",
    }),
    viewBox: "0 0 10 16",
    width: 10,
  },
  diff: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M6 7h2v1H6v2H5V8H3V7h2V5h1v2zm-3 6h5v-1H3v1zM7.5 2L11 5.5V15c0 .55-.45 1-1 1H1c-.55 0-1-.45-1-1V3c0-.55.45-1 1-1h6.5zM10 6L7 3H1v12h9V6zM8.5 0H3v1h5l4 4v8h1V4.5L8.5 0z",
    }),
    viewBox: "0 0 13 16",
    width: 13,
  },
  discussion: {
    width: 12,
    height: 11,
    viewBox: "0 0 12 11",
    pathSolid: (
      <path
        fillRule="evenodd"
        d="M3 1C3 0.447715 3.44772 0 4 0H10C11.1046 0 12 0.895431 12 2V8C12 8.55229 11.5523 9 11 9V3C11 1.89543 10.1046 1 9 1H3ZM0 4C0 2.89543 0.895431 2 2 2H8C9.10457 2 10 2.89543 10 4V7C10 8.10457 9.10457 9 8 9H3.66667L1 11V8.73244C0.402199 8.38663 0 7.74028 0 7V4ZM2 4H8V5H2V4ZM2 6H8V7H2V6Z"
      />
    ),
  },
  discussions: {
    height: 16,
    width: 16,
    viewBox: "0 0 16 16",
    pathSolid: (
      <path d="M11 2C11.5523 2 12 2.44772 12 3V6H8C7.44772 6 7 6.44771 7 7V9.99993L6 10L3.88569 12.5628C3.5873 12.9245 3 12.7135 3 12.2446V10H2C1.44772 10 1 9.55229 1 9V3C1 2.44772 1.44771 2 2 2H11ZM14 7C14.5523 7 15 7.44772 15 8V12C15 12.5523 14.5523 13 14 13H13V13.8634C13 14.2984 12.4828 14.5259 12.1621 14.232L10.8182 13H9C8.44772 13 8 12.5523 8 12V8C8 7.44772 8.44772 7 9 7H14Z" />
    ),
  },
  document: {
    height: 16,
    pathSolid: () => {
      const maskId = uuid4();
      return (
        <>
          <mask id={maskId} fill="none">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M13.7007 4.70605C13.8921 4.89408 14 5.15117 14 5.41952V11.8C14 11.9104 13.9105 12 13.8 12L5.33088 12C5.13235 12 5 11.883 5 11.766L5.00007 1.2C5.00007 1.08954 5.08962 1 5.20007 1H9.51809C9.78018 1 10.0318 1.10289 10.2188 1.28653L13.7007 4.70605ZM10 2V5H13L10 2ZM4 12.5001C4 12.7762 4.22386 13.0001 4.5 13.0001H12.0353L12 13.5715V13.8C12 13.9105 11.9105 14 11.8 14H3.5C3.22386 14 3 13.7762 3 13.5V2.20006C3 2.0896 3.08954 2.00006 3.2 2.00006H4V12.5001Z"
            />
          </mask>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M13.7007 4.70605C13.8921 4.89408 14 5.15117 14 5.41952V11.8C14 11.9104 13.9105 12 13.8 12L5.33088 12C5.13235 12 5 11.883 5 11.766L5.00007 1.2C5.00007 1.08954 5.08962 1 5.20007 1H9.51809C9.78018 1 10.0318 1.10289 10.2188 1.28653L13.7007 4.70605ZM10 2V5H13L10 2ZM4 12.5001C4 12.7762 4.22386 13.0001 4.5 13.0001H12.0353L12 13.5715V13.8C12 13.9105 11.9105 14 11.8 14H3.5C3.22386 14 3 13.7762 3 13.5V2.20006C3 2.0896 3.08954 2.00006 3.2 2.00006H4V12.5001Z"
          />
          <path
            mask={`url(#${maskId})`}
            d="M13.7007 4.70605L13 5.41952L13 5.41952L13.7007 4.70605ZM13.8 12L13.8 11L13.8 11L13.8 12ZM5.33088 12L5.33088 13L5.33089 13L5.33088 12ZM5 11.766L4 11.766V11.766H5ZM5.00007 1.2L6.00007 1.20001L6.00007 1.20001L5.00007 1.2ZM10.2188 1.28653L9.51809 2L9.51809 2L10.2188 1.28653ZM10 5H9V6H10V5ZM10 2L10.7071 1.29289L9 -0.414214V2H10ZM13 5V6H15.4142L13.7071 4.29289L13 5ZM12.0353 13.0001L13.0334 13.0618L13.0991 12.0001H12.0353V13.0001ZM12 13.5715L11.0019 13.5098L11 13.5406V13.5715H12ZM4 2.00006H5V1.00006H4V2.00006ZM15 5.41952C15 4.88282 14.7843 4.36864 14.4014 3.99258L13 5.41952L13 5.41952H15ZM15 11.8V5.41952H13V11.8H15ZM13.8 13C14.4627 13 15 12.4627 15 11.8H13C13 11.3581 13.3582 11 13.8 11V13ZM5.33089 13L13.8 13L13.8 11L5.33088 11L5.33089 13ZM4 11.766C4 12.2128 4.24655 12.5416 4.487 12.7239C4.7372 12.9135 5.03794 13 5.33088 13V11C5.4253 11 5.56059 11.028 5.69498 11.1299C5.81963 11.2243 6 11.4361 6 11.766H4ZM4.00007 1.19999L4 11.766L6 11.766L6.00007 1.20001L4.00007 1.19999ZM5.20007 0C4.53733 0 4.00008 0.537254 4.00007 1.19999L6.00007 1.20001C6.00007 1.64183 5.6419 2 5.20007 2V0ZM9.51809 0H5.20007V2H9.51809V0ZM10.9195 0.573061C10.5455 0.205781 10.0423 0 9.51809 0V2L9.51809 2L10.9195 0.573061ZM14.4014 3.99258L10.9195 0.57306L9.51809 2L13 5.41952L14.4014 3.99258ZM11 5V2H9V5H11ZM13 4H10V6H13V4ZM9.29289 2.70711L12.2929 5.70711L13.7071 4.29289L10.7071 1.29289L9.29289 2.70711ZM4.5 12.0001C4.77614 12.0001 5 12.2239 5 12.5001H3C3 13.3285 3.67157 14.0001 4.5 14.0001V12.0001ZM12.0353 12.0001H4.5V14.0001H12.0353V12.0001ZM12.9981 13.6333L13.0334 13.0618L11.0373 12.9383L11.0019 13.5098L12.9981 13.6333ZM13 13.8V13.5715H11V13.8H13ZM11.8 15C12.4627 15 13 14.4628 13 13.8H11C11 13.3582 11.3582 13 11.8 13V15ZM3.5 15H11.8V13H3.5V15ZM2 13.5C2 14.3284 2.67157 15 3.5 15V13C3.77614 13 4 13.2239 4 13.5H2ZM2 2.20006V13.5H4V2.20006H2ZM3.2 1.00006C2.53726 1.00006 2 1.53732 2 2.20006H4C4 2.64189 3.64183 3.00006 3.2 3.00006V1.00006ZM4 1.00006H3.2V3.00006H4V1.00006ZM5 12.5001V2.00006H3V12.5001H5Z"
          />
        </>
      );
    },
    viewBox: "0 0 16 16",
    width: 16,
  },
  "double-arrow-left": {
    height: 21,
    width: 21,
    viewBox: "0 0 21 21",
    pathSolid: () => {
      const maskId1 = uuid4();
      return (
        <>
          <mask
            id={maskId1}
            style={{ maskType: "alpha" }}
            width="21"
            height="21"
            x="0"
            y="0"
            maskUnits="userSpaceOnUse"
          >
            <path fill="#D9D9D9" d="M0.918 0.973H20.918V20.973H0.918z"></path>
          </mask>
          <g fill="#777" mask={`url(#${maskId1})`}>
            <path d="M11 15.973l-5-5 5-5 1.063 1.063-3.938 3.937 3.938 3.938L11 15.973zM14.082 5.973v10h1.5v-10h-1.5z"></path>
          </g>
        </>
      );
    },
  },
  "double-arrow-right": {
    height: 21,
    width: 21,
    viewBox: "0 0 21 21",
    pathSolid: () => {
      const maskId1 = uuid4();
      return (
        <>
          <mask
            id={maskId1}
            style={{ maskType: "alpha" }}
            width="21"
            height="21"
            x="0"
            y="0"
            maskUnits="userSpaceOnUse"
          >
            <path fill="#D9D9D9" d="M0.918 0.473H20.918V20.473H0.918z"></path>
          </mask>
          <g mask={`url(#${maskId1})`}>
            <path
              fill="#fff"
              d="M8.855 10.473L4.918 6.536 5.98 5.473l5 5-5 5-1.062-1.062 3.937-3.938zm5.938 0l-3.938-3.937 1.063-1.063 5 5-5 5-1.063-1.062 3.938-3.938z"
            ></path>
          </g>
        </>
      );
    },
  },
  edx: {
    height: 20,
    width: 20,
    viewBox: "0 0 20 20",
    pathSolid: (
      <>
        <g clip-path="url(#clip0_13452_167346)">
          <path
            d="M5.10067 8.53738C5.01262 8.43441 4.90219 8.35382 4.77087 8.29413C4.63955 8.23444 4.48733 8.20459 4.31273 8.20459C4.13514 8.20459 3.97099 8.23593 3.81728 8.29861C3.66357 8.36128 3.52329 8.45082 3.39794 8.56573C3.27258 8.68064 3.16365 8.81942 3.07262 8.98059C2.98158 9.14176 2.90846 9.32084 2.85474 9.51783H5.35586C5.35884 9.5044 5.36033 9.47753 5.36183 9.43873C5.36332 9.39993 5.36481 9.36263 5.36481 9.32532C5.36481 9.17459 5.34243 9.03133 5.29915 8.89553C5.25587 8.75973 5.19021 8.64035 5.10067 8.53738Z"
            fill="#11252B"
          />
          <path
            d="M9.83734 8.56101C9.73736 8.45207 9.61648 8.36701 9.47322 8.30731C9.32996 8.24762 9.17028 8.21777 8.98971 8.21777C8.75392 8.21777 8.53306 8.27 8.3316 8.37596C8.12865 8.48191 7.95404 8.62816 7.8063 8.81619C7.65857 9.00422 7.54067 9.22956 7.45561 9.48923C7.37055 9.75038 7.32727 10.0354 7.32727 10.3473C7.32727 10.5413 7.35562 10.7189 7.41084 10.8816C7.46606 11.0442 7.54366 11.183 7.64364 11.2979C7.74363 11.4128 7.86301 11.5024 8.00329 11.568C8.14357 11.6322 8.29877 11.665 8.47038 11.665C8.70915 11.665 8.93151 11.6083 9.13446 11.4964C9.33891 11.3845 9.515 11.2293 9.66722 11.0353C9.81794 10.8398 9.93733 10.61 10.0224 10.3473C10.1075 10.0832 10.1507 9.79963 10.1507 9.4937C10.1507 9.29672 10.1239 9.12062 10.0687 8.96393C10.0134 8.80425 9.93584 8.67144 9.83734 8.56101Z"
            fill="#11252B"
          />
          <path
            d="M12.7861 6.43774L13.1712 4.59473H1.85644L0 13.4576H9.45978L8.93896 15.8348H18.0018L20 6.43774H12.7861V6.43774ZM6.13192 9.57906C6.12894 9.64024 6.12446 9.70292 6.11849 9.76709C6.11252 9.83126 6.10655 9.89394 6.10207 9.95512C6.09611 10.0163 6.09014 10.0685 6.08118 10.1103H2.7578C2.75481 10.1506 2.75332 10.1894 2.75183 10.2252C2.75034 10.2625 2.75034 10.3013 2.75034 10.3401C2.75034 10.5774 2.78466 10.7789 2.85331 10.9445C2.92195 11.1117 3.01 11.2475 3.12043 11.3534C3.22937 11.4594 3.35323 11.5355 3.49052 11.5817C3.62782 11.628 3.76511 11.6519 3.90539 11.6519C4.20982 11.6519 4.45605 11.5997 4.6411 11.4937C4.82614 11.3877 4.96792 11.246 5.06492 11.0654H5.84689C5.79913 11.243 5.71855 11.4071 5.60662 11.5609C5.49619 11.7146 5.35592 11.8474 5.18579 11.9608C5.01567 12.0727 4.81868 12.1623 4.59633 12.2264C4.37248 12.2906 4.12625 12.3219 3.85913 12.3219C3.60245 12.3219 3.36069 12.2801 3.13237 12.1951C2.90404 12.1115 2.70557 11.9877 2.53544 11.8235C2.36532 11.6593 2.2325 11.4579 2.13401 11.2191C2.03552 10.9788 1.98627 10.7043 1.98627 10.3939C1.98627 9.96855 2.04447 9.58204 2.16386 9.23284C2.28175 8.88364 2.4474 8.58518 2.65781 8.33447C2.86823 8.08525 3.12192 7.89274 3.41591 7.75545C3.71139 7.61816 4.03373 7.551 4.38442 7.551C4.6217 7.551 4.84555 7.59279 5.05745 7.67486C5.26936 7.75843 5.4559 7.87782 5.61558 8.036C5.77526 8.19419 5.9021 8.38819 5.99612 8.6195C6.09013 8.85081 6.13789 9.11495 6.13789 9.4149C6.1349 9.46415 6.1349 9.51787 6.13192 9.57906ZM9.62244 12.2458L9.75526 11.6176H9.68661C9.51798 11.8369 9.30757 12.01 9.05387 12.1369C8.80018 12.2637 8.52709 12.3264 8.2331 12.3264C8.00179 12.3264 7.7854 12.2831 7.58245 12.1966C7.3795 12.11 7.20191 11.9847 7.04969 11.822C6.89748 11.6593 6.7766 11.4624 6.68855 11.2325C6.60051 11.0012 6.55574 10.7446 6.55574 10.458C6.55574 10.182 6.58409 9.91781 6.63931 9.66561C6.69452 9.41341 6.77362 9.17912 6.87659 8.96422C6.97956 8.74933 7.10192 8.55384 7.24668 8.38073C7.38994 8.20613 7.54962 8.05839 7.72571 7.93602C7.9018 7.81365 8.08984 7.71963 8.29428 7.65397C8.49724 7.58831 8.71064 7.55548 8.93299 7.55548C9.09864 7.55548 9.25533 7.57786 9.40606 7.62412C9.55529 7.67039 9.69109 7.73306 9.81197 7.81365C9.93285 7.89573 10.0373 7.99273 10.1239 8.10913C10.2104 8.22404 10.2746 8.35088 10.3149 8.48818H10.3835L10.9521 5.80798H11.6997L10.3328 12.2458H9.62244ZM17.0318 14.6231H15.3753L14.2919 12.2458H14.1561L12.1519 14.6231H10.5059L13.6293 10.9221L12.1818 7.648H13.8696L14.8321 9.9014H14.9217L16.7423 7.648H18.4107L15.429 11.0848L17.0318 14.6231Z"
            fill="#11252B"
          />
        </g>
        <defs>
          <clipPath id="clip0_13452_167346">
            <rect width="20" height="20" fill="white" />
          </clipPath>
        </defs>
      </>
    ),
  },
  email: {
    height: 16,
    width: 16,
    viewBox: "0 0 16 16",
    pathSolid: (
      <>
        <path d="M1.53568 5.6607C1.73808 5.80353 2.34818 6.22771 3.36602 6.93304C4.38388 7.63836 5.16364 8.18145 5.70531 8.56232C5.76483 8.60407 5.89126 8.69482 6.08468 8.8347C6.27814 8.97468 6.43888 9.08779 6.56678 9.17408C6.69478 9.26033 6.84952 9.35708 7.03115 9.46419C7.21272 9.57121 7.38388 9.6517 7.54459 9.70499C7.70533 9.75872 7.85413 9.78533 7.99101 9.78533H7.99999H8.00899C8.14587 9.78533 8.29473 9.75869 8.45547 9.70499C8.61612 9.6517 8.78743 9.57111 8.96885 9.46419C9.15036 9.35696 9.30509 9.26029 9.43309 9.17408C9.56109 9.08779 9.72171 8.97468 9.91523 8.8347C10.1086 8.69469 10.2352 8.60407 10.2947 8.56232C10.8422 8.18145 12.2352 7.21415 14.4732 5.66052C14.9077 5.35708 15.2708 4.99093 15.5624 4.56234C15.8542 4.13393 16 3.68451 16 3.21433C16 2.82142 15.8585 2.69948 15.5758 2.41974C15.293 2.13994 14.9581 2.00012 14.5714 2.00012H1.42851C0.970207 2.00012 0.617512 2.15486 0.370489 2.46434C0.123496 2.77387 0 2.94638 0 3.41063C0 3.78562 0.163744 4.19198 0.491077 4.62945C0.818378 5.06696 1.16669 5.41074 1.53568 5.6607Z" />
        <path d="M15.107 6.27681C13.1548 7.59812 11.6725 8.62499 10.6607 9.35722C10.3215 9.60712 10.0462 9.8022 9.83485 9.94199C9.62348 10.0819 9.34237 10.2247 8.99111 10.3705C8.63998 10.5165 8.31274 10.5892 8.00912 10.5892H8.00002H7.99104C7.68747 10.5892 7.36005 10.5165 7.00892 10.3705C6.65779 10.2247 6.37649 10.0819 6.16518 9.94199C5.95393 9.8022 5.67861 9.60712 5.33936 9.35722C4.53574 8.76797 3.0566 7.74104 0.901876 6.27681C0.562504 6.05077 0.26191 5.79168 0 5.50006V12.5891C0 12.9822 0.139821 13.3184 0.419618 13.5982C0.699353 13.878 1.03572 14.0179 1.4286 14.0179H14.5715C14.9643 14.0179 15.3006 13.878 15.5804 13.5982C15.8602 13.3183 16 12.9823 16 12.5891V5.50006C15.744 5.78564 15.4465 6.04474 15.107 6.27681Z" />
      </>
    ),
  },
  explore: {
    height: 22,
    width: 22,
    viewBox: "0 0 22 22",
    pathSolid: (props: IconPathProps) => (
      <>
        <circle cx="5.5" cy="14.5" r="3" fill="none" stroke={props.fill} />
        <path
          d="M5 6.5C5 6.77614 5.22386 7 5.5 7C5.77614 7 6 6.77614 6 6.5H5ZM6 6.5C6 5.94772 6.44772 5.5 7 5.5V4.5C5.89543 4.5 5 5.39543 5 6.5H6ZM7 5.5C7.55228 5.5 8 5.94772 8 6.5H9C9 5.39543 8.10457 4.5 7 4.5V5.5ZM8 6.5V15H9V6.5H8Z"
          fill={props.fill}
        />
        <path d="M17 6.5C17 6.77614 16.7761 7 16.5 7C16.2239 7 16 6.77614 16 6.5H17ZM16 6.5C16 5.94772 15.5523 5.5 15 5.5V4.5C16.1046 4.5 17 5.39543 17 6.5H16ZM15 5.5C14.4477 5.5 14 5.94772 14 6.5H13C13 5.39543 13.8954 4.5 15 4.5V5.5ZM14 6.5V15H13V6.5H14Z" />
        <circle cx="16.5" cy="14.5" r="3" fill="none" stroke={props.fill} />
        <path d="M2.53576 12.3143C2.43321 12.5707 2.55792 12.8617 2.81431 12.9642C3.0707 13.0668 3.36168 12.9421 3.46424 12.6857L2.53576 12.3143ZM5 7.4999L4.55278 7.27631L4.54348 7.29491L4.53576 7.31421L5 7.4999ZM3.46424 12.6857L5.46424 7.68559L4.53576 7.31421L2.53576 12.3143L3.46424 12.6857ZM5.44722 7.72349C5.63431 7.34927 5.97723 7.0727 6.39826 7.02115C6.81255 6.97043 7.41809 7.12509 8.14645 7.85345L8.85355 7.14635C7.98191 6.27471 7.08745 5.92931 6.27674 6.02856C5.47277 6.12699 4.86569 6.65041 4.55278 7.27631L5.44722 7.72349Z" />
        <path d="M19.4642 12.3143C19.5668 12.5707 19.4421 12.8617 19.1857 12.9642C18.9293 13.0668 18.6383 12.9421 18.5358 12.6857L19.4642 12.3143ZM17 7.4999L17.4472 7.27631L17.4565 7.29491L17.4642 7.31421L17 7.4999ZM18.5358 12.6857L16.5358 7.68559L17.4642 7.31421L19.4642 12.3143L18.5358 12.6857ZM16.5528 7.72349C16.3657 7.34927 16.0228 7.0727 15.6017 7.02115C15.1875 6.97043 14.5819 7.12509 13.8536 7.85345L13.1464 7.14635C14.0181 6.27471 14.9125 5.92931 15.7233 6.02856C16.5272 6.12699 17.1343 6.65041 17.4472 7.27631L16.5528 7.72349Z" />
        <path d="M13.5 9H8.5" stroke={props.fill} />
        <path d="M13.5 13H8.5" stroke={props.fill} />
        <path d="M13.5 15H8.5" stroke={props.fill} />
      </>
    ),
  },
  eye: {
    height: 16,
    pathSolid: (
      <>
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M8 11C9.933 11 11.5 9.433 11.5 7.5C11.5 5.567 9.933 4 8 4C6.067 4 4.5 5.567 4.5 7.5C4.5 9.433 6.067 11 8 11ZM6.699 6.46779C6.85383 6.28229 6.82897 6.0064 6.64347 5.85156C6.45797 5.69673 6.18208 5.72159 6.02725 5.90709C5.09826 7.02008 5.24741 8.67543 6.3604 9.60442C6.5459 9.75925 6.82179 9.73439 6.97662 9.54889C7.13145 9.3634 7.10659 9.0875 6.9211 8.93267C6.17911 8.31334 6.07967 7.20978 6.699 6.46779Z"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M8 3C4.13173 3 2.44243 5.42857 1.34708 7.02311L1.34186 7.0307C1.22923 7.19466 1.11644 7.35886 1.00904 7.50719C1.35881 8.26719 1.97819 9.38166 3.06296 10.3102C4.15969 11.2491 5.73426 12 8 12C10.2657 12 11.8403 11.2491 12.937 10.3102C14.0218 9.38166 14.6412 8.26719 14.991 7.50719C14.8835 7.35883 14.7707 7.19459 14.6581 7.03059L14.6529 7.02311C13.5576 5.42857 11.8683 3 8 3ZM8 2C3.58442 2 1.63222 4.84189 0.52282 6.45689L0.522802 6.45692C0.398276 6.6382 0.284368 6.80402 0.178787 6.94862C-0.0195136 7.22022 -0.0557636 7.5827 0.0846116 7.89031C0.460741 8.71457 1.15636 9.9945 2.41266 11.0699C3.68551 12.1595 5.48919 13 8 13C10.5108 13 12.3145 12.1595 13.5873 11.0699C14.8436 9.9945 15.5393 8.71457 15.9154 7.89031C16.0558 7.5827 16.0195 7.22022 15.8212 6.94862C15.7156 6.80401 15.6017 6.63818 15.4772 6.45689C14.3678 4.84189 12.4156 2 8 2Z"
        />
      </>
    ),
    viewBox: "0 0 16 16",
    width: 16,
  },
  "eye-closed": {
    height: 16,
    pathSolid: (
      <>
        <path
          fillRule="evenodd"
          d="M.143 2.31a.75.75 0 011.047-.167l14.5 10.5a.75.75 0 11-.88 1.214l-2.248-1.628C11.346 13.19 9.792 14 8 14c-1.981 0-3.67-.992-4.933-2.078C1.797 10.832.88 9.577.43 8.9a1.618 1.618 0 010-1.797c.353-.533.995-1.42 1.868-2.305L.31 3.357A.75.75 0 01.143 2.31zm3.386 3.378a14.21 14.21 0 00-1.85 2.244.12.12 0 00-.022.068c0 .021.006.045.022.068.412.621 1.242 1.75 2.366 2.717C5.175 11.758 6.527 12.5 8 12.5c1.195 0 2.31-.488 3.29-1.191L9.063 9.695A2 2 0 016.058 7.52l-2.53-1.832zM8 3.5c-.516 0-1.017.09-1.499.251a.75.75 0 11-.473-1.423A6.23 6.23 0 018 2c1.981 0 3.67.992 4.933 2.078 1.27 1.091 2.187 2.345 2.637 3.023a1.619 1.619 0 010 1.798c-.11.166-.248.365-.41.587a.75.75 0 11-1.21-.887c.148-.201.272-.382.371-.53a.119.119 0 000-.137c-.412-.621-1.242-1.75-2.366-2.717C10.825 4.242 9.473 3.5 8 3.5z"
        />
      </>
    ),
    viewBox: "0 0 16 16",
    width: 16,
  },
  "eye-navy": {
    height: 18,
    width: 18,
    pathSolid: (
      <path
        fillRule="evenodd"
        fill="#003E6B"
        clipRule="evenodd"
        d="M9 14.5C3.686 14.5 1 9 1 9s2.686-5.5 8-5.5S17 9 17 9s-2.686 5.5-8 5.5zM2.341 9.315A10.69 10.69 0 012.145 9a11.56 11.56 0 011.58-2.053C4.956 5.679 6.714 4.5 9 4.5c2.286 0 4.044 1.18 5.275 2.447A11.57 11.57 0 0115.855 9a11.568 11.568 0 01-1.58 2.053C13.044 12.321 11.286 13.5 9 13.5c-2.286 0-4.044-1.18-5.275-2.447A11.559 11.559 0 012.34 9.315zm-.443-.755zM12.5 9a3.5 3.5 0 11-7 0 3.5 3.5 0 017 0zM9 7.5A1.5 1.5 0 007.5 9a.5.5 0 01-1 0A2.5 2.5 0 019 6.5a.5.5 0 010 1z"
      />
    ),
    viewBox: "0 0 18 18",
  },
  facebook: {
    height: 36,
    width: 36,
    viewBox: "0 0 36 36",
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M21.0971 9.05255L18.9434 9.04883C16.5238 9.04883 14.9601 10.7765 14.9601 13.4505V15.4799H12.7947C12.6076 15.4799 12.4561 15.6433 12.4561 15.8448V18.7853C12.4561 18.9868 12.6078 19.15 12.7947 19.15H14.9601V26.5697C14.9601 26.7712 15.1117 26.9344 15.2988 26.9344H18.1241C18.3112 26.9344 18.4627 26.771 18.4627 26.5697V19.15H20.9946C21.1817 19.15 21.3332 18.9868 21.3332 18.7853L21.3343 15.8448C21.3343 15.7481 21.2985 15.6554 21.2351 15.5869C21.1717 15.5185 21.0853 15.4799 20.9955 15.4799H18.4627V13.7595C18.4627 12.9327 18.6457 12.5129 19.6459 12.5129L21.0967 12.5123C21.2837 12.5123 21.4352 12.349 21.4352 12.1476V9.41725C21.4352 9.2161 21.2838 9.05292 21.0971 9.05255Z",
    }),
  },
  "facebook-color": {
    height: 20,
    width: 20,
    viewBox: "0 0 20 20",
    pathSolid: (
      <>
        <path
          d="M14.2397 9.98074H11.2436V20H6.70431V9.98074H4.54541V6.45957H6.70431V4.18098C6.70431 2.55154 7.55225 0 11.284 0L14.6464 0.0128407V3.43073H12.2068C11.8066 3.43073 11.2439 3.61324 11.2439 4.39053V6.46285H14.6362L14.2397 9.98074Z"
          fill="#3B5998"
        />
      </>
    ),
  },
  favorite: {
    height: 18,
    width: 18,
    viewBox: "0 0 18 18",
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M9 1.625a.5.5 0 01.442.267l2.293 4.344 4.84.838a.5.5 0 01.273.841l-3.423 3.523.699 4.862a.5.5 0 01-.716.52L9 14.652 4.592 16.82a.5.5 0 01-.716-.52l.7-4.862-3.424-3.523a.5.5 0 01.273-.841l4.84-.838 2.293-4.344A.5.5 0 019 1.625zm0 1.571L7.035 6.92a.5.5 0 01-.357.26l-4.148.718 2.934 3.019a.5.5 0 01.136.42l-.599 4.167 3.778-1.858a.5.5 0 01.442 0l3.778 1.858-.6-4.167a.5.5 0 01.137-.42l2.935-3.02-4.15-.717a.5.5 0 01-.356-.26L9 3.196z",
    }),
  },
  "favorite-toggled": {
    height: 18,
    width: 18,
    viewBox: "0 0 18 18",
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M9.942 1.892a.5.5 0 00-.884 0L6.766 6.236l-4.84.838a.5.5 0 00-.274.841l3.424 3.523-.7 4.862a.5.5 0 00.716.52L9.5 14.652l4.408 2.168a.5.5 0 00.716-.52l-.7-4.862 3.424-3.523a.5.5 0 00-.273-.841l-4.84-.838-2.293-4.344z",
    }),
  },
  file: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M6 5H2V4h4v1zM2 8h7V7H2v1zm0 2h7V9H2v1zm0 2h7v-1H2v1zm10-7.5V14c0 .55-.45 1-1 1H1c-.55 0-1-.45-1-1V2c0-.55.45-1 1-1h7.5L12 4.5zM11 5L8 2H1v12h10V5z",
    }),
    viewBox: "0 0 12 16",
    width: 12,
  },
  "file-google-upload": {
    height: 163,
    pathSolid: (
      <>
        <svg
          width="87"
          height="163"
          viewBox="0 0 87 163"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M87 159.457C87 161.406 85.3688 163 83.375 163H3.625C1.63125 163 0 161.406 0 159.457V124.022C0 122.073 1.63125 120.479 3.625 120.479H83.375C85.3688 120.479 87 122.073 87 124.022V159.457Z"
            fill="#003E6B"
          />
          <path
            d="M87 29.7138L63.5769 23.1107L56.8846 0H10.0385C4.49433 0 0 4.43439 0 9.90459V102.788C0 108.258 4.49433 112.692 10.0385 112.692H76.9615C82.5057 112.692 87 108.258 87 102.788V29.7138Z"
            fill="#4086F4"
          />
          <path
            d="M87 29.7138V102.788C87 108.258 82.5057 112.692 76.9615 112.692H43.5V0H56.8846L63.5769 23.1107L87 29.7138Z"
            fill="#4175DF"
          />
          <path
            d="M87 29.7138H63.577C59.8962 29.7138 56.8846 26.7424 56.8846 23.1107V0C57.7546 0 58.6246 0.330153 59.2267 0.990679L85.996 27.4029C86.6654 27.997 87 28.8554 87 29.7138Z"
            fill="#80AEF8"
          />
          <path
            d="M63.5769 53.0445H23.4231C21.5735 53.0445 20.0769 51.5678 20.0769 49.7429C20.0769 47.9181 21.5735 46.4414 23.4231 46.4414H63.5769C65.4264 46.4414 66.9231 47.9181 66.9231 49.7429C66.9231 51.5678 65.4264 53.0445 63.5769 53.0445Z"
            fill="#FFF5F5"
          />
          <path
            d="M63.5769 66.2505H23.4231C21.5735 66.2505 20.0769 64.7739 20.0769 62.949C20.0769 61.1241 21.5735 59.6475 23.4231 59.6475H63.5769C65.4264 59.6475 66.9231 61.1241 66.9231 62.949C66.9231 64.7739 65.4264 66.2505 63.5769 66.2505Z"
            fill="#FFF5F5"
          />
          <path
            d="M63.5769 79.4566H23.4231C21.5735 79.4566 20.0769 77.9799 20.0769 76.155C20.0769 74.3302 21.5735 72.8535 23.4231 72.8535H63.5769C65.4264 72.8535 66.9231 74.3302 66.9231 76.155C66.9231 77.9799 65.4264 79.4566 63.5769 79.4566Z"
            fill="#FFF5F5"
          />
          <path
            d="M50.1923 92.6626H23.4231C21.5735 92.6626 20.0769 91.186 20.0769 89.3611C20.0769 87.5362 21.5735 86.0596 23.4231 86.0596H50.1923C52.0418 86.0596 53.5384 87.5362 53.5384 89.3611C53.5384 91.186 52.0418 92.6626 50.1923 92.6626Z"
            fill="#FFF5F5"
          />
          <path
            d="M43.5 92.6626H50.1923C52.0418 92.6626 53.5385 91.186 53.5385 89.3611C53.5385 87.5362 52.0418 86.0596 50.1923 86.0596H43.5V92.6626Z"
            fill="#E3E7EA"
          />
          <path
            d="M43.5 79.4566H63.5769C65.4264 79.4566 66.9231 77.9799 66.9231 76.155C66.9231 74.3302 65.4264 72.8535 63.5769 72.8535H43.5V79.4566Z"
            fill="#E3E7EA"
          />
          <path
            d="M43.5 66.2505H63.5769C65.4264 66.2505 66.9231 64.7739 66.9231 62.949C66.9231 61.1241 65.4264 59.6475 63.5769 59.6475H43.5V66.2505Z"
            fill="#E3E7EA"
          />
          <path
            d="M43.5 53.0445H63.5769C65.4264 53.0445 66.9231 51.5678 66.9231 49.7429C66.9231 47.9181 65.4264 46.4414 63.5769 46.4414H43.5V53.0445Z"
            fill="#E3E7EA"
          />
          <path
            d="M71.0152 151.163C66.2027 151.163 63.7486 148.778 63.7486 144.103V140.859C63.7486 136.184 66.2027 133.799 71.0152 133.799C75.6684 133.799 78.0587 135.993 78.1225 140.509H73.6286C73.533 138.601 72.8637 137.615 71.0789 137.615C69.2942 137.615 68.4655 138.601 68.4655 140.891V144.103C68.4655 146.361 69.2942 147.347 71.0789 147.347C72.8637 147.347 73.5649 146.361 73.6286 144.453H78.1225C78.0587 148.969 75.6684 151.163 71.0152 151.163Z"
            fill="white"
          />
          <path
            d="M53.3124 151.163C48.2767 151.163 45.7589 148.778 45.7589 144.008V140.955C45.7589 136.184 48.2767 133.799 53.3124 133.799C58.348 133.799 60.8658 136.184 60.8658 140.955V144.008C60.8658 148.778 58.348 151.163 53.3124 151.163ZM53.3124 147.347C55.3202 147.347 56.1489 146.329 56.1489 143.912V141.05C56.1489 138.633 55.3202 137.615 53.3124 137.615C51.3045 137.615 50.4758 138.633 50.4758 141.05V143.912C50.4758 146.329 51.3045 147.347 53.3124 147.347Z"
            fill="white"
          />
          <path
            d="M33.2622 151.164C29.4695 151.164 27.3342 148.842 27.3342 144.358V140.637C27.3342 136.152 29.4695 133.799 33.2622 133.799C35.047 133.799 36.4493 134.34 37.5648 135.516V127.565H42.2817V150.782H38.2659L38.1385 148.778C36.8955 150.432 35.3657 151.164 33.2622 151.164ZM34.3777 147.347C35.8437 147.347 36.8955 146.488 37.5648 144.835V140.16C36.8955 138.474 35.8437 137.615 34.3458 137.615C32.8479 137.615 32.0511 138.474 32.0511 140.637V144.326C32.0511 146.457 32.8479 147.347 34.3777 147.347Z"
            fill="white"
          />
          <path
            d="M16.2398 156.952C11.8097 156.952 9.35563 155.362 9.1644 151.481H13.5945C13.722 152.754 14.5187 153.262 16.176 153.262C18.2477 153.262 19.1082 152.467 19.1082 150.337V148.428C17.9927 149.605 16.5904 150.146 14.8056 150.146C11.0129 150.146 8.87756 147.792 8.87756 143.308V140.605C8.87756 136.12 11.0129 133.799 14.8056 133.799C16.9091 133.799 18.4389 134.53 19.6819 136.184L19.8093 134.18H23.8251V150.337C23.8251 154.884 21.3392 156.952 16.2398 156.952ZM15.8892 146.329C17.3871 146.329 18.4389 145.471 19.1082 143.785V140.128C18.4389 138.474 17.3871 137.615 15.9211 137.615C14.3913 137.615 13.5945 138.506 13.5945 140.637V143.308C13.5945 145.471 14.3913 146.329 15.8892 146.329Z"
            fill="white"
          />
        </svg>
      </>
    ),
    viewBox: "0 0 87 163",
    width: 87,
  },
  "file-pdf-upload": {
    height: 118,
    pathSolid: (
      <>
        <path
          d="M55.982 24.3519L38.5654 7.20605H0V78.239H55.982V24.3519Z"
          fill="#668BA6"
        />
        <path
          d="M12.4403 35.3742H43.5415C44.2282 35.3742 44.7855 34.8268 44.7855 34.1495C44.7855 33.4722 44.2282 32.9248 43.5415 32.9248H12.4403C11.7536 32.9248 11.1963 33.4722 11.1963 34.1495C11.1963 34.8268 11.7524 35.3742 12.4403 35.3742Z"
          fill="#CCD8E1"
        />
        <path
          d="M12.4403 25.5764H24.8808C25.5675 25.5764 26.1248 25.0289 26.1248 24.3517C26.1248 23.6744 25.5675 23.127 24.8808 23.127H12.4403C11.7536 23.127 11.1963 23.6744 11.1963 24.3517C11.1963 25.0289 11.7524 25.5764 12.4403 25.5764Z"
          fill="#CCD8E1"
        />
        <path
          d="M43.5415 42.7217H12.4403C11.7536 42.7217 11.1963 43.2691 11.1963 43.9464C11.1963 44.6236 11.7536 45.1711 12.4403 45.1711H43.5415C44.2282 45.1711 44.7855 44.6236 44.7855 43.9464C44.7855 43.2691 44.2282 42.7217 43.5415 42.7217Z"
          fill="#CCD8E1"
        />
        <path
          d="M43.5415 52.5195H12.4403C11.7536 52.5195 11.1963 53.067 11.1963 53.7442C11.1963 54.4215 11.7536 54.9689 12.4403 54.9689H43.5415C44.2282 54.9689 44.7855 54.4215 44.7855 53.7442C44.7855 53.067 44.2282 52.5195 43.5415 52.5195Z"
          fill="#CCD8E1"
        />
        <path
          d="M43.5415 62.3174H12.4403C11.7536 62.3174 11.1963 62.8648 11.1963 63.5421C11.1963 64.2193 11.7536 64.7668 12.4403 64.7668H43.5415C44.2282 64.7668 44.7855 64.2193 44.7855 63.5421C44.7855 62.8648 44.2282 62.3174 43.5415 62.3174Z"
          fill="#CCD8E1"
        />
        <path
          d="M38.5654 7.20605V24.3519H55.9821L38.5654 7.20605Z"
          fill="#CCD8E1"
        />
        <path
          d="M63.302 17.9565L45.2157 0H7.75122V70.5435H63.302V17.9565Z"
          fill="#CCD8E1"
        />
        <path
          d="M19.3783 29.4998H51.6753C52.3884 29.4998 52.9672 28.9265 52.9672 28.2172C52.9672 27.5079 52.3884 26.9346 51.6753 26.9346H19.3783C18.6652 26.9346 18.0864 27.5079 18.0864 28.2172C18.0864 28.9265 18.6639 29.4998 19.3783 29.4998Z"
          fill="#003E6B"
        />
        <path
          d="M19.3894 20.5213H32.4196C33.1389 20.5213 33.7226 19.9479 33.7226 19.2387C33.7226 18.5294 33.1389 17.9561 32.4196 17.9561H19.3894C18.6702 17.9561 18.0864 18.5294 18.0864 19.2387C18.0864 19.9479 18.6689 20.5213 19.3894 20.5213Z"
          fill="#003E6B"
        />
        <path
          d="M51.6753 35.9121H19.3783C18.6652 35.9121 18.0864 36.4854 18.0864 37.1947C18.0864 37.904 18.6652 38.4773 19.3783 38.4773H51.6753C52.3884 38.4773 52.9672 37.904 52.9672 37.1947C52.9672 36.4854 52.3884 35.9121 51.6753 35.9121Z"
          fill="#003E6B"
        />
        <path
          d="M51.6753 44.8906H19.3783C18.6652 44.8906 18.0864 45.464 18.0864 46.1732C18.0864 46.8825 18.6652 47.4558 19.3783 47.4558H51.6753C52.3884 47.4558 52.9672 46.8825 52.9672 46.1732C52.9672 45.464 52.3884 44.8906 51.6753 44.8906Z"
          fill="#003E6B"
        />
        <path
          d="M51.6753 53.8691H19.3783C18.6652 53.8691 18.0864 54.4425 18.0864 55.1517C18.0864 55.861 18.6652 56.4344 19.3783 56.4344H51.6753C52.3884 56.4344 52.9672 55.861 52.9672 55.1517C52.9672 54.4425 52.3884 53.8691 51.6753 53.8691Z"
          fill="#003E6B"
        />
        <path d="M45.2158 0V17.9565H63.3021L45.2158 0Z" fill="#668BA6" />
        <path
          d="M63.3021 115.435C63.3021 116.846 62.1152 118 60.6645 118H2.63759C1.18691 118 0 116.846 0 115.435V89.783C0 88.3721 1.18691 87.2178 2.63759 87.2178H60.6645C62.1152 87.2178 63.3021 88.3721 63.3021 89.783V115.435Z"
          fill="#ED3431"
        />
        <path
          d="M18.2174 104.582H15.4722V108.741C15.4722 109.335 15.3309 109.786 15.0483 110.094C14.7657 110.401 14.4091 110.555 13.9784 110.555C13.5276 110.555 13.1643 110.404 12.8884 110.104C12.6125 109.796 12.4746 109.349 12.4746 108.761V97.438C12.4746 96.7833 12.626 96.3157 12.9288 96.0351C13.2316 95.7545 13.7127 95.6143 14.3721 95.6143H18.2174C19.3545 95.6143 20.2292 95.7011 20.8415 95.8748C21.4471 96.0418 21.9686 96.319 22.4059 96.7065C22.85 97.0939 23.1864 97.5682 23.4152 98.1294C23.644 98.6905 23.7584 99.3218 23.7584 100.023C23.7584 101.52 23.2941 102.655 22.3656 103.43C21.437 104.198 20.0543 104.582 18.2174 104.582ZM17.4907 97.8488H15.4722V102.338H17.4907C18.1972 102.338 18.786 102.264 19.257 102.117C19.7347 101.971 20.098 101.73 20.347 101.396C20.596 101.062 20.7204 100.624 20.7204 100.083C20.7204 99.4354 20.5287 98.9076 20.1451 98.5001C19.7145 98.0659 18.8297 97.8488 17.4907 97.8488Z"
          fill="white"
        />
        <path
          d="M28.1185 95.6143H32.0244C33.0404 95.6143 33.9117 95.7078 34.6384 95.8948C35.3651 96.0819 36.0245 96.4326 36.6166 96.947C38.1574 98.2563 38.9279 100.247 38.9279 102.919C38.9279 103.801 38.8505 104.606 38.6957 105.334C38.541 106.062 38.3021 106.72 37.9791 107.308C37.6562 107.889 37.2424 108.41 36.7377 108.871C36.3407 109.225 35.9067 109.509 35.4357 109.723C34.9647 109.937 34.4567 110.087 33.9117 110.174C33.3734 110.261 32.7645 110.304 32.0849 110.304H28.179C27.634 110.304 27.2236 110.224 26.9477 110.064C26.6718 109.897 26.4902 109.666 26.4027 109.372C26.3219 109.072 26.2816 108.684 26.2816 108.21V97.438C26.2816 96.7967 26.4262 96.3324 26.7156 96.0451C27.0049 95.7579 27.4725 95.6143 28.1185 95.6143ZM29.2791 97.969V107.939H31.55C32.0479 107.939 32.4382 107.926 32.7208 107.899C33.0034 107.873 33.2961 107.806 33.5989 107.699C33.9016 107.592 34.1641 107.442 34.3861 107.248C35.3886 106.406 35.8899 104.957 35.8899 102.899C35.8899 101.449 35.6679 100.364 35.2238 99.6424C34.7864 98.921 34.2448 98.4634 33.5989 98.2697C32.9529 98.0693 32.1724 97.969 31.2573 97.969H29.2791Z"
          fill="white"
        />
        <path
          d="M50.4034 97.9089H44.4991V101.667H49.4345C49.892 101.667 50.2318 101.77 50.4538 101.977C50.6826 102.178 50.797 102.448 50.797 102.789C50.797 103.13 50.6826 103.4 50.4538 103.6C50.2251 103.801 49.8853 103.901 49.4345 103.901H44.4991V108.741C44.4991 109.356 44.3578 109.813 44.0752 110.114C43.7993 110.408 43.4427 110.555 43.0054 110.555C42.5613 110.555 42.1979 110.404 41.9153 110.104C41.6395 109.803 41.5015 109.349 41.5015 108.741V97.438C41.5015 97.0104 41.5654 96.6631 41.6933 96.3958C41.8211 96.122 42.0196 95.9249 42.2888 95.8046C42.5646 95.6777 42.9145 95.6143 43.3384 95.6143H50.4034C50.8811 95.6143 51.2344 95.7211 51.4631 95.9349C51.6986 96.142 51.8164 96.4159 51.8164 96.7566C51.8164 97.104 51.6986 97.3845 51.4631 97.5983C51.2344 97.8054 50.8811 97.9089 50.4034 97.9089Z"
          fill="white"
        />
      </>
    ),
    viewBox: "0 0 64 118",
    width: 64,
  },
  "file-submodule": {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M10 7H4v7h9c.55 0 1-.45 1-1V8h-4V7zM9 9H5V8h4v1zm4-5H7V3c0-.66-.31-1-1-1H1c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h2V7c0-.55.45-1 1-1h6c.55 0 1 .45 1 1h3V5c0-.55-.45-1-1-1zM6 4H1V3h5v1z",
    }),
    viewBox: "0 0 14 16",
    width: 14,
  },
  filter: {
    width: 44,
    height: 44,
    viewBox: "0 0 44 44",
    pathSolid: (
      <>
        <rect width="44" height="44" rx="6.75" fill="white" />
        <rect
          x="13.9004"
          y="32.8"
          width="21.6"
          height="1.35"
          rx="0.675"
          transform="rotate(-90 13.9004 32.8)"
          fill="#003E6B"
        />
        <circle
          cx="14.5758"
          cy="17.275"
          r="2.025"
          transform="rotate(-90 14.5758 17.275)"
          fill="#003E6B"
        />
        <rect
          width="21.6"
          height="1.35"
          rx="0.675"
          transform="matrix(4.37114e-08 1 1 -4.37114e-08 22 11.2)"
          fill="#003E6B"
        />
        <circle
          cx="2.025"
          cy="2.025"
          r="2.025"
          transform="matrix(4.37114e-08 1 1 -4.37114e-08 20.6504 24.7)"
          fill="#003E6B"
        />
        <rect
          width="21.6"
          height="1.35"
          rx="0.675"
          transform="matrix(4.37114e-08 1 1 -4.37114e-08 30.0996 11.2)"
          fill="#003E6B"
        />
        <circle
          cx="2.025"
          cy="2.025"
          r="2.025"
          transform="matrix(4.37114e-08 1 1 -4.37114e-08 28.75 17.95)"
          fill="#003E6B"
        />
      </>
    ),
  },
  flag: {
    width: 9,
    height: 12,
    pathSolid: <path d="M1 0H0V1V7V12H1V7H9L7 4L9 1H1V0Z" />,
    viewBox: "0 0 9 12",
  },
  flame: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M5.05.31c.81 2.17.41 3.38-.52 4.31C3.55 5.67 1.98 6.45.9 7.98c-1.45 2.05-1.7 6.53 3.53 7.7-2.2-1.16-2.67-4.52-.3-6.61-.61 2.03.53 3.33 1.94 2.86 1.39-.47 2.3.53 2.27 1.67-.02.78-.31 1.44-1.13 1.81 3.42-.59 4.78-3.42 4.78-5.56 0-2.84-2.53-3.22-1.25-5.61-1.52.13-2.03 1.13-1.89 2.75.09 1.08-1.02 1.8-1.86 1.33-.67-.41-.66-1.19-.06-1.78C8.18 5.31 8.68 2.45 5.05.32L5.03.3l.02.01z",
    }),
    viewBox: "0 0 12 16",
    width: 12,
  },
  flask: {
    height: 16,
    pathSolid: (
      <>
        <path
          fillRule="evenodd"
          d="M4 1c-.71 0-1 .29-1 1 0 .56.5 1 1 1v1.786C2.5 5 1 6.956 1 8.714 1 11.081 2.919 13 5.286 13c2.367 0 4.285-1.919 4.285-4.286C9.571 6.956 8.5 5.5 7 4.786V3c1 0 1-.44 1-1 0-.71-.29-1-1-1H4zm1.286 11.143c1.893 0 3.428-1.535 3.428-3.429H1.857c0 1.894 1.535 3.429 3.429 3.429z"
        />
        <path
          fillRule="evenodd"
          d="M8.715 8.784V8.77v-.054.07zm1.715-.07c0-1.524-.663-2.892-1.715-3.833V2.714c0-.473.384-.857.857-.857h2.571c.474 0 .858.384.858.857v.857h-2.572v.858h2.572v.857h-1.715v.857h1.715V7h-1.715v.857h1.715v3C13 12.041 12.04 13 10.858 13c-.708 0-1.335-.343-1.725-.871.807-.909 1.297-2.104 1.297-3.415z"
        />
      </>
    ),
    viewBox: "0 0 12 16",
    width: 12,
  },
  fold: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M7 9l3 3H8v3H6v-3H4l3-3zm3-6H8V0H6v3H4l3 3 3-3zm4 2c0-.55-.45-1-1-1h-2.5l-1 1h3l-2 2h-7l-2-2h3l-1-1H1c-.55 0-1 .45-1 1l2.5 2.5L0 10c0 .55.45 1 1 1h2.5l1-1h-3l2-2h7l2 2h-3l1 1H13c.55 0 1-.45 1-1l-2.5-2.5L14 5z",
    }),
    viewBox: "0 0 14 16",
    width: 14,
  },
  folder: {
    height: 18,
    width: 18,
    fill: "none",
    viewBox: "0 0 18 18",
    pathSolid: React.createElement("path", {
      fill: "#003E6B",
      d: "M7.6 3H3.4c-.773 0-1.393.671-1.393 1.5L2 13.5c0 .829.627 1.5 1.4 1.5h11.2c.774 0 1.4-.671 1.4-1.5V6c0-.829-.627-1.5-1.4-1.5H9L7.6 3z",
    }),
  },
  gear: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M14 8.77v-1.6l-1.94-.64-.45-1.09.88-1.84-1.13-1.13-1.81.91-1.09-.45-.69-1.92h-1.6l-.63 1.94-1.11.45-1.84-.88-1.13 1.13.91 1.81-.45 1.09L0 7.23v1.59l1.94.64.45 1.09-.88 1.84 1.13 1.13 1.81-.91 1.09.45.69 1.92h1.59l.63-1.94 1.11-.45 1.84.88 1.13-1.13-.92-1.81.47-1.09L14 8.75v.02zM7 11c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3z",
    }),
    viewBox: "0 0 14 16",
    width: 14,
  },
  "git-compare": {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M5 12H4c-.27-.02-.48-.11-.69-.31-.21-.2-.3-.42-.31-.69V4.72A1.993 1.993 0 0 0 2 1a1.993 1.993 0 0 0-1 3.72V11c.03.78.34 1.47.94 2.06.6.59 1.28.91 2.06.94h1v2l3-3-3-3v2zM2 1.8c.66 0 1.2.55 1.2 1.2 0 .65-.55 1.2-1.2 1.2C1.35 4.2.8 3.65.8 3c0-.65.55-1.2 1.2-1.2zm11 9.48V5c-.03-.78-.34-1.47-.94-2.06-.6-.59-1.28-.91-2.06-.94H9V0L6 3l3 3V4h1c.27.02.48.11.69.31.21.2.3.42.31.69v6.28A1.993 1.993 0 0 0 12 15a1.993 1.993 0 0 0 1-3.72zm-1 2.92c-.66 0-1.2-.55-1.2-1.2 0-.65.55-1.2 1.2-1.2.65 0 1.2.55 1.2 1.2 0 .65-.55 1.2-1.2 1.2z",
    }),
    viewBox: "0 0 14 16",
    width: 14,
  },
  globe: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M7 1C3.14 1 0 4.14 0 8s3.14 7 7 7c.48 0 .94-.05 1.38-.14-.17-.08-.2-.73-.02-1.09.19-.41.81-1.45.2-1.8-.61-.35-.44-.5-.81-.91-.37-.41-.22-.47-.25-.58-.08-.34.36-.89.39-.94.02-.06.02-.27 0-.33 0-.08-.27-.22-.34-.23-.06 0-.11.11-.2.13-.09.02-.5-.25-.59-.33-.09-.08-.14-.23-.27-.34-.13-.13-.14-.03-.33-.11s-.8-.31-1.28-.48c-.48-.19-.52-.47-.52-.66-.02-.2-.3-.47-.42-.67-.14-.2-.16-.47-.2-.41-.04.06.25.78.2.81-.05.02-.16-.2-.3-.38-.14-.19.14-.09-.3-.95s.14-1.3.17-1.75c.03-.45.38.17.19-.13-.19-.3 0-.89-.14-1.11-.13-.22-.88.25-.88.25.02-.22.69-.58 1.16-.92.47-.34.78-.06 1.16.05.39.13.41.09.28-.05-.13-.13.06-.17.36-.13.28.05.38.41.83.36.47-.03.05.09.11.22s-.06.11-.38.3c-.3.2.02.22.55.61s.38-.25.31-.55c-.07-.3.39-.06.39-.06.33.22.27.02.5.08.23.06.91.64.91.64-.83.44-.31.48-.17.59.14.11-.28.3-.28.3-.17-.17-.19.02-.3.08-.11.06-.02.22-.02.22-.56.09-.44.69-.42.83 0 .14-.38.36-.47.58-.09.2.25.64.06.66-.19.03-.34-.66-1.31-.41-.3.08-.94.41-.59 1.08.36.69.92-.19 1.11-.09.19.1-.06.53-.02.55.04.02.53.02.56.61.03.59.77.53.92.55.17 0 .7-.44.77-.45.06-.03.38-.28 1.03.09.66.36.98.31 1.2.47.22.16.08.47.28.58.2.11 1.06-.03 1.28.31.22.34-.88 2.09-1.22 2.28-.34.19-.48.64-.84.92s-.81.64-1.27.91c-.41.23-.47.66-.66.8 3.14-.7 5.48-3.5 5.48-6.84 0-3.86-3.14-7-7-7L7 1zm1.64 6.56c-.09.03-.28.22-.78-.08-.48-.3-.81-.23-.86-.28 0 0-.05-.11.17-.14.44-.05.98.41 1.11.41.13 0 .19-.13.41-.05.22.08.05.13-.05.14zM6.34 1.7c-.05-.03.03-.08.09-.14.03-.03.02-.11.05-.14.11-.11.61-.25.52.03-.11.27-.58.3-.66.25zm1.23.89c-.19-.02-.58-.05-.52-.14.3-.28-.09-.38-.34-.38-.25-.02-.34-.16-.22-.19.12-.03.61.02.7.08.08.06.52.25.55.38.02.13 0 .25-.17.25zm1.47-.05c-.14.09-.83-.41-.95-.52-.56-.48-.89-.31-1-.41-.11-.1-.08-.19.11-.34.19-.15.69.06 1 .09.3.03.66.27.66.55.02.25.33.5.19.63h-.01z",
    }),
    viewBox: "0 0 14 16",
    width: 14,
  },
  "globe-wireframe": {
    height: 20,
    pathSolid: React.createElement("path", {
      fill: "#fff",
      fillRule: "evenodd",
      d: "M10 19.646a9.196 9.196 0 01-3.688-.749 9.637 9.637 0 01-3.024-2.039 9.635 9.635 0 01-2.039-3.024A9.197 9.197 0 01.5 10.146c0-1.312.25-2.544.749-3.695a9.66 9.66 0 012.04-3.016 9.635 9.635 0 013.023-2.04A9.197 9.197 0 0110 .647a9.18 9.18 0 013.695.75 9.66 9.66 0 013.017 2.039c.86.86 1.54 1.866 2.039 3.016.5 1.15.749 2.383.749 3.696 0 1.302-.25 2.531-.75 3.687a9.633 9.633 0 01-2.038 3.024 9.662 9.662 0 01-3.017 2.04 9.18 9.18 0 01-3.695.748zm0-1.52c.51-.678.94-1.36 1.289-2.049.348-.688.632-1.44.851-2.258H7.86c.232.843.519 1.608.861 2.297.342.688.769 1.358 1.279 2.01zm-1.937-.276a10.841 10.841 0 01-1.032-1.876c-.305-.7-.543-1.419-.712-2.155H2.927a7.84 7.84 0 002.125 2.618 7.139 7.139 0 003.011 1.413zm3.873 0a7.14 7.14 0 003.012-1.413 7.84 7.84 0 002.125-2.618h-3.392a15.721 15.721 0 01-.76 2.165c-.305.7-.633 1.323-.984 1.866zm-9.638-5.53h3.717a12.447 12.447 0 01-.136-1.094 13.71 13.71 0 010-2.16c.028-.356.074-.72.136-1.093H2.298a8.03 8.03 0 00-.221 3.287c.051.367.125.72.221 1.06zm5.217 0h4.97a12.596 12.596 0 00.179-2.174 12.593 12.593 0 00-.179-2.173h-4.97a12.553 12.553 0 00-.178 2.173 12.556 12.556 0 00.179 2.173zm6.47 0h3.717a8.04 8.04 0 000-4.346h-3.717c.062.371.108.736.136 1.093a13.71 13.71 0 010 2.16c-.028.356-.074.72-.136 1.093zm-.304-5.847h3.392a7.821 7.821 0 00-2.11-2.617 7.365 7.365 0 00-3.026-1.423 12.44 12.44 0 011.023 1.91c.298.69.539 1.4.72 2.13zm-5.821 0h4.28a12.856 12.856 0 00-.876-2.31A11.675 11.675 0 0010 2.168c-.491.625-.913 1.29-1.264 1.995a12.85 12.85 0 00-.876 2.31zm-4.933 0h3.392c.182-.73.423-1.44.721-2.13a12.43 12.43 0 011.023-1.91c-1.15.255-2.161.731-3.03 1.428a7.925 7.925 0 00-2.106 2.612z",
    }),
    viewBox: "0 0 20 20",
    width: 20,
  },
  gmail: {
    height: 19,
    pathSolid: (
      <>
        <g clip-path="url(#clip0_9060_20126)">
          <path
            d="M2.13636 18.5H5.95455V9.22726L0.5 5.13635V16.8636C0.5 17.7691 1.23364 18.5 2.13636 18.5Z"
            fill="#4285F4"
          />
          <path
            d="M19.0454 18.5H22.8636C23.769 18.5 24.5 17.7664 24.5 16.8636V5.13635L19.0454 9.22726"
            fill="#34A853"
          />
          <path
            d="M19.0454 2.13637V9.22728L24.5 5.13637V2.95455C24.5 0.930912 22.19 -0.222724 20.5727 0.990912"
            fill="#FBBC04"
          />
          <path
            d="M5.95459 9.22726V2.13635L12.5 7.04544L19.0455 2.13635V9.22726L12.5 14.1364"
            fill="#EA4335"
          />
          <path
            d="M0.5 2.95455V5.13637L5.95455 9.22728V2.13637L4.42727 0.990912C2.80727 -0.222724 0.5 0.930912 0.5 2.95455Z"
            fill="#C5221F"
          />
        </g>
        <defs>
          <clipPath id="clip0_9060_20126">
            <rect
              width="24"
              height="18"
              fill="white"
              transform="translate(0.5 0.5)"
            />
          </clipPath>
        </defs>
      </>
    ),
    viewBox: "0 0 25 18",
    width: 25,
  },
  google: {
    height: 20,
    pathSolid: (
      <>
        <path
          d="M4.98918 11.878L4.36263 14.217L2.07258 14.2654C1.3882 12.996 1 11.5437 1 10.0003C1 8.50792 1.36295 7.10055 2.00631 5.86133H2.00681L4.04559 6.23511L4.9387 8.26166C4.75177 8.80661 4.64989 9.39161 4.64989 10.0003C4.64996 10.661 4.76963 11.294 4.98918 11.878Z"
          fill="#FBBB00"
        />
        <path
          d="M18.8428 8.31836C18.9461 8.86279 19 9.42504 19 9.99967C19 10.644 18.9323 11.2725 18.8032 11.8788C18.3651 13.9419 17.2203 15.7434 15.6344 17.0182L15.6339 17.0177L13.066 16.8867L12.7025 14.6179C13.7548 14.0007 14.5772 13.035 15.0104 11.8788H10.1979V8.31836H15.0806H18.8428Z"
          fill="#518EF8"
        />
        <path
          d="M15.6339 17.0173L15.6344 17.0178C14.0921 18.2575 12.1328 18.9993 10 18.9993C6.57263 18.9993 3.59275 17.0836 2.07263 14.2644L4.98923 11.877C5.74927 13.9054 7.70603 15.3494 10 15.3494C10.9861 15.3494 11.9098 15.0828 12.7025 14.6175L15.6339 17.0173Z"
          fill="#28B446"
        />
        <path
          d="M15.7446 3.07197L12.829 5.45894C12.0086 4.94615 11.0388 4.64992 9.99991 4.64992C7.65397 4.64992 5.66061 6.16013 4.93864 8.26131L2.00672 5.86098H2.00623C3.50409 2.97307 6.52155 1 9.99991 1C12.1836 1 14.1859 1.77787 15.7446 3.07197Z"
          fill="#F14336"
        />
      </>
    ),
    viewBox: "0 0 20 20",
    width: 20,
  },
  "google-doc": {
    height: 18,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M11 0H3.8C2.81 0 2.009 0.81 2.009 1.8L2 16.2C2 17.19 2.801 18 3.791 18H14.6C15.59 18 16.4 17.19 16.4 16.2V5.4L11 0ZM12.8 14.4H5.6V12.6H12.8V14.4ZM12.8 10.8H5.6V9H12.8V10.8ZM10.1 6.3V1.35L15.05 6.3H10.1Z",
    }),
    viewBox: "0 0 16 18",
    width: 16,
  },
  grabber: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M8 4v1H0V4h8zM0 8h8V7H0v1zm0 3h8v-1H0v1z",
    }),
    viewBox: "0 0 8 16",
    width: 8,
  },
  graph: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M16 14v1H0V0h1v14h15zM5 13H3V8h2v5zm4 0H7V3h2v10zm4 0h-2V6h2v7z",
    }),
    viewBox: "0 0 16 16",
    width: 16,
  },
  heart: {
    height: 16,
    pathOutline: (props: IconPathProps) => {
      return React.createElement("path", {
        fillRule: "evenodd",
        d: "M24.965 10.3581L24.9684 10.394L24.9745 10.4295C24.981 10.4678 25.094 11.1704 24.786 12.3976L24.7859 12.3979C24.3431 14.1638 23.316 15.7912 21.787 17.0964L13.9337 23.6897L6.21631 17.0988L6.21573 17.0983C4.68524 15.7933 3.65706 14.1646 3.21403 12.3978L3.21398 12.3977C2.90572 11.1694 3.01929 10.4675 3.02545 10.4318L3.03183 10.3949L3.03543 10.3575C3.34111 7.18709 5.6876 5 8.53477 5C10.4257 5 12.1036 5.94827 13.0842 7.52735L13.9244 8.88044L14.778 7.53572C15.7641 5.98216 17.5267 5.00043 19.4652 5.00043C22.3127 5.00043 24.6589 7.18749 24.965 10.3581Z",
        stroke: props.fill,
        strokeWidth: "2",
        fill: "transparent",
      });
    },
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M24.965 10.3581L24.9684 10.394L24.9745 10.4295C24.981 10.4678 25.094 11.1704 24.786 12.3976L24.7859 12.3979C24.3431 14.1638 23.316 15.7912 21.787 17.0964L13.9337 23.6897L6.21631 17.0988L6.21573 17.0983C4.68524 15.7933 3.65706 14.1646 3.21403 12.3978L3.21398 12.3977C2.90572 11.1694 3.01929 10.4675 3.02545 10.4318L3.03183 10.3949L3.03543 10.3575C3.34111 7.18709 5.6876 5 8.53477 5C10.4257 5 12.1036 5.94827 13.0842 7.52735L13.9244 8.88044L14.778 7.53572C15.7641 5.98216 17.5267 5.00043 19.4652 5.00043C22.3127 5.00043 24.6589 7.18749 24.965 10.3581Z",
      stroke: "transparent",
      strokeWidth: "2",
    }),
    viewBox: "0 0 28 28",
    width: 16,
  },
  history: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M8 13H6V6h5v2H8v5zM7 1C4.81 1 2.87 2.02 1.59 3.59L0 2v4h4L2.5 4.5C3.55 3.17 5.17 2.3 7 2.3c3.14 0 5.7 2.56 5.7 5.7s-2.56 5.7-5.7 5.7A5.71 5.71 0 0 1 1.3 8c0-.34.03-.67.09-1H.08C.03 7.33 0 7.66 0 8c0 3.86 3.14 7 7 7s7-3.14 7-7-3.14-7-7-7z",
    }),
    viewBox: "0 0 14 16",
    width: 14,
  },
  image: {
    height: 16,
    width: 16,
    viewBox: "0 0 16 16",
    pathSolid: () => {
      const maskId1 = uuid4();
      const maskId2 = uuid4();
      return (
        <>
          <mask id={maskId1} fill="none">
            <path d="M14 12.0002L2 12V12.8889C2 13.5025 2.49746 14 3.11111 14L12.8889 14C13.5025 14 14 13.5025 14 12.8889V12.0002Z" />
          </mask>
          <path d="M14 12.0002L2 12V12.8889C2 13.5025 2.49746 14 3.11111 14L12.8889 14C13.5025 14 14 13.5025 14 12.8889V12.0002Z" />
          <path
            mask={`url(#${maskId1})`}
            d="M14 12.0002H14.9524V11.0478L14 11.0478L14 12.0002ZM2 12L2.00001 11.0476L1.04762 11.0476V12H2ZM3.11111 14L3.11111 14.9524L3.11111 14.9524L3.11111 14ZM12.8889 14L12.8889 13.0476L12.8889 13.0476L12.8889 14ZM14 11.0478L2.00001 11.0476L1.99999 12.9524L14 12.9525L14 11.0478ZM1.04762 12V12.8889H2.95238V12H1.04762ZM1.04762 12.8889C1.04762 14.0285 1.97148 14.9524 3.11111 14.9524V13.0476C3.02345 13.0476 2.95238 12.9766 2.95238 12.8889H1.04762ZM3.11111 14.9524L12.8889 14.9524L12.8889 13.0476L3.11111 13.0476L3.11111 14.9524ZM12.8889 14.9524C14.0285 14.9524 14.9524 14.0285 14.9524 12.8889H13.0476C13.0476 12.9766 12.9766 13.0476 12.8889 13.0476V14.9524ZM14.9524 12.8889V12.0002H13.0476V12.8889H14.9524Z"
          />
          <mask id={maskId2} fill="none">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M6.69946 8.57376C6.84852 8.28918 7.23084 8.23084 7.458 8.458L8 9L9.51509 5.96982C9.70051 5.59898 10.2384 5.62581 10.386 6.01326L12.2857 11H14V3.11111C14 2.49746 13.5025 2 12.8889 2H3.11111C2.49746 2 2 2.49746 2 3.11111V11H5.42857L6.69946 8.57376ZM5.5 7C6.32843 7 7 6.32843 7 5.5C7 4.67157 6.32843 4 5.5 4C4.67157 4 4 4.67157 4 5.5C4 6.32843 4.67157 7 5.5 7Z"
            />
          </mask>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M6.69946 8.57376C6.84852 8.28918 7.23084 8.23084 7.458 8.458L8 9L9.51509 5.96982C9.70051 5.59898 10.2384 5.62581 10.386 6.01326L12.2857 11H14V3.11111C14 2.49746 13.5025 2 12.8889 2H3.11111C2.49746 2 2 2.49746 2 3.11111V11H5.42857L6.69946 8.57376ZM5.5 7C6.32843 7 7 6.32843 7 5.5C7 4.67157 6.32843 4 5.5 4C4.67157 4 4 4.67157 4 5.5C4 6.32843 4.67157 7 5.5 7Z"
          />
          <path
            mask={`url(#${maskId2})`}
            d="M6.69946 8.57376L7.58529 9.03777L7.58529 9.03777L6.69946 8.57376ZM8 9L7.29289 9.70711L8.27395 10.6882L8.89443 9.44721L8 9ZM9.51509 5.96982L10.4095 6.41703L10.4095 6.41703L9.51509 5.96982ZM10.386 6.01326L11.3205 5.65726L11.3205 5.65726L10.386 6.01326ZM12.2857 11L11.3512 11.356L11.5966 12H12.2857V11ZM14 11V12H15V11H14ZM2 11H1V12H2V11ZM5.42857 11V12H6.03364L6.3144 11.464L5.42857 11ZM8.16511 7.75089C7.4609 7.04669 6.27573 7.22756 5.81363 8.10976L7.58529 9.03777C7.42132 9.35081 7.00077 9.41499 6.75089 9.16511L8.16511 7.75089ZM8.70711 8.29289L8.16511 7.75089L6.75089 9.16511L7.29289 9.70711L8.70711 8.29289ZM8.62066 5.52261L7.10557 8.55279L8.89443 9.44721L10.4095 6.41703L8.62066 5.52261ZM11.3205 5.65726C10.8629 4.45617 9.19547 4.373 8.62066 5.52261L10.4095 6.41703C10.2056 6.82496 9.61387 6.79544 9.45151 6.36925L11.3205 5.65726ZM13.2202 10.644L11.3205 5.65726L9.45151 6.36925L11.3512 11.356L13.2202 10.644ZM14 10H12.2857V12H14V10ZM13 3.11111V11H15V3.11111H13ZM12.8889 3C12.9502 3 13 3.04975 13 3.11111H15C15 1.94518 14.0548 1 12.8889 1V3ZM3.11111 3H12.8889V1H3.11111V3ZM3 3.11111C3 3.04974 3.04975 3 3.11111 3V1C1.94517 1 1 1.94518 1 3.11111H3ZM3 11V3.11111H1V11H3ZM5.42857 10H2V12H5.42857V10ZM5.81363 8.10975L4.54274 10.536L6.3144 11.464L7.58529 9.03777L5.81363 8.10975ZM6 5.5C6 5.77614 5.77614 6 5.5 6V8C6.88071 8 8 6.88071 8 5.5H6ZM5.5 5C5.77614 5 6 5.22386 6 5.5H8C8 4.11929 6.88071 3 5.5 3V5ZM5 5.5C5 5.22386 5.22386 5 5.5 5V3C4.11929 3 3 4.11929 3 5.5H5ZM5.5 6C5.22386 6 5 5.77614 5 5.5H3C3 6.88071 4.11929 8 5.5 8V6Z"
          />
        </>
      );
    },
  },
  inbox: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M14 9l-1.13-7.14c-.08-.48-.5-.86-1-.86H2.13c-.5 0-.92.38-1 .86L0 9v5c0 .55.45 1 1 1h12c.55 0 1-.45 1-1V9zm-3.28.55l-.44.89c-.17.34-.52.56-.91.56H4.61c-.38 0-.72-.22-.89-.55l-.44-.91c-.17-.33-.52-.55-.89-.55H1l1-7h10l1 7h-1.38c-.39 0-.73.22-.91.55l.01.01z",
    }),
    viewBox: "0 0 14 16",
    width: 14,
  },
  info: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M6.3 5.69a.942.942 0 0 1-.28-.7c0-.28.09-.52.28-.7.19-.18.42-.28.7-.28.28 0 .52.09.7.28.18.19.28.42.28.7 0 .28-.09.52-.28.7a1 1 0 0 1-.7.3c-.28 0-.52-.11-.7-.3zM8 7.99c-.02-.25-.11-.48-.31-.69-.2-.19-.42-.3-.69-.31H6c-.27.02-.48.13-.69.31-.2.2-.3.44-.31.69h1v3c.02.27.11.5.31.69.2.2.42.31.69.31h1c.27 0 .48-.11.69-.31.2-.19.3-.42.31-.69H8V7.98v.01zM7 2.3c-3.14 0-5.7 2.54-5.7 5.68 0 3.14 2.56 5.7 5.7 5.7s5.7-2.55 5.7-5.7c0-3.15-2.56-5.69-5.7-5.69v.01zM7 .98c3.86 0 7 3.14 7 7s-3.14 7-7 7-7-3.12-7-7 3.14-7 7-7z",
    }),
    viewBox: "0 0 14 16",
    width: 14,
  },
  instagram: {
    height: 36,
    width: 36,
    viewBox: "-7 -7 36 36",
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M15.125 0H6.875C3.07863 0 0 3.07863 0 6.875V15.125C0 18.9214 3.07863 22 6.875 22H15.125C18.9214 22 22 18.9214 22 15.125V6.875C22 3.07863 18.9214 0 15.125 0ZM20 15.125C20 17.7787 17.7787 20 15.125 20H6.875C4.22125 20 2 17.7787 2 15.125V6.875C2 4.22125 4.22125 2 6.875 2H15.125C17.7787 2 20 4.22125 20 6.875V15.125ZM11 5.5C7.96263 5.5 5.5 7.96263 5.5 11C5.5 14.0374 7.96263 16.5 11 16.5C14.0374 16.5 16.5 14.0374 16.5 11C16.5 7.96263 14.0374 5.5 11 5.5ZM11 14.4375C9.10525 14.4375 7.5625 12.8947 7.5625 11C7.5625 9.10388 9.10525 7.5625 11 7.5625C12.8947 7.5625 14.4375 9.10388 14.4375 11C14.4375 12.8947 12.8947 14.4375 11 14.4375ZM18 5C18 5.55229 17.5523 6 17 6C16.4477 6 16 5.55229 16 5C16 4.44772 16.4477 4 17 4C17.5523 4 18 4.44772 18 5Z",
    }),
  },
  interactive: {
    height: 16,
    width: 16,
    viewBox: "0 0 16 16",
    pathSolid: (
      <path d="M7.00017 2C7.00017 1.43474 7.42669 1 8.00013 1C8.57357 1 9.00017 1.35399 9.00017 2V6L12.2818 7.63158C13.0191 7.87383 13.0913 8.75215 12.9275 9.47891L12.0001 15H7.00017L3.8369 9C3.50922 8.51549 3.26284 7.5 4.00012 7.5C4.7374 7.5 5.52557 7.81971 7.00013 10L7.00017 2Z" />
    ),
  },
  "issue-closed": {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M7 10h2v2H7v-2zm2-6H7v5h2V4zm1.5 1.5l-1 1L12 9l4-4.5-1-1L12 7l-1.5-1.5zM8 13.7A5.71 5.71 0 0 1 2.3 8c0-3.14 2.56-5.7 5.7-5.7 1.83 0 3.45.88 4.5 2.2l.92-.92A6.947 6.947 0 0 0 8 1C4.14 1 1 4.14 1 8s3.14 7 7 7 7-3.14 7-7l-1.52 1.52c-.66 2.41-2.86 4.19-5.48 4.19v-.01z",
    }),
    viewBox: "0 0 16 16",
    width: 16,
  },
  "issue-opened": {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M7 2.3c3.14 0 5.7 2.56 5.7 5.7s-2.56 5.7-5.7 5.7A5.71 5.71 0 0 1 1.3 8c0-3.14 2.56-5.7 5.7-5.7zM7 1C3.14 1 0 4.14 0 8s3.14 7 7 7 7-3.14 7-7-3.14-7-7-7zm1 3H6v5h2V4zm0 6H6v2h2v-2z",
    }),
    viewBox: "0 0 14 16",
    width: 14,
  },
  "issue-reopened": {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M8 9H6V4h2v5zm-2 3h2v-2H6v2zm6.33-2H10l1.5 1.5c-1.05 1.33-2.67 2.2-4.5 2.2A5.71 5.71 0 0 1 1.3 8c0-.34.03-.67.09-1H.08C.03 7.33 0 7.66 0 8c0 3.86 3.14 7 7 7 2.19 0 4.13-1.02 5.41-2.59L14 14v-4h-1.67zM1.67 6H4L2.5 4.5C3.55 3.17 5.17 2.3 7 2.3c3.14 0 5.7 2.56 5.7 5.7 0 .34-.03.67-.09 1h1.31c.05-.33.08-.66.08-1 0-3.86-3.14-7-7-7-2.19 0-4.13 1.02-5.41 2.59L0 2v4h1.67z",
    }),
    viewBox: "0 0 14 16",
    width: 14,
  },
  italic: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M2.81 5h1.98L3 14H1l1.81-9zm.36-2.7c0-.7.58-1.3 1.33-1.3.56 0 1.13.38 1.13 1.03 0 .75-.59 1.3-1.33 1.3-.58 0-1.13-.38-1.13-1.03z",
    }),
    viewBox: "0 0 6 16",
    width: 6,
  },
  jobs: {
    viewBox: "0 0 32 32",
    width: 32,
    height: 32,
    pathSolid: () => {
      const maskId = uuid4();
      return (
        <>
          <mask
            id={maskId}
            style={{ maskType: "alpha" }}
            maskUnits="userSpaceOnUse"
            x="0"
            y="0"
            width="32"
            height="32"
          >
            <rect width="32" height="32" fill="#ECF8F6" />
          </mask>
          <g mask={`url(#${maskId})`}>
            <path
              d="M5.33329 28C4.59996 28 3.97218 27.7388 3.44996 27.2166C2.92774 26.6944 2.66663 26.0666 2.66663 25.3333V10.6666C2.66663 9.93329 2.92774 9.30552 3.44996 8.78329C3.97218 8.26107 4.59996 7.99996 5.33329 7.99996H10.6666V5.33329C10.6666 4.59996 10.9277 3.97218 11.45 3.44996C11.9722 2.92774 12.6 2.66663 13.3333 2.66663H18.6666C19.4 2.66663 20.0277 2.92774 20.55 3.44996C21.0722 3.97218 21.3333 4.59996 21.3333 5.33329V7.99996H26.6666C27.4 7.99996 28.0277 8.26107 28.55 8.78329C29.0722 9.30552 29.3333 9.93329 29.3333 10.6666V25.3333C29.3333 26.0666 29.0722 26.6944 28.55 27.2166C28.0277 27.7388 27.4 28 26.6666 28H5.33329ZM13.3333 7.99996H18.6666V5.33329H13.3333V7.99996ZM26.6666 20H20V21.3333C20 21.7111 19.8722 22.0277 19.6166 22.2833C19.3611 22.5388 19.0444 22.6666 18.6666 22.6666H13.3333C12.9555 22.6666 12.6388 22.5388 12.3833 22.2833C12.1277 22.0277 12 21.7111 12 21.3333V20H5.33329V25.3333H26.6666V20ZM14.6666 20H17.3333V17.3333H14.6666V20ZM5.33329 17.3333H12V16C12 15.6222 12.1277 15.3055 12.3833 15.05C12.6388 14.7944 12.9555 14.6666 13.3333 14.6666H18.6666C19.0444 14.6666 19.3611 14.7944 19.6166 15.05C19.8722 15.3055 20 15.6222 20 16V17.3333H26.6666V10.6666H5.33329V17.3333Z"
              fill="#5AC5B8"
            />
          </g>
        </>
      );
    },
  },
  "kebab-horizontal": {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M1.5 9a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zm5 0a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zM13 7.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z",
    }),
    viewBox: "0 0 13 16",
    width: 13,
  },
  "kebab-vertical": {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M0 2.5a1.5 1.5 0 1 0 3 0 1.5 1.5 0 0 0-3 0zm0 5a1.5 1.5 0 1 0 3 0 1.5 1.5 0 0 0-3 0zM1.5 14a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3z",
    }),
    viewBox: "0 0 3 16",
    width: 3,
  },
  key: {
    viewBox: "0 0 14 16",
    width: 14,
    height: 16,
    pathSolid: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.2922 9.33751C7.64345 10.5864 6.35402 11.4374 4.86925 11.4374C2.73232 11.4374 1 9.67459 1 7.50002C1 5.32546 2.73232 3.56262 4.86925 3.56262C6.55395 3.56262 7.98718 4.65829 8.51834 6.18758H13.4825C13.5685 6.18758 13.6545 6.18759 13.7405 6.27509L14.8583 7.41256C15.0302 7.58756 15.0303 7.85005 14.9443 8.11255L13.8265 9.25001C13.7405 9.3375 13.6545 9.33751 13.5685 9.33751H12.5367L11.8489 10.0375C11.7629 10.2125 11.5049 10.2125 11.333 10.0375L10.6451 9.33751H10.2152L9.52733 10.0375C9.44135 10.2125 9.18339 10.2125 9.01143 10.0375L8.32355 9.33751H8.2922ZM4.43998 7.06256C4.43998 7.54579 4.05502 7.93753 3.58015 7.93753C3.10528 7.93753 2.72032 7.54579 2.72032 7.06256C2.72032 6.57932 3.10528 6.18758 3.58015 6.18758C4.05502 6.18758 4.43998 6.57932 4.43998 7.06256Z"
      />
    ),
  },
  language: {
    height: 16,
    width: 16,
    viewBox: "0 0 16 16",
    pathSolid: (
      <path d="M8 9.11699L7.02551 8.16L7.04301 8.1425C8.05801 7.01083 8.78134 5.71 9.20717 4.33333H10.9163V3.16667H6.83301V2H5.66634V3.16667H1.58301V4.3275H8.09884C7.70801 5.45333 7.08967 6.52083 6.24967 7.45417C5.70717 6.85333 5.25801 6.19417 4.90217 5.5H3.73551C4.16134 6.45083 4.74467 7.34917 5.47384 8.16L2.50467 11.0883L3.33301 11.9167L6.24967 9L7.55667 10.307L8 9.11699ZM11.7913 6.66667H10.6247L7.99967 13.6667H9.16634L9.81967 11.9167H12.5905L13.2497 13.6667H14.4163L11.7913 6.66667ZM10.263 10.75L11.208 8.22417L12.153 10.75H10.263Z" />
    ),
  },
  launch: {
    height: 18,
    width: 19,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M7.878 4.547C10.462 1.746 14.544-.06 18.124.002a.377.377 0 01.373.36c.084 3.45-1.803 7.389-4.708 9.888a9.182 9.182 0 01-2.805 6.39.44.44 0 01-.296.119.364.364 0 01-.109-.017c-.137-.044-.25-.146-.27-.284l-.422-2.843-.407.351a.388.388 0 01-.255.094.388.388 0 01-.27-.107l-4.91-4.74a.36.36 0 01-.016-.507l.364-.393-2.945-.407a.38.38 0 01-.308-.25.36.36 0 01.094-.378c1.77-1.708 4.175-2.686 6.644-2.73zm5.374 2.36c.49 0 .98-.18 1.351-.54a1.8 1.8 0 000-2.607 1.961 1.961 0 00-2.701 0 1.802 1.802 0 000 2.607c.372.36.86.54 1.35.54zM.515 17.5c.114-.424 1.14-4.175 1.887-5.006.842-.934 2.21-.935 3.053 0 .842.935.842 2.457 0 3.392-.747.83-4.123 1.97-4.505 2.098a.332.332 0 01-.348-.098.42.42 0 01-.087-.386z",
    }),
    viewBox: "0 0 19 18",
  },
  law: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M7 4c-.83 0-1.5-.67-1.5-1.5S6.17 1 7 1s1.5.67 1.5 1.5S7.83 4 7 4zm7 6c0 1.11-.89 2-2 2h-1c-1.11 0-2-.89-2-2l2-4h-1c-.55 0-1-.45-1-1H8v8c.42 0 1 .45 1 1h1c.42 0 1 .45 1 1H3c0-.55.58-1 1-1h1c0-.55.58-1 1-1h.03L6 5H5c0 .55-.45 1-1 1H3l2 4c0 1.11-.89 2-2 2H2c-1.11 0-2-.89-2-2l2-4H1V5h3c0-.55.45-1 1-1h4c.55 0 1 .45 1 1h3v1h-1l2 4zM2.5 7L1 10h3L2.5 7zM13 10l-1.5-3-1.5 3h3z",
    }),
    viewBox: "0 0 14 16",
    width: 14,
  },
  learner: {
    height: 18,
    pathSolid: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M1.29358 2.90829L2.6881 3.12848V5.18356L1.73396 9.8075C1.66056 10.0277 1.88075 10.1745 2.02754 10.1745H4.00923C4.22942 10.1745 4.37621 9.9543 4.30281 9.8075L3.34868 5.18356V3.27527C3.84233 3.35532 4.15894 3.40627 4.37961 3.44178C4.64024 3.48372 4.76706 3.50413 4.89372 3.52552C5.01377 3.5458 5.13367 3.56696 5.36721 3.60817C5.82634 3.68919 6.72466 3.84772 8.92674 4.22942H9.00014L16.5599 2.90829C16.7067 2.90829 16.8535 2.7615 16.8535 2.61471C16.8535 2.46792 16.7067 2.32113 16.5599 2.32113C11.2851 1.43329 10.7299 1.32585 10.108 1.20552C9.85594 1.15675 9.59291 1.10585 9.00014 1H8.85337L1.29358 2.32113C1.14679 2.32113 1 2.46792 1 2.61471C1 2.7615 1.14679 2.90829 1.29358 2.90829ZM8.92661 4.88981C8.85321 4.88981 5.25682 4.30265 5.25682 4.30265C5.25682 4.52283 5.25682 4.66963 5.33022 4.81642C5.40361 4.88981 6.43117 5.91736 8.85323 5.91736C11.2753 5.91736 12.3762 4.88981 12.3762 4.81642C12.4837 4.70896 12.4731 4.6015 12.4598 4.46524L12.4598 4.46522C12.455 4.41614 12.4498 4.36332 12.4496 4.30542C12.4554 4.3036 12.4555 4.30265 12.4496 4.30265L12.4496 4.30542C12.3126 4.34897 8.99705 4.88981 8.92661 4.88981ZM13.1467 11.5325C12.9816 11.4774 12.8164 11.4224 14.9449 12.0095C16.1193 12.4499 16.8532 13.5508 17 14.7986V16.4133C17 16.5601 16.7798 16.7069 16.633 16.7069H1.44009C1.21991 16.7069 1.07311 16.5601 1.07311 16.4133V14.7986C1.07311 13.5508 1.80708 12.4499 2.98141 12.0095C3.1142 11.9691 3.2379 11.9314 3.35395 11.8961C4.71324 11.4819 5.02249 11.3877 6.5778 10.982C5.91724 10.3948 5.40346 9.66086 5.33007 8.78011C4.88969 8.63332 4.59611 8.19295 4.59611 7.53238C4.59611 6.94522 4.88969 6.43144 5.33007 6.28465V5.55069C5.39087 5.5811 5.46426 5.62409 5.55025 5.67446C5.67186 5.7457 5.81865 5.83169 5.99062 5.91767V6.57824C5.99062 6.72503 5.84384 6.87182 5.69704 6.87182C5.33007 6.87182 5.25667 7.31219 5.25667 7.53238C5.25667 7.75257 5.40346 8.19295 5.69704 8.19295C5.84384 8.19295 5.99062 8.26634 5.99062 8.48653C5.99062 10.248 8.11911 11.4958 8.99986 11.4958C9.88061 11.4958 11.9357 10.1746 11.9357 8.48653C11.9357 8.33974 12.1559 8.19295 12.3027 8.19295C12.6697 8.19295 12.7431 7.75257 12.7431 7.53238C12.7431 7.31219 12.5963 6.87182 12.3027 6.87182C12.0825 6.87182 11.9357 6.72503 11.9357 6.57824V5.99107C12.0144 5.95174 12.0878 5.91768 12.1559 5.88606C12.342 5.79968 12.4888 5.73155 12.5963 5.62409V6.35805C13.0366 6.50484 13.3302 7.01861 13.3302 7.60578C13.3302 8.19295 13.0366 8.70672 12.5963 8.85351C12.4495 9.73426 12.0091 10.4682 11.3485 11.0554C13.477 11.6426 13.3119 11.5875 13.1467 11.5325ZM6.06402 11.7893L8.92646 14.6518L11.7889 11.7893C11.4953 11.7159 11.0549 11.6426 10.5412 11.5692C9.95401 11.9361 9.29344 12.1563 8.92646 12.1563C8.48608 12.1563 7.89893 11.9361 7.31176 11.5692C6.79799 11.6426 6.35761 11.7159 6.06402 11.7893Z"
        fill="#003E6B"
      />
    ),
    viewBox: "0 0 18 18",
    width: 18,
  },
  "learning-objective": {
    height: 28,
    pathSolid: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M14.0013 25.6666C13.4957 25.6666 13.0388 25.5451 12.6305 25.302C12.2221 25.0589 11.9013 24.7333 11.668 24.3249C11.0263 24.3249 10.477 24.0964 10.0201 23.6395C9.56311 23.1826 9.33464 22.6333 9.33464 21.9916V17.8499C8.18741 17.0916 7.26866 16.0902 6.57839 14.8458C5.88811 13.6013 5.54297 12.2499 5.54297 10.7916C5.54297 8.43881 6.3645 6.44089 8.00755 4.79784C9.65061 3.15478 11.6485 2.33325 14.0013 2.33325C16.3541 2.33325 18.352 3.15478 19.9951 4.79784C21.6381 6.44089 22.4596 8.43881 22.4596 10.7916C22.4596 12.2888 22.1145 13.6499 21.4242 14.8749C20.7339 16.0999 19.8152 17.0916 18.668 17.8499V21.9916C18.668 22.6333 18.4395 23.1826 17.9826 23.6395C17.5256 24.0964 16.9763 24.3249 16.3346 24.3249C16.1013 24.7333 15.7805 25.0589 15.3721 25.302C14.9638 25.5451 14.5069 25.6666 14.0013 25.6666ZM11.668 21.9916H16.3346V20.9416H11.668V21.9916ZM11.668 19.7749H16.3346V18.6666H11.668V19.7749ZM11.4346 16.3333H13.1263V13.1833L10.5596 10.6166L11.7846 9.39159L14.0013 11.6083L16.218 9.39159L17.443 10.6166L14.8763 13.1833V16.3333H16.568C17.618 15.8277 18.4735 15.0839 19.1346 14.102C19.7957 13.1201 20.1263 12.0166 20.1263 10.7916C20.1263 9.08047 19.5332 7.63186 18.3471 6.44575C17.161 5.25964 15.7124 4.66659 14.0013 4.66659C12.2902 4.66659 10.8416 5.25964 9.65547 6.44575C8.46936 7.63186 7.8763 9.08047 7.8763 10.7916C7.8763 12.0166 8.20686 13.1201 8.86797 14.102C9.52908 15.0839 10.3846 15.8277 11.4346 16.3333Z"
        fill="#333333"
      />
    ),
    viewBox: "0 0 28 28",
    width: 28,
  },
  left: {
    width: 10,
    height: 10,
    pathSolid: (
      <path d="M0 0H7.5C7.77614 0 8 0.223858 8 0.5C8 0.776142 7.77614 1 7.5 1H1V9H7.5C7.77614 9 8 9.22386 8 9.5C8 9.77614 7.77614 10 7.5 10H0V0ZM4.38101 4H9.5V5H4.38104L6.32009 6.61588C6.53223 6.79266 6.56089 7.10794 6.38411 7.32008C6.20733 7.53222 5.89205 7.56088 5.67991 7.3841L2.67991 4.8841C2.56591 4.7891 2.5 4.64838 2.5 4.49999C2.5 4.3516 2.56591 4.21087 2.67991 4.11588L5.67991 1.61588C5.89205 1.43909 6.20733 1.46776 6.38411 1.6799C6.56089 1.89203 6.53223 2.20732 6.32009 2.3841L4.38101 4Z" />
    ),
    viewBox: "0 0 10 10",
  },
  library: {
    viewBox: "0 0 22 22",
    width: 22,
    height: 22,
    pathOutline: (props: IconPathProps) => (
      <>
        <path
          d="M19 6.997v11.289c0 .857-.833 1.714-1.667 1.714h-11.5C4.833 20 4 19.314 4 18.286v-14.4C4 2.857 4.833 2 5.833 2h12.792c.167 0 .375.243.375.5s-.165.5-.332.5H6c-.583 0-1 .9-1 1.5C5 5.186 5.417 6 6 6h12c.552 0 1 .444 1 .997z"
          fill="none"
          stroke={props.fill}
        />
        <rect width="13" height="1" x="6" y="4" rx=".5" />
      </>
    ),
    pathSolid: (
      <>
        <path d="M19 6.997v11.289c0 .857-.833 1.714-1.667 1.714h-11.5C4.833 20 4 19.314 4 18.286v-14.4C4 2.857 4.833 2 5.833 2h12.792c.167 0 .375.243.375.5s-.165.5-.332.5H6c-.583 0-1 .9-1 1.5C5 5.186 5.417 6 6 6h12c.552 0 1 .444 1 .997z" />
        <rect width="13" height="1" x="6" y="4" rx=".5" />
      </>
    ),
  },
  "library-bold": {
    height: 18,
    width: 18,
    fill: "none",
    viewBox: "0 0 18 18",
    pathSolid: () => {
      const maskId1 = uuid4();
      return (
        <>
          <mask
            id={maskId1}
            style={{ maskType: "alpha" }}
            width="18"
            height="18"
            x="0"
            y="0"
            maskUnits="userSpaceOnUse"
          >
            <path fill="#D9D9D9" d="M0 0H18V18H0z"></path>
          </mask>
          <g mask={`url(#${maskId1})`}>
            <path
              fill="#003E6B"
              d="M5.625 16.5a2.53 2.53 0 01-1.856-.769A2.53 2.53 0 013 13.875v-9.75c0-.725.256-1.344.769-1.856A2.53 2.53 0 015.625 1.5H15v11.25c-.313 0-.578.11-.797.328a1.085 1.085 0 00-.328.797c0 .313.11.578.328.797.219.219.485.328.797.328v1.5H5.625zM6 11.25h1.5V3H6v8.25zM5.625 15h6.994a3.354 3.354 0 01-.178-.534 2.516 2.516 0 01-.066-.591c0-.2.019-.394.056-.581.038-.188.1-.369.188-.544H5.625c-.325 0-.594.11-.806.328a1.101 1.101 0 00-.319.797c0 .325.106.594.319.806.212.213.481.319.806.319z"
            ></path>
          </g>
        </>
      );
    },
  },
  license: {
    height: 16,
    width: 16,
    viewBox: "0 0 16 16",
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M7.8143 14.0742L3.87921 15.6483C3.50607 15.7975 3.11824 15.4679 3.20542 15.0756L4.37941 9.7926C4.37941 9.7926 5.5 11 8 11C10.5 11 11.6206 9.7926 11.6206 9.7926L12.7946 15.0756C12.8818 15.4679 12.4939 15.7975 12.1208 15.6483L8.1857 14.0742C8.06649 14.0266 7.93351 14.0266 7.8143 14.0742Z M8 10C10.7614 10 13 7.76142 13 5C13 2.23858 10.7614 0 8 0C5.23858 0 3 2.23858 3 5C3 7.76142 5.23858 10 8 10ZM8 7C9.10457 7 10 6.10457 10 5C10 3.89543 9.10457 3 8 3C6.89543 3 6 3.89543 6 5C6 6.10457 6.89543 7 8 7Z",
    }),
  },
  link: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M4 9h1v1H4c-1.5 0-3-1.69-3-3.5S2.55 3 4 3h4c1.45 0 3 1.69 3 3.5 0 1.41-.91 2.72-2 3.25V8.59c.58-.45 1-1.27 1-2.09C10 5.22 8.98 4 8 4H4c-.98 0-2 1.22-2 2.5S3 9 4 9zm9-3h-1v1h1c1 0 2 1.22 2 2.5S13.98 12 13 12H9c-.98 0-2-1.22-2-2.5 0-.83.42-1.64 1-2.09V6.25c-1.09.53-2 1.84-2 3.25C6 11.31 7.55 13 9 13h4c1.45 0 3-1.69 3-3.5S14.5 6 13 6z",
    }),
    viewBox: "0 0 16 16",
    width: 16,
  },
  "link-external": {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M11 10h1v3c0 .55-.45 1-1 1H1c-.55 0-1-.45-1-1V3c0-.55.45-1 1-1h3v1H1v10h10v-3zM6 2l2.25 2.25L5 7.5 6.5 9l3.25-3.25L12 8V2H6z",
    }),
    viewBox: "0 0 12 16",
    width: 12,
  },
  "link-internal": {
    height: 22,
    pathSolid: React.createElement("path", {
      fillRule: "none",
      d: "M20.25 2.16113C20.25 1.47078 19.6904 0.911134 19 0.911134L7.75 0.911132C7.05965 0.911132 6.5 1.47078 6.5 2.16113C6.5 2.85149 7.05965 3.41113 7.75 3.41113L17.75 3.41113L17.75 13.4111C17.75 14.1015 18.3096 14.6611 19 14.6611C19.6904 14.6611 20.25 14.1015 20.25 13.4111L20.25 2.16113ZM0.438445 18.9549C-0.0497108 19.4431 -0.049711 20.2345 0.438444 20.7227C0.9266 21.2108 1.71806 21.2108 2.20621 20.7227L0.438445 18.9549ZM18.1161 1.27725L0.438445 18.9549L2.20621 20.7227L19.8839 3.04502L18.1161 1.27725Z",
    }),
    viewBox: "0 0 21 22",
    width: 21,
  },
  linkedin: {
    height: 36,
    width: 36,
    viewBox: "0 0 36 36",
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M25.1048 19.469V25.4253H22.0474V19.8682C22.0474 18.4728 21.6059 17.5199 20.4989 17.5199C19.6541 17.5199 19.1522 18.1614 18.9306 18.7826C18.8501 19.0046 18.8293 19.3129 18.8293 19.6243V25.4251H15.7716C15.7716 25.4251 15.8127 16.0131 15.7716 15.0388H18.8296V16.5106C18.8234 16.5222 18.8147 16.5335 18.8093 16.5446H18.8296V16.5106C19.2359 15.8044 19.9605 14.7949 21.5852 14.7949C23.5967 14.7949 25.1048 16.2793 25.1048 19.469ZM12.573 10.0322C11.5271 10.0322 10.8428 10.8077 10.8428 11.8265C10.8428 12.8237 11.5072 13.6215 12.5328 13.6215H12.5527C13.6191 13.6215 14.2822 12.8237 14.2822 11.8265C14.2619 10.8077 13.6191 10.0322 12.573 10.0322ZM11.0245 25.4253H14.0811V15.0388H11.0245V25.4253Z",
    }),
  },
  "list-ordered": {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M12 12.99c0 .589 0 .998-.59.998H4.596c-.59 0-.59-.41-.59-.999 0-.59 0-.999.59-.999H11.4c.59 0 .59.41.59 1H12zM4.596 3.996H11.4c.59 0 .59-.41.59-1 0-.589 0-.999-.59-.999H4.596c-.59 0-.59.41-.59 1 0 .589 0 .999.59.999zM11.4 6.994H4.596c-.59 0-.59.41-.59 1 0 .589 0 .999.59.999H11.4c.59 0 .59-.41.59-1 0-.59 0-.999-.59-.999zM2.008 1h-.72C.99 1.19.71 1.25.26 1.34V2h.75v2.138H.17v.859h2.837v-.86h-.999V1zm.25 8.123c-.17 0-.45.03-.66.06.53-.56 1.14-1.249 1.14-1.888-.02-.78-.56-1.299-1.36-1.299-.589 0-.968.2-1.378.64l.58.579c.19-.19.38-.38.639-.38.28 0 .48.16.48.52 0 .53-.77 1.199-1.699 2.058v.58h2.998l-.09-.88h-.66l.01.01zm-.08 3.777v-.03c.44-.19.64-.47.64-.859 0-.7-.56-1.11-1.44-1.11-.479 0-.888.19-1.278.52l.55.64c.25-.2.44-.31.689-.31.27 0 .42.13.42.36 0 .27-.2.44-.86.44v.749c.83 0 .98.17.98.47 0 .25-.23.38-.58.38-.28 0-.56-.14-.81-.38l-.479.659c.3.36.77.56 1.409.56.83 0 1.529-.41 1.529-1.16 0-.5-.31-.809-.77-.939v.01z",
    }),
    viewBox: "0 0 12 16",
    width: 12,
  },
  "list-unordered": {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M2 13c0 .59 0 1-.59 1H.59C0 14 0 13.59 0 13c0-.59 0-1 .59-1h.81c.59 0 .59.41.59 1H2zm2.59-9h6.81c.59 0 .59-.41.59-1 0-.59 0-1-.59-1H4.59C4 2 4 2.41 4 3c0 .59 0 1 .59 1zM1.41 7H.59C0 7 0 7.41 0 8c0 .59 0 1 .59 1h.81c.59 0 .59-.41.59-1 0-.59 0-1-.59-1h.01zm0-5H.59C0 2 0 2.41 0 3c0 .59 0 1 .59 1h.81c.59 0 .59-.41.59-1 0-.59 0-1-.59-1h.01zm10 5H4.59C4 7 4 7.41 4 8c0 .59 0 1 .59 1h6.81c.59 0 .59-.41.59-1 0-.59 0-1-.59-1h.01zm0 5H4.59C4 12 4 12.41 4 13c0 .59 0 1 .59 1h6.81c.59 0 .59-.41.59-1 0-.59 0-1-.59-1h.01z",
    }),
    viewBox: "0 0 12 16",
    width: 12,
  },
  location: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M6 0C2.69 0 0 2.5 0 5.5 0 10.02 6 16 6 16s6-5.98 6-10.5C12 2.5 9.31 0 6 0zm0 14.55C4.14 12.52 1 8.44 1 5.5 1 3.02 3.25 1 6 1c1.34 0 2.61.48 3.56 1.36.92.86 1.44 1.97 1.44 3.14 0 2.94-3.14 7.02-5 9.05zM8 5.5c0 1.11-.89 2-2 2-1.11 0-2-.89-2-2 0-1.11.89-2 2-2 1.11 0 2 .89 2 2z",
    }),
    viewBox: "0 0 12 16",
    width: 12,
  },
  lock: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M4 13H3v-1h1v1zm8-6v7c0 .55-.45 1-1 1H1c-.55 0-1-.45-1-1V7c0-.55.45-1 1-1h1V4c0-2.2 1.8-4 4-4s4 1.8 4 4v2h1c.55 0 1 .45 1 1zM3.8 6h4.41V4c0-1.22-.98-2.2-2.2-2.2-1.22 0-2.2.98-2.2 2.2v2H3.8zM11 7H2v7h9V7zM4 8H3v1h1V8zm0 2H3v1h1v-1z",
    }),
    viewBox: "0 0 12 16",
    width: 12,
  },
  "lock-filled": {
    height: 18,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M6.4285 6.42858L11.5714 6.42857V5.0625C11.5714 3.375 10.1249 2.57143 8.99993 2.57143C7.87494 2.57143 6.4285 3.45536 6.4285 5.14286V6.42858ZM3.85707 6.42858L2.28568 6.42858C1.73339 6.42858 1.28568 6.87629 1.28568 7.42858L1.28564 15.7143C1.28564 16.2666 1.73336 16.7143 2.28564 16.7143H15.7142C16.2665 16.7143 16.7142 16.2666 16.7142 15.7143L16.7142 7.42858C16.7142 6.87629 16.2665 6.42857 15.7142 6.42857L14.1428 6.42857V5.0625C14.1428 2.25 12.3749 0 8.99993 0C5.62494 0 3.85707 2.25 3.85707 5.0625V6.42858ZM10.2856 11.5714C10.2856 12.2815 9.71 12.8571 8.99992 12.8571C8.28984 12.8571 7.7142 12.2815 7.7142 11.5714C7.7142 10.8613 8.28984 10.2857 8.99992 10.2857C9.71 10.2857 10.2856 10.8613 10.2856 11.5714Z",
    }),
    viewBox: "0 0 18 18",
    width: 18,
  },
  logout: {
    viewBox: "0 0 22 22",
    width: 22,
    height: 22,
    pathSolid: (props: IconPathProps) => (
      <>
        <path
          d="M9.49994 17.0001L9.50018 20.5001H21.5002V1.50005L9.50018 1.5V5.10005M5.5 6.00012C2.8 6.00012 0.5 8.20012 0.5 11.0001C0.5 13.7001 2.7 16.0001 5.5 16.0001C7.8 16.0001 9.69999 14.6001 10.3 12.5001H11.8999L12.7999 13.3001L13.6999 12.5001H14.5999L15.4999 13.3001L16.3999 12.5001H17.6999L19.0999 11.0001L17.6999 9.50012H10.3C9.69999 7.40012 7.7 6.00012 5.5 6.00012Z"
          stroke={props.fill}
          strokeMiterlimit="10"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
        <circle cx="4" cy="11" r="1.5" stroke={props.fill} fill="none" />
      </>
    ),
  },
  "logout-alt": {
    height: 18,
    width: 18,
    viewBox: "0 0 18 18",
    pathSolid: () => {
      const maskId1 = uuid4();
      return (
        <>
          <mask
            id={maskId1}
            style={{ maskType: "alpha" }}
            width="18"
            height="18"
            x="0"
            y="0"
            maskUnits="userSpaceOnUse"
          >
            <path fill="#D9D9D9" d="M0 0H18V18H0z"></path>
          </mask>
          <g mask={`url(#${maskId1})`}>
            <path
              fill="#003E6B"
              d="M3.75 15.75c-.413 0-.766-.147-1.06-.44a1.445 1.445 0 01-.44-1.06V3.75c0-.413.147-.766.44-1.06.294-.293.647-.44 1.06-.44H9v1.5H3.75v10.5H9v1.5H3.75zm8.25-3l-1.031-1.088L12.88 9.75H6.75v-1.5h6.131L10.97 6.338 12 5.25 15.75 9 12 12.75z"
            ></path>
          </g>
        </>
      );
    },
  },
  mail: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M0 4v8c0 .55.45 1 1 1h12c.55 0 1-.45 1-1V4c0-.55-.45-1-1-1H1c-.55 0-1 .45-1 1zm13 0L7 9 1 4h12zM1 5.5l4 3-4 3v-6zM2 12l3.5-3L7 10.5 8.5 9l3.5 3H2zm11-.5l-4-3 4-3v6z",
    }),
    viewBox: "0 0 14 16",
    width: 14,
  },
  "mail-read": {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M6 5H4V4h2v1zm3 1H4v1h5V6zm5-.48V14c0 .55-.45 1-1 1H1c-.55 0-1-.45-1-1V5.52c0-.33.16-.63.42-.81L2 3.58V3c0-.55.45-1 1-1h1.2L7 0l2.8 2H11c.55 0 1 .45 1 1v.58l1.58 1.13c.27.19.42.48.42.81zM3 7.5L7 10l4-2.5V3H3v4.5zm-2 6l4.5-3-4.5-3v6zm11 .5l-5-3-5 3h10zm1-6.5l-4.5 3 4.5 3v-6z",
    }),
    viewBox: "0 0 14 16",
    width: 14,
  },
  megaphone: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M10 1c-.17 0-.36.05-.52.14C8.04 2.02 4.5 4.58 3 5c-1.38 0-3 .67-3 2.5S1.63 10 3 10c.3.08.64.23 1 .41V15h2v-3.45c1.34.86 2.69 1.83 3.48 2.31.16.09.34.14.52.14.52 0 1-.42 1-1V2c0-.58-.48-1-1-1zm0 12c-.38-.23-.89-.58-1.5-1-.16-.11-.33-.22-.5-.34V3.31c.16-.11.31-.2.47-.31.61-.41 1.16-.77 1.53-1v11zm2-6h4v1h-4V7zm0 2l4 2v1l-4-2V9zm4-6v1l-4 2V5l4-2z",
    }),
    viewBox: "0 0 16 16",
    width: 16,
  },
  mention: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M6.58 15c1.25 0 2.52-.31 3.56-.94l-.42-.94c-.84.52-1.89.83-3.03.83-3.23 0-5.64-2.08-5.64-5.72 0-4.37 3.23-7.18 6.58-7.18 3.45 0 5.22 2.19 5.22 5.2 0 2.39-1.34 3.86-2.5 3.86-1.05 0-1.36-.73-1.05-2.19l.73-3.75H8.98l-.11.72c-.41-.63-.94-.83-1.56-.83-2.19 0-3.66 2.39-3.66 4.38 0 1.67.94 2.61 2.3 2.61.84 0 1.67-.53 2.3-1.25.11.94.94 1.45 1.98 1.45 1.67 0 3.77-1.67 3.77-5C14 2.61 11.59 0 7.83 0 3.66 0 0 3.33 0 8.33 0 12.71 2.92 15 6.58 15zm-.31-5c-.73 0-1.36-.52-1.36-1.67 0-1.45.94-3.22 2.41-3.22.52 0 .84.2 1.25.83l-.52 3.02c-.63.73-1.25 1.05-1.78 1.05V10z",
    }),
    viewBox: "0 0 14 16",
    width: 14,
  },
  mentorship: {
    width: 13,
    height: 11,
    pathSolid: (
      <path d="M10.9231 2.57143C10.9231 3.59721 10.3571 4.4827 9.53846 4.8954L9.53847 5.14286C9.67484 5.25001 10.1364 5.46429 11.3636 5.78571C12.3455 6.04286 13 6.42857 13 7.07143V8.35714C13 8.71218 12.69 9 12.3077 9H9.99872C9.97385 8.3103 9.59045 7.80517 9.1394 7.47608C8.70318 7.15782 8.15203 6.95847 7.61699 6.81834C7.47992 6.78244 7.35386 6.74821 7.23805 6.71568C7.67124 6.1138 7.92308 5.36848 7.92308 4.57143C7.92308 3.30905 7.28149 2.15951 6.28711 1.5223C6.66538 0.625113 7.5139 0 8.5 0C9.83823 0 10.9231 1.15127 10.9231 2.57143ZM6.92266 4.52348C6.90954 3.76195 6.58447 3.0813 6.07734 2.61937C5.65346 2.23329 5.1024 2 4.5 2C3.16177 2 2.07692 3.15127 2.07692 4.57143C2.07692 5.59721 2.6429 6.4827 3.46154 6.8954V7.14286C3.32516 7.25 2.86361 7.46429 1.63636 7.78571C0.654545 8.04286 0 8.42857 0 9.07143V10.3571C0 10.7122 0.309957 11 0.692308 11H8.30769C8.69004 11 9 10.7122 9 10.3571V9.07143C9 9.04726 8.99907 9.02345 8.99724 9C8.95032 8.39975 8.30854 8.03319 7.36364 7.78571C6.98528 7.68662 6.6797 7.59771 6.43399 7.51898C6.08758 7.40799 5.86017 7.31724 5.71558 7.24673C5.63007 7.20503 5.57352 7.17041 5.53847 7.14286L5.53846 6.8954C5.67441 6.82687 5.80338 6.7453 5.92395 6.65221C6.03053 6.56993 6.13054 6.47865 6.223 6.37943C6.46672 6.11788 6.65794 5.80111 6.77851 5.4484C6.87206 5.17472 6.92308 4.8794 6.92308 4.57143C6.92308 4.55541 6.92294 4.53943 6.92266 4.52348Z" />
    ),
    viewBox: "0 0 13 11",
  },
  message: {
    width: 10,
    height: 9,
    pathSolid: (
      <path
        fillRule="evenodd"
        d="M2 0C0.895431 0 0 0.89543 0 2V5C0 5.74028 0.402199 6.38663 1 6.73244V9L3.66667 7H8C9.10457 7 10 6.10457 10 5V2C10 0.895431 9.10457 0 8 0H2ZM8 2H2V3H8V2ZM8 4H2V5H8V4Z"
      />
    ),
    viewBox: "0 0 10 9",
  },
  message2: {
    width: 18,
    height: 18,
    pathSolid: (
      <path
        d="M5.50012 15.7929V13C5.50012 12.7238 5.27626 12.5 5.00012 12.5H4.99816H4.99621H4.99425H4.99229H4.99033H4.98837H4.98641H4.98445H4.98249H4.98053H4.97857H4.97662H4.97466H4.9727H4.97074H4.96878H4.96682H4.96485H4.96289H4.96093H4.95897H4.95701H4.95505H4.95309H4.95113H4.94917H4.94721H4.94524H4.94328H4.94132H4.93936H4.9374H4.93543H4.93347H4.93151H4.92955H4.92759H4.92562H4.92366H4.9217H4.91973H4.91777H4.91581H4.91384H4.91188H4.90992H4.90795H4.90599H4.90403H4.90206H4.9001H4.89813H4.89617H4.89421H4.89224H4.89028H4.88831H4.88635H4.88438H4.88242H4.88045H4.87849H4.87652H4.87456H4.87259H4.87063H4.86866H4.8667H4.86473H4.86277H4.8608H4.85883H4.85687H4.8549H4.85294H4.85097H4.849H4.84704H4.84507H4.84311H4.84114H4.83917H4.83721H4.83524H4.83327H4.83131H4.82934H4.82737H4.82541H4.82344H4.82147H4.81951H4.81754H4.81557H4.8136H4.81164H4.80967H4.8077H4.80573H4.80377H4.8018H4.79983H4.79786H4.7959H4.79393H4.79196H4.78999H4.78803H4.78606H4.78409H4.78212H4.78015H4.77819H4.77622H4.77425H4.77228H4.77031H4.76835H4.76638H4.76441H4.76244H4.76047H4.7585H4.75654H4.75457H4.7526H4.75063H4.74866H4.74669H4.74472H4.74276H4.74079H4.73882H4.73685H4.73488H4.73291H4.73094H4.72898H4.72701H4.72504H4.72307H4.7211H4.71913H4.71716H4.7152H4.71323H4.71126H4.70929H4.70732H4.70535H4.70338H4.70141H4.69945H4.69748H4.69551H4.69354H4.69157H4.6896H4.68763H4.68566H4.6837H4.68173H4.67976H4.67779H4.67582H4.67385H4.67188H4.66992H4.66795H4.66598H4.66401H4.66204H4.66007H4.6581H4.65614H4.65417H4.6522H4.65023H4.64826H4.64629H4.64433H4.64236H4.64039H4.63842H4.63645H4.63448H4.63252H4.63055H4.62858H4.62661H4.62464H4.62268H4.62071H4.61874H4.61677H4.6148H4.61284H4.61087H4.6089H4.60693H4.60497H4.603H4.60103H4.59906H4.5971H4.59513H4.59316H4.59119H4.58923H4.58726H4.58529H4.58333H4.58136H4.57939H4.57742H4.57546H4.57349H4.57152H4.56956H4.56759H4.56562H4.56366H4.56169H4.55973H4.55776H4.55579H4.55383H4.55186H4.54989H4.54793H4.54596H4.544H4.54203H4.54007H4.5381H4.53613H4.53417H4.5322H4.53024H4.52827H4.52631H4.52434H4.52238H4.52041H4.51845H4.51648H4.51452H4.51256H4.51059H4.50863H4.50666H4.5047H4.50273H4.50077H4.49881H4.49684H4.49488H4.49292H4.49095H4.48899H4.48703H4.48506H4.4831H4.48114H4.47917H4.47721H4.47525H4.47329H4.47132H4.46936H4.4674H4.46544H4.46348H4.46151H4.45955H4.45759H4.45563H4.45367H4.45171H4.44975H4.44778H4.44582H4.44386H4.4419H4.43994H4.43798H4.43602H4.43406H4.4321H4.43014H4.42818H4.42622H4.42426H4.4223H4.42034H4.41839H4.41643H4.41447H4.41251H4.41055H4.40859H4.40663H4.40468H4.40272H4.40076H4.3988H4.39684H4.39489H4.39293H4.39097H4.38902H4.38706H4.3851H4.38315H4.38119H4.37923H4.37728H4.37532H4.37336H4.37141H4.36945H4.3675H4.36554H4.36359H4.36163H4.35968H4.35772H4.35577H4.35382H4.35186H4.34991H4.34795H4.346H4.34405H4.34209H4.34014H4.33819H4.33624H4.33428H4.33233H4.33038H4.32843H4.32647H4.32452H4.32257H4.32062H4.31867H4.31672H4.31477H4.31282H4.31087H4.30892H4.30697H4.30502H4.30307H4.30112H4.29917H4.29722H4.29527H4.29332H4.29137H4.28943H4.28748H4.28553H4.28358H4.28163H4.27969H4.27774H4.27579H4.27385H4.2719H4.26995H4.26801H4.26606H4.26412H4.26217H4.26023H4.25828H4.25634H4.25439H4.25245H4.2505H4.24856H4.24662H4.24467H4.24273H4.24079H4.23884H4.2369H4.23496H4.23302H4.23107H4.22913H4.22719H4.22525H4.22331H4.22137H4.21943H4.21749H4.21555H4.21361H4.21167H4.20973H4.20779H4.20585H4.20391H4.20197H4.20003H4.1981H4.19616H4.19422H4.19228H4.19035H4.18841H4.18647H4.18454H4.1826H4.18066H4.17873H4.17679H4.17486H4.17292H4.17099H4.16905H4.16712H4.16519H4.16325H4.16132H4.15939H4.15745H4.15552H4.15359H4.15166H4.14972H4.14779H4.14586H4.14393H4.142H4.14007H4.13814H4.13621H4.13428H4.13235H4.13042H4.12849H4.12656H4.12463H4.12271H4.12078H4.11885H4.11692H4.115H4.11307H4.11114H4.10922H4.10729H4.10537H4.10344H4.10152H4.09959H4.09767H4.09574H4.09382H4.0919H4.08997H4.08805H4.08613H4.08421H4.08228H4.08036H4.07844H4.07652H4.0746H4.07268H4.07076H4.06884H4.06692H4.065H4.06308H4.06116H4.05924H4.05732H4.05541H4.05349H4.05157H4.04965H4.04774H4.04582H4.04391H4.04199H4.04007H4.03816H4.03624H4.03433H4.03242H4.0305H4.02859H4.02668H4.02476H4.02285H4.02094H4.01903H4.01711H4.0152H4.01329H4.01138H4.00947H4.00756H4.00565H4.00374H4.00183H3.99993C3.17156 12.5 2.50012 11.8285 2.50012 11V4.49969C2.50012 3.67124 3.17173 2.99965 4.00019 2.99969L9.0001 2.9999H9.00012L14.0001 2.99995C14.8286 2.99996 15.5001 3.67153 15.5001 4.49995V11C15.5001 11.8284 14.8285 12.5 14.0001 12.5H9.00012C8.86751 12.5 8.74034 12.5526 8.64657 12.6464L5.50012 15.7929Z"
        fill="#003E6B"
        stroke="#003E6B"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    ),
    viewBox: "0 0 18 18",
  },
  message3: {
    width: 22,
    height: 22,
    pathOutline: (props: IconPathProps) => (
      <path
        d="M1.5 14.9524C1.5 16.057 2.39504 16.9524 3.49961 16.9524C4.06813 16.9524 4.73242 16.9524 5.45833 16.9524V21L9.41667 16.9524H18.5C19.6046 16.9524 20.5 16.057 20.5 14.9524V6.0001C20.5 4.89549 19.6045 4.00005 18.4999 4.0001L11 4.00048L3.4999 4.00086C2.39537 4.00092 1.5 4.89634 1.5 6.00086V14.9524Z"
        stroke={props.fill}
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    ),
    pathSolid: (props: IconPathProps) => (
      <path
        d="M1.5 14.9524C1.5 16.057 2.39504 16.9524 3.49961 16.9524C4.06813 16.9524 4.73242 16.9524 5.45833 16.9524V21L9.41667 16.9524H18.5C19.6046 16.9524 20.5 16.057 20.5 14.9524V6.0001C20.5 4.89549 19.6045 4.00005 18.4999 4.0001L11 4.00048L3.4999 4.00086C2.39537 4.00092 1.5 4.89634 1.5 6.00086V14.9524Z"
        stroke={props.fill}
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    ),
    viewBox: "0 0 22 22",
  },
  "move-arrow-down": {
    version: "1.1",
    aria: true,
    height: 17,
    width: 17,
    viewBox: "0 0 17 17",
    pathSolid: <path d="M8 15L2 2L8 4L14 2L8 15Z" />,
  },
  "move-arrow-up": {
    version: "1.1",
    aria: true,
    height: 17,
    width: 17,
    viewBox: "0 0 17 17",
    pathSolid: <path d="M8 0.999992L14 14L8 12L2 14L8 0.999992Z" />,
  },
  mute: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M8 2.81v10.38c0 .67-.81 1-1.28.53L3 10H1c-.55 0-1-.45-1-1V7c0-.55.45-1 1-1h2l3.72-3.72C7.19 1.81 8 2.14 8 2.81zm7.53 3.22l-1.06-1.06-1.97 1.97-1.97-1.97-1.06 1.06L11.44 8 9.47 9.97l1.06 1.06 1.97-1.97 1.97 1.97 1.06-1.06L13.56 8l1.97-1.97z",
    }),
    viewBox: "0 0 16 16",
    width: 16,
  },
  narrative: {
    height: 16,
    width: 16,
    viewBox: "0 0 16 16",
    pathSolid: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M1 3C1 1.89543 1.89543 1 3 1H13C14.1046 1 15 1.89543 15 3V10.9C15 12.0046 14.1046 12.9 13 12.9H5.26622C4.44334 13.635 3.09019 14.3931 1.7 14.3931C2.07805 14.1221 2.67245 13.5334 2.78712 12.8888C1.78264 12.7825 1 11.9327 1 10.9V3ZM6.94171 8.5555C7.34159 8.12703 7.54152 7.53415 7.54152 6.77686V5.19999H5.19981V7.16547H6.4898C6.51171 7.36476 6.46789 7.57152 6.35834 7.78575C6.16662 8.18931 5.79414 8.46831 5.24089 8.62276V9.39999C5.96942 9.26547 6.53636 8.98397 6.94171 8.5555ZM10.2164 8.54803C10.6053 8.11458 10.7998 7.52419 10.7998 6.77686V5.19999H8.45809V7.16547H9.74809C9.76452 7.38469 9.72344 7.59643 9.62484 7.8007C9.43312 8.18931 9.0579 8.46333 8.49917 8.62276V9.39999C9.24962 9.26547 9.82203 8.98148 10.2164 8.54803Z"
      />
    ),
  },
  note: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M3 10h4V9H3v1zm0-2h6V7H3v1zm0-2h8V5H3v1zm10 6H1V3h12v9zM1 2c-.55 0-1 .45-1 1v9c0 .55.45 1 1 1h12c.55 0 1-.45 1-1V3c0-.55-.45-1-1-1H1z",
    }),
    viewBox: "0 0 14 16",
    width: 14,
  },
  notes: {
    height: 18,
    width: 18,
    pathSolid: (
      <path
        fillRule="evenodd"
        fill="#003E6B"
        clipRule="evenodd"
        d="M5 12v1.586L6.586 12H14a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v6a1 1 0 001 1h1zm-1 4v-3a2 2 0 01-2-2V5a2 2 0 012-2h10a2 2 0 012 2v6a2 2 0 01-2 2H7l-3 3z"
      />
    ),
    viewBox: "0 0 18 18",
  },
  "notes-active": {
    height: 18,
    width: 18,
    pathSolid: (
      <>
        <rect width="18" height="18" fill="#003E6B" rx="2" />
        <path
          fillRule="evenodd"
          fill="#fff"
          clipRule="evenodd"
          d="M5 12v1.586L6.586 12H14a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v6a1 1 0 001 1h1zm-1 4v-3a2 2 0 01-2-2V5a2 2 0 012-2h10a2 2 0 012 2v6a2 2 0 01-2 2H7l-3 3z"
        />
      </>
    ),
    viewBox: "0 0 18 18",
  },
  notification: {
    height: 21,
    width: 20,
    viewBox: "0 0 20 21",
    pathSolid: (
      <>
        <path
          d="M10 0.511719L19 5.51172V15.5117L10 20.5117L1 15.5117V5.51172L10 0.511719Z"
          fill="#003E6B"
        />
        <path
          d="M9 6.56626H11V4.51172H9V6.56626ZM9.0625 15.5117H10.9375V8.07535H9.0625V15.5117Z"
          fill="white"
        />
      </>
    ),
  },
  organization: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M16 12.999c0 .439-.45 1-1 1H7.995c-.539 0-.994-.447-.995-.999H1c-.54 0-1-.561-1-1 0-2.634 3-4 3-4s.229-.409 0-1c-.841-.621-1.058-.59-1-3 .058-2.419 1.367-3 2.5-3s2.442.58 2.5 3c.058 2.41-.159 2.379-1 3-.229.59 0 1 0 1s1.549.711 2.42 2.088C9.196 9.369 10 8.999 10 8.999s.229-.409 0-1c-.841-.62-1.058-.59-1-3 .058-2.419 1.367-3 2.5-3s2.437.581 2.495 3c.059 2.41-.158 2.38-1 3-.229.59 0 1 0 1s3.005 1.366 3.005 4z",
    }),
    viewBox: "0 0 16 16",
    width: 16,
  },
  "paper-clip": {
    height: 18,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M16.9554 2.04332C15.5626 0.650458 13.2962 0.650458 11.9033 2.04332L3.48334 10.4633C3.07848 10.8681 2.85552 11.4064 2.85548 11.9789C2.85548 12.5515 3.07845 13.0897 3.48331 13.4946C4.31901 14.3303 5.67885 14.3303 6.51455 13.4946L13.2505 6.75855L12.2401 5.74816L5.50413 12.4841C5.22558 12.7627 4.77231 12.7628 4.4937 12.4841C4.35877 12.3492 4.28441 12.1697 4.28441 11.9789C4.28441 11.7881 4.35877 11.6087 4.49373 11.4737L12.9137 3.05371C13.7495 2.21801 15.1093 2.21801 15.945 3.05371C16.7807 3.88941 16.7807 5.24922 15.945 6.08496L7.52491 14.505C6.13209 15.8978 3.86581 15.8978 2.47288 14.505C1.08005 13.1121 1.08005 10.8458 2.47288 9.45293L10.893 1.03285L9.88256 0.0224609L1.46249 8.44254C-0.487495 10.3925 -0.487495 13.5654 1.46249 15.5154C2.43748 16.4903 3.71819 16.9779 4.99893 16.9779C6.27964 16.9779 7.56035 16.4903 8.53531 15.5154L16.9554 7.09528C18.3482 5.70246 18.3482 3.43614 16.9554 2.04332Z",
    }),
    viewBox: "0 0 18 18",
    width: 18,
  },
  pathway: {
    height: 16,
    pathSolid: (props: IconPathProps) => {
      const stroke = props.fill;
      return (
        <>
          <path
            d="M2.5 1.52712L4 0.589624L5.5 1.52712V3.47288L4 4.41038L2.5 3.47288V1.52712Z M9.5 4.26759L12 2.60093L14.5 4.26759V6.73241L12 8.39908L9.5 6.73241V4.26759Z"
            stroke={stroke}
          />
          <path d="M7.5 11L14.5 7" stroke={stroke} strokeLinecap="round" />
          <path d="M4 3L10 4.5" stroke={stroke} strokeLinecap="round" />
          <path
            d="M1.5 10.2902L4.5 8.57588L7.5 10.2902V13.7098L4.5 15.4241L1.5 13.7098V10.2902Z"
            stroke={stroke}
          />
        </>
      );
    },
    viewBox: "0 0 16 16",
    width: 16,
  },
  pencil: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M0 12v3h3l8-8-3-3-8 8zm3 2H1v-2h1v1h1v1zm10.3-9.3L12 6 9 3l1.3-1.3a.996.996 0 0 1 1.41 0l1.59 1.59c.39.39.39 1.02 0 1.41z",
    }),
    viewBox: "0 0 14 16",
    width: 14,
  },
  person: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M12 14.002a.998.998 0 0 1-.998.998H1.001A1 1 0 0 1 0 13.999V13c0-2.633 4-4 4-4s.229-.409 0-1c-.841-.62-.944-1.59-1-4 .173-2.413 1.867-3 3-3s2.827.586 3 3c-.056 2.41-.159 3.38-1 4-.229.59 0 1 0 1s4 1.367 4 4v1.002z",
    }),
    viewBox: "0 0 12 16",
    width: 12,
  },
  pin: {
    pathOutline: (
      <>
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="m15.903 6.7027c0.0718-0.07184 0.1026-0.17506 0.0821-0.27466-0.0205-0.0993-0.0896-0.182-0.1838-0.21943-0.1976-0.07875-0.398-0.14826-0.6008-0.20848-1e-4 0 0 1e-5 0 0-0.0024-6.8e-4 -0.0047-0.00139-0.0071-0.00207 0 0 1e-4 0 0 0-0.648-0.19158-1.3215-0.28836-2.0098-0.28836-1.4129 0-2.5031 0.40384-2.9241 0.58614l-4.4908-3.8208c0.15695-1.3787-0.58675-2.3216-0.61995-2.3627-0.05402-0.066703-0.1334-0.10745-0.21882-0.11228-0.08511-0.0018109-0.16932 0.027466-0.22969 0.088133l-4.604 4.604c-0.060968 0.06066-0.092962 0.14457-0.087831 0.23089 0.0048292 0.08572 0.046179 0.1657 0.11349 0.21913 0.65828 0.52215 1.4617 0.63202 2.0195 0.63202 0.12435 0 0.23059-0.00574 0.31178-0.01208l3.8398 4.5135c-0.24003 0.6167-0.93206 2.7054-0.30051 4.9237 0.00145 0.0051 0.00292 0.0102 0.00439 0.0153 0.05685 0.1978 0.12425 0.3967 0.20346 0.5955 0.03743 0.0945 0.11982 0.1633 0.21943 0.1838 0.02022 0.0043 0.04074 0.0064 0.06096 0.0064 0.07938 0 0.15635-0.0314 0.21339-0.0885l9.209-9.2092zm-1.5353 0.1212c-0.3861-0.0759-0.7816-0.1142-1.1841-0.1142-1.2298 0-2.1801 0.35371-2.5268 0.50381l-0.5712 0.24733-5.3708-4.5694 0.06027-0.52945c0.03759-0.33019 8.9e-4 -0.62571-0.06325-0.87113l-3.2143 3.2143c0.21832 0.05008 0.4395 0.06914 0.64385 0.06914 0.09377 0 0.17362-0.00433 0.23395-0.00904l0.5088-0.03972 4.5572 5.3568-0.21648 0.5561c-0.19747 0.5073-0.6964 2.0424-0.3989 3.7275l7.5418-7.542z"
        />
        <rect
          xmlns="http://www.w3.org/2000/svg"
          transform="rotate(45 11.225 10.525)"
          x="11.225"
          y="10.525"
          width="7.0449"
          height="1"
          rx=".5"
        />
      </>
    ),
    pathSolid: (
      <>
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="m15.903 6.7027c0.0718-0.07184 0.1026-0.17506 0.0821-0.27466-0.0205-0.0993-0.0896-0.182-0.1838-0.21943-0.1976-0.07875-0.398-0.14826-0.6008-0.20848-0.0024-6.8e-4 -0.0047-0.00139-0.0071-0.00207-0.648-0.19158-1.3215-0.28836-2.0098-0.28836-1.4129 0-2.5031 0.40384-2.9241 0.58614l-4.4908-3.8208c0.15695-1.3787-0.58675-2.3216-0.61995-2.3627-0.05402-0.066703-0.1334-0.10745-0.21882-0.11228-0.08511-0.0018109-0.16932 0.027466-0.22969 0.088133l-4.604 4.604c-0.060968 0.06066-0.092962 0.14457-0.087831 0.23089 0.0048292 0.08572 0.046179 0.1657 0.11349 0.21913 0.65828 0.52215 1.4617 0.63202 2.0195 0.63202 0.12435 0 0.23059-0.00574 0.31178-0.01208l3.8398 4.5135c-0.24003 0.6167-0.93206 2.7054-0.30051 4.9237 0.00145 0.0051 0.00292 0.0102 0.00439 0.0153 0.05685 0.1978 0.12425 0.3967 0.20346 0.5955 0.03743 0.0945 0.11982 0.1633 0.21943 0.1838 0.02022 0.0043 0.04074 0.0064 0.06096 0.0064 0.07938 0 0.15635-0.0314 0.21339-0.0885l9.209-9.2092z"
        />
        <rect
          xmlns="http://www.w3.org/2000/svg"
          transform="rotate(45 11.225 10.525)"
          x="11.225"
          y="10.525"
          width="7.0449"
          height="1"
          rx=".5"
        />
      </>
    ),
    viewBox: "0 0 16 16",
    height: 16,
    width: 16,
  },
  pinterest: {
    height: 36,
    width: 36,
    viewBox: "0 0 36 36",
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M19.4582 22.1593C18.3343 22.0724 17.8621 21.5151 16.9807 20.9801C16.4963 23.5217 15.9043 25.958 14.1508 27.2308C13.60891 23.3894 14.9453 20.5042 15.5659 17.4416C14.508 15.6608 15.6931 12.07651 17.9244 12.95974C20.6705 14.0457 15.5468 19.5812 18.9865 20.2726C22.5774 20.9944 24.0434 14.0417 21.8164 11.78024C18.5988 8.51523 12.45061 11.70604 13.20664 16.38C13.39082 17.5227 14.5716 17.8696 13.67834 19.4463C11.61905 18.9902 11.00479 17.3661 11.08376 15.2008C11.21123 11.65649 14.2685 9.1748 17.335 8.83137C21.2132 8.3973 24.8532 10.25545 25.3556 13.90339C25.9209 18.0206 23.6048 22.4794 19.4582 22.1593Z",
    }),
  },
  plus: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M12 9H7v5H5V9H0V7h5V2h2v5h5v2z",
    }),
    viewBox: "0 0 12 16",
    width: 12,
  },
  "plus-alt": {
    height: 20,
    width: 20,
    viewBox: "0 0 20 20",
    pathSolid: () => {
      const maskId1 = uuid4();
      return (
        <>
          <mask
            id={maskId1}
            style={{ maskType: "alpha" }}
            width="20"
            height="20"
            x="0"
            y="0"
            maskUnits="userSpaceOnUse"
          >
            <path fill="#D9D9D9" d="M0 0H20V20H0z"></path>
          </mask>
          <g mask={`url(#${maskId1})`}>
            <path
              fill="#003E6B"
              d="M9.3 13.5h1.4v-2.8h2.8V9.3h-2.8V6.5H9.3v2.8H6.5v1.4h2.8v2.8zM10 17a6.816 6.816 0 01-2.73-.551 7.069 7.069 0 01-2.222-1.497A7.069 7.069 0 013.55 12.73 6.817 6.817 0 013 10c0-.968.184-1.878.551-2.73a7.069 7.069 0 011.497-2.222A7.069 7.069 0 017.27 3.55 6.816 6.816 0 0110 3c.968 0 1.878.184 2.73.551a7.069 7.069 0 012.223 1.497c.63.63 1.128 1.37 1.496 2.222.367.852.551 1.762.551 2.73s-.184 1.878-.551 2.73a7.068 7.068 0 01-1.497 2.223 7.068 7.068 0 01-2.222 1.496A6.817 6.817 0 0110 17z"
            ></path>
          </g>
        </>
      );
    },
  },
  "plus-small": {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M4 4H3v3H0v1h3v3h1V8h3V7H4V4z",
    }),
    viewBox: "0 0 7 16",
    width: 7,
  },
  "polygon-right": {
    height: 14,
    pathSolid: (
      <path
        d="M12 6.99998L0 13.9282L6.05683e-07 0.0717773L12 6.99998Z"
        fill="#333333"
      />
    ),
    viewBox: "0 0 12 14",
    width: 12,
  },
  "polygon-right-curved": {
    height: 10,
    pathSolid: (
      <path
        d="M8 4.13397C8.66667 4.51887 8.66667 5.48113 8 5.86603L2 9.33013C1.33333 9.71503 0.5 9.2339 0.5 8.4641L0.5 1.5359C0.5 0.766098 1.33333 0.284973 2 0.669873L8 4.13397Z"
        fill="#333333"
      />
    ),
    viewBox: "0 0 9 10",
    width: 9,
  },
  "primitive-dot": {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M0 8c0-2.2 1.8-4 4-4s4 1.8 4 4-1.8 4-4 4-4-1.8-4-4z",
    }),
    viewBox: "0 0 8 16",
    width: 8,
  },
  printer: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      fill: "#0063C3",
      d: "M14.7556 3.68452H13.0586V0.597797C13.0586 0.303266 12.8198 0.0644531 12.5253 0.0644531H3.47475C3.18022 0.0644531 2.94141 0.303234 2.94141 0.597797V3.68452H1.24444C0.55825 3.68452 0 4.2428 0 4.92898V10.2947C0 10.9809 0.55825 11.5391 1.24444 11.5391H2.9415V14.402C2.9415 14.6966 3.18028 14.9354 3.47484 14.9354H12.5252C12.8197 14.9354 13.0585 14.6966 13.0585 14.402V11.5391H14.7556C15.4418 11.5391 16 10.9809 16 10.2947V4.92898C16 4.24283 15.4418 3.68452 14.7556 3.68452ZM4.00806 1.13114H11.9919V3.68452H4.00806V1.13114ZM11.9918 13.8687H4.00819C4.00819 13.7645 4.00819 9.63423 4.00819 9.50533H11.9918C11.9918 9.63748 11.9918 13.7685 11.9918 13.8687ZM12.5253 6.81623H11.1677C10.8731 6.81623 10.6343 6.57745 10.6343 6.28289C10.6343 5.98833 10.8731 5.74955 11.1677 5.74955H12.5253C12.8198 5.74955 13.0586 5.98833 13.0586 6.28289C13.0586 6.57745 12.8198 6.81623 12.5253 6.81623Z",
    }),
    viewBox: "0 0 16 16",
    width: 16,
  },
  progress: {
    height: 16,
    pathSolid: (
      <>
        <path
          d="M0.677808 12.9308C0.363442 13.3849 0.476706 14.0078 0.93079 14.3222C1.38487 14.6366 2.00783 14.5233 2.32219 14.0692L0.677808 12.9308ZM6 7L6.5547 6.16795C6.10086 5.86539 5.48828 5.98233 5.17781 6.43079L6 7ZM9 9L8.4453 9.83205C8.89438 10.1314 9.50002 10.0204 9.81373 9.58124L9 9ZM14.8137 2.58124C15.1347 2.13183 15.0307 1.50728 14.5812 1.18627C14.1318 0.865258 13.5073 0.969349 13.1863 1.41876L14.8137 2.58124ZM2.32219 14.0692L6.82219 7.56921L5.17781 6.43079L0.677808 12.9308L2.32219 14.0692ZM5.4453 7.83205L8.4453 9.83205L9.5547 8.16795L6.5547 6.16795L5.4453 7.83205ZM9.81373 9.58124L14.8137 2.58124L13.1863 1.41876L8.18627 8.41876L9.81373 9.58124Z"
          fill="#003E6B"
        />
        <path
          d="M14 8.5V2H8"
          stroke="#003E6B"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </>
    ),
    viewBox: "0 0 16 16",
    width: 16,
  },
  pulse: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M11.5 8L8.8 5.4 6.6 8.5 5.5 1.6 2.38 8H0v2h3.6l.9-1.8.9 5.4L9 8.5l1.6 1.5H14V8h-2.5z",
    }),
    viewBox: "0 0 14 16",
    width: 14,
  },
  question: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M6 10h2v2H6v-2zm4-3.5C10 8.64 8 9 8 9H6c0-.55.45-1 1-1h.5c.28 0 .5-.22.5-.5v-1c0-.28-.22-.5-.5-.5h-1c-.28 0-.5.22-.5.5V7H4c0-1.5 1.5-3 3-3s3 1 3 2.5zM7 2.3c3.14 0 5.7 2.56 5.7 5.7s-2.56 5.7-5.7 5.7A5.71 5.71 0 0 1 1.3 8c0-3.14 2.56-5.7 5.7-5.7zM7 1C3.14 1 0 4.14 0 8s3.14 7 7 7 7-3.14 7-7-3.14-7-7-7z",
    }),
    viewBox: "0 0 14 16",
    width: 14,
  },
  "question-item": {
    height: 18,
    pathSolid: React.createElement("path", {
      fillRule: "none",
      d: "M15.3673 6.30415C15.3673 2.40553 11.7755 0 8.34694 0C4.26531 0 1 3.31797 1 7.54839C1 10.2028 1.9796 12.1936 3.93878 13.5207V17.5853C3.93878 17.8341 4.10204 18 4.34694 18H11.6939C11.8571 18 12.102 17.8341 12.102 17.5853V15.3456C13.4082 15.3456 14.1429 15.1797 14.7143 14.5991C15.3674 13.9355 15.449 12.1106 15.449 11.1982C15.6939 11.1982 15.9388 11.1982 16.1837 11.1982C16.5918 11.1982 17 10.9493 17 10.1198C16.9184 8.70968 15.8571 7.71429 15.3673 6.30415ZM12.0204 10.8664C12.0204 11.0323 11.8571 11.2811 11.6122 11.2811H4.99999C4.83673 11.2811 4.59183 11.1152 4.59183 10.8664V4.14747C4.59183 3.98157 4.7551 3.81567 4.99999 3.81567H11.7755L7.93877 7.46544L7.20408 6.71889C6.79592 6.30415 6.06123 6.30415 5.65307 6.71889C5.24491 7.13364 5.24491 7.88019 5.65307 8.29493L7.12245 9.78802C7.36735 10.0369 7.61224 10.1198 7.93877 10.1198C8.18367 10.1198 8.5102 10.0369 8.67346 9.78802L11.9388 6.71889V10.8664H12.0204ZM13 4.81106L8.18369 9.29032C8.02042 9.45622 7.85715 9.45622 7.69389 9.29032L6.22448 7.79724C6.06122 7.63134 6.06122 7.38249 6.22448 7.29954C6.38775 7.13364 6.63265 7.13364 6.71428 7.29954L7.93877 8.54378L12.4286 4.31336C12.5918 4.14746 12.8367 4.14746 12.9184 4.31336C13.1633 4.39631 13.1633 4.64516 13 4.81106Z",
    }),
    viewBox: "0 0 18 18",
    width: 18,
  },
  quote: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M6.16 3.5C3.73 5.06 2.55 6.67 2.55 9.36c.16-.05.3-.05.44-.05 1.27 0 2.5.86 2.5 2.41 0 1.61-1.03 2.61-2.5 2.61-1.9 0-2.99-1.52-2.99-4.25 0-3.8 1.75-6.53 5.02-8.42L6.16 3.5zm7 0c-2.43 1.56-3.61 3.17-3.61 5.86.16-.05.3-.05.44-.05 1.27 0 2.5.86 2.5 2.41 0 1.61-1.03 2.61-2.5 2.61-1.89 0-2.98-1.52-2.98-4.25 0-3.8 1.75-6.53 5.02-8.42l1.14 1.84h-.01z",
    }),
    viewBox: "0 0 14 16",
    width: 14,
  },
  "quote-v2": {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M0 16V8.51692C0 2.65846 2.26462 0.0492301 7.63077 0V3.84C5.76 3.88923 4.67692 4.92308 4.67692 7.18769V8.56615H7.43385V16H0ZM11.3723 16V8.51692C11.3723 2.65846 13.6369 0.0492301 19.0031 0V3.84C17.1323 3.88923 16.0492 4.92308 16.0492 7.18769V8.56615H18.8062V16H11.3723Z",
    }),
    viewBox: "0 0 19 16",
    width: 19,
  },
  redo: {
    height: 16,
    width: 16,
    viewBox: "0 0 16 16",
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M5.1527 6.90265C5.62968 5.22703 7.1717 4 9.0003 4C11.2094 4 13.0003 5.79086 13.0003 8C13.0003 10.2091 11.2094 12 9.0003 12C8.44802 12 8.0003 12.4477 8.0003 13C8.0003 13.5523 8.44802 14 9.0003 14C12.314 14 15.0003 11.3137 15.0003 8C15.0003 4.68629 12.314 2 9.0003 2C5.87536 2 3.30839 4.38896 3.02607 7.44019L1.69929 6.11341C1.30877 5.72289 0.675605 5.72289 0.285081 6.11341C-0.105444 6.50394 -0.105444 7.1371 0.285081 7.52763L3.11351 10.3561C3.50403 10.7466 4.1372 10.7466 4.52772 10.3561L7.35615 7.52763C7.74667 7.1371 7.74667 6.50394 7.35615 6.11341C6.96562 5.72289 6.33246 5.72289 5.94194 6.11341L5.1527 6.90265Z",
    }),
  },
  remix: {
    height: 16,
    width: 16,
    viewBox: "0 0 16 16",
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M8.00001 3.05721C9.38 3.05721 10.63 3.63324 11.535 4.56409L9.16667 7H15V1L12.95 3.10861C11.685 1.80746 9.93499 1 8.00001 1C4.47505 1 1.48496 3.51306 1 7H3.33333C3.79833 4.6548 5.58007 3.05721 8.00001 3.05721Z M12.1399 13.5959C14.3475 11.6461 14.835 10.1932 15 9H12.6667C12.2017 11.3452 10.4199 12.9428 7.99992 12.9428C6.61993 12.9428 5.36993 12.3667 4.46497 11.4359L6.83333 9H1V15L3.33333 12.6001C4.59834 13.9013 6.06494 15 7.99992 15C9.54992 15 10.9799 14.4754 12.1399 13.5959Z",
    }),
  },
  reply: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M6 3.5c3.92.44 8 3.125 8 10-2.312-5.062-4.75-6-8-6V11L.5 5.5 6 0v3.5z",
    }),
    viewBox: "0 0 14 16",
    width: 14,
  },
  repo: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M4 9H3V8h1v1zm0-3H3v1h1V6zm0-2H3v1h1V4zm0-2H3v1h1V2zm8-1v12c0 .55-.45 1-1 1H6v2l-1.5-1.5L3 16v-2H1c-.55 0-1-.45-1-1V1c0-.55.45-1 1-1h10c.55 0 1 .45 1 1zm-1 10H1v2h2v-1h3v1h5v-2zm0-10H2v9h9V1z",
    }),
    viewBox: "0 0 12 16",
    width: 12,
  },
  report: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M0 2a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H7l-4 4v-4H1a1 1 0 0 1-1-1V2zm1 0h14v9H6.5L4 13.5V11H1V2zm6 6h2v2H7V8zm0-5h2v4H7V3z",
    }),
    viewBox: "0 0 16 16",
    width: 16,
  },
  "report-flag": {
    height: 16,
    pathSolid: (
      <>
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M12.5985 0.282501C12.8661 0.363919 13.0661 0.587459 13.1174 0.862398L14.2374 6.8624C14.2956 7.17389 14.1517 7.48831 13.8779 7.64792L13.5002 7.00002C13.8779 7.64792 13.8782 7.64777 13.8779 7.64792L13.8769 7.64855L13.8756 7.64928L13.8725 7.65105L13.8642 7.65581L13.8389 7.66993C13.8182 7.68131 13.7899 7.6965 13.7544 7.71479C13.6834 7.75135 13.5832 7.8004 13.4567 7.85611C13.204 7.96733 12.8436 8.10619 12.3981 8.22478C11.5087 8.46152 10.262 8.62208 8.8543 8.30288C7.74198 8.05065 6.74875 8.1758 6.02809 8.36764C5.7226 8.44896 5.4694 8.54143 5.28144 8.61966L6.67207 14.8363C6.7625 15.2405 6.50811 15.6415 6.10389 15.7319C5.69966 15.8224 5.29867 15.568 5.20825 15.1637L2.26825 2.02089C2.17783 1.61667 2.43221 1.21568 2.83644 1.12525C3.20122 1.04365 3.56337 1.24286 3.69702 1.57958C3.92862 1.4712 4.19769 1.35834 4.49652 1.25528C5.51329 0.904643 6.94411 0.644539 8.42168 1.14711C9.4695 1.50352 10.324 1.32554 10.9247 1.06286C11.2291 0.929716 11.4682 0.774244 11.6289 0.653995C11.7089 0.594188 11.7681 0.544089 11.8048 0.511409C11.8232 0.495105 11.8358 0.483253 11.8425 0.476838L11.8479 0.471661C12.0436 0.274474 12.3324 0.201549 12.5985 0.282501ZM4.0428 3.08249C4.28098 2.9566 4.6044 2.80477 4.98554 2.67333C5.8329 2.38111 6.89622 2.21264 7.93865 2.56721C9.41082 3.06795 10.6563 2.81736 11.5257 2.43718C11.6426 2.38604 11.7528 2.3326 11.8558 2.27838L12.656 6.56482C12.4821 6.63301 12.2651 6.70795 12.0122 6.77526C11.2916 6.96709 10.2983 7.09225 9.18602 6.84002C7.77834 6.52082 6.53157 6.68138 5.64223 6.91812C5.38064 6.98775 5.14839 7.06437 4.95006 7.13828L4.0428 3.08249Z"
          fill="#003E6B"
        />
        <path
          d="M8 2.00006C5.6 1.20006 4 2.3334 3.5 3.00006L4.5 7.99998C5.16667 7.83332 6.60557 6.99998 7.5 6.99998C9 6.99998 11 7.49998 12 7.49998C13 7.49998 13 6.99998 13 6.49998C13 6.09998 12.6667 4.66665 12.5 3.99998V2.00005C12.5 1.20005 11.6667 1.16667 11.5 1.5C11.1 2.7 9.5 1.99998 8 2.00006Z"
          fill="#003E6B"
        />
      </>
    ),
    viewBox: "0 0 16 16",
    width: 16,
  },
  rocket: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M12.17 3.83c-.27-.27-.47-.55-.63-.88-.16-.31-.27-.66-.34-1.02-.58.33-1.16.7-1.73 1.13-.58.44-1.14.94-1.69 1.48-.7.7-1.33 1.81-1.78 2.45H3L0 10h3l2-2c-.34.77-1.02 2.98-1 3l1 1c.02.02 2.23-.64 3-1l-2 2v3l3-3v-3c.64-.45 1.75-1.09 2.45-1.78.55-.55 1.05-1.13 1.47-1.7.44-.58.81-1.16 1.14-1.72-.36-.08-.7-.19-1.03-.34a3.39 3.39 0 0 1-.86-.63zM16 0s-.09.38-.3 1.06c-.2.7-.55 1.58-1.06 2.66-.7-.08-1.27-.33-1.66-.72-.39-.39-.63-.94-.7-1.64C13.36.84 14.23.48 14.92.28 15.62.08 16 0 16 0z",
    }),
    viewBox: "0 0 16 16",
    width: 16,
  },
  salary: {
    viewBox: "0 0 32 32",
    width: 32,
    height: 32,
    pathSolid: () => {
      const maskId = uuid4();
      return (
        <>
          <mask
            id={maskId}
            style={{ maskType: "alpha" }}
            maskUnits="userSpaceOnUse"
            x="0"
            y="0"
            width="32"
            height="32"
          >
            <rect width="32" height="32" fill="#ECF8F6" />
          </mask>
          <g mask={`url(#${maskId})`}>
            <path
              d="M18.6667 17.3333C17.5556 17.3333 16.6112 16.9444 15.8334 16.1666C15.0556 15.3888 14.6667 14.4444 14.6667 13.3333C14.6667 12.2221 15.0556 11.2777 15.8334 10.4999C16.6112 9.72214 17.5556 9.33325 18.6667 9.33325C19.7778 9.33325 20.7223 9.72214 21.5 10.4999C22.2778 11.2777 22.6667 12.2221 22.6667 13.3333C22.6667 14.4444 22.2778 15.3888 21.5 16.1666C20.7223 16.9444 19.7778 17.3333 18.6667 17.3333ZM9.33337 21.3333C8.60004 21.3333 7.97226 21.0721 7.45004 20.5499C6.92782 20.0277 6.66671 19.3999 6.66671 18.6666V7.99992C6.66671 7.26659 6.92782 6.63881 7.45004 6.11659C7.97226 5.59436 8.60004 5.33325 9.33337 5.33325H28C28.7334 5.33325 29.3612 5.59436 29.8834 6.11659C30.4056 6.63881 30.6667 7.26659 30.6667 7.99992V18.6666C30.6667 19.3999 30.4056 20.0277 29.8834 20.5499C29.3612 21.0721 28.7334 21.3333 28 21.3333H9.33337ZM12 18.6666H25.3334C25.3334 17.9333 25.5945 17.3055 26.1167 16.7833C26.6389 16.261 27.2667 15.9999 28 15.9999V10.6666C27.2667 10.6666 26.6389 10.4055 26.1167 9.88325C25.5945 9.36103 25.3334 8.73325 25.3334 7.99992H12C12 8.73325 11.7389 9.36103 11.2167 9.88325C10.6945 10.4055 10.0667 10.6666 9.33337 10.6666V15.9999C10.0667 15.9999 10.6945 16.261 11.2167 16.7833C11.7389 17.3055 12 17.9333 12 18.6666ZM25.3334 26.6666H4.00004C3.26671 26.6666 2.63893 26.4055 2.11671 25.8833C1.59449 25.361 1.33337 24.7333 1.33337 23.9999V10.6666C1.33337 10.2888 1.46115 9.97214 1.71671 9.71659C1.97226 9.46103 2.28893 9.33325 2.66671 9.33325C3.04449 9.33325 3.36115 9.46103 3.61671 9.71659C3.87226 9.97214 4.00004 10.2888 4.00004 10.6666V23.9999H25.3334C25.7112 23.9999 26.0278 24.1277 26.2834 24.3833C26.5389 24.6388 26.6667 24.9555 26.6667 25.3333C26.6667 25.711 26.5389 26.0277 26.2834 26.2833C26.0278 26.5388 25.7112 26.6666 25.3334 26.6666Z"
              fill="#80D2C9"
            />
          </g>
        </>
      );
    },
  },
  search: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M15.7 13.3l-3.81-3.83A5.93 5.93 0 0 0 13 6c0-3.31-2.69-6-6-6S1 2.69 1 6s2.69 6 6 6c1.3 0 2.48-.41 3.47-1.11l3.83 3.81c.19.2.45.3.7.3.25 0 .52-.09.7-.3a.996.996 0 0 0 0-1.41v.01zM7 10.7c-2.59 0-4.7-2.11-4.7-4.7 0-2.59 2.11-4.7 4.7-4.7 2.59 0 4.7 2.11 4.7 4.7 0 2.59-2.11 4.7-4.7 4.7z",
    }),
    viewBox: "0 0 16 16",
    width: 16,
  },
  "search-alt": {
    viewBox: "0 0 22 22",
    width: 22,
    height: 22,
    pathSolid: (props: IconPathProps) => (
      <path
        d="M15.8544 15.8792L21 21L15.8544 15.8792ZM18.4757 9.69565C18.4757 14.5266 14.5922 18.3913 9.73786 18.3913C4.88349 18.3913 1 14.5266 1 9.69565C1 4.86473 4.88349 1 9.73786 1C14.4951 1 18.4757 4.96135 18.4757 9.69565Z"
        fill="none"
        stroke={props.fill}
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    ),
  },
  "search-autocomplete": {
    height: 18,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      clipRule: "evenodd",
      d: "M12 8C12 10.2091 10.2091 12 8 12C5.79086 12 4 10.2091 4 8C4 5.79086 5.79086 4 8 4C10.2091 4 12 5.79086 12 8ZM11.4766 12.8907C10.4957 13.5892 9.29584 14 8 14C4.68629 14 2 11.3137 2 8C2 4.68629 4.68629 2 8 2C11.3137 2 14 4.68629 14 8C14 9.29582 13.5892 10.4957 12.8908 11.4765L16.0077 14.5934C16.3982 14.9839 16.3982 15.6171 16.0077 16.0076C15.6171 16.3981 14.984 16.3981 14.5934 16.0076L11.4766 12.8907Z",
    }),
    viewBox: "0 0 18 18",
    width: 18,
  },
  "search-suggestion": {
    height: 18,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      clipRule: "evenodd",
      d: "M12.4812 11.694C13.2538 10.7463 13.7157 9.53385 13.7157 8.20782C13.7157 5.19841 11.2172 2.69995 8.20782 2.69995C5.15235 2.69995 2.69995 5.15235 2.69995 8.20782C2.69995 11.2633 5.15235 13.7157 8.20782 13.7157C9.44252 13.7157 10.5787 13.3152 11.4945 12.6364L14.1544 15.117C14.4294 15.3735 14.8604 15.3585 15.117 15.0834C15.3735 14.8083 15.3585 14.3773 15.0834 14.1208L12.4812 11.694ZM10.5518 11.6355C9.88663 12.0891 9.08027 12.3535 8.20782 12.3535C5.90465 12.3535 4.06211 10.511 4.06211 8.20782C4.06211 5.90465 5.90465 4.06211 8.20782 4.06211C10.4649 4.06211 12.3535 5.95071 12.3535 8.20782C12.3535 9.19143 12.0175 10.091 11.453 10.799C11.1964 11.1208 10.8926 11.403 10.5518 11.6355Z",
    }),
    viewBox: "0 0 18 18",
    width: 18,
    fill: "#333333",
  },
  settings: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M4 7H3V2h1v5zm-1 7h1v-3H3v3zm5 0h1V8H8v6zm5 0h1v-2h-1v2zm1-12h-1v6h1V2zM9 2H8v2h1V2zM5 8H2c-.55 0-1 .45-1 1s.45 1 1 1h3c.55 0 1-.45 1-1s-.45-1-1-1zm5-3H7c-.55 0-1 .45-1 1s.45 1 1 1h3c.55 0 1-.45 1-1s-.45-1-1-1zm5 4h-3c-.55 0-1 .45-1 1s.45 1 1 1h3c.55 0 1-.45 1-1s-.45-1-1-1z",
    }),
    viewBox: "0 0 16 16",
    width: 16,
  },
  settings2: {
    width: 16,
    height: 16,
    viewBox: "0 0 16 16",
    pathSolid: (
      <>
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M6.71777 0.56437C6.71777 0.337609 6.88678 0.0753661 7.11169 0.0500642C7.7176 -0.0176839 8.32947 -0.0168485 8.93104 0.0536385C9.16518 0.08144 9.34186 0.292483 9.32323 0.521864L9.32067 0.620808C9.32067 1.328 9.75632 1.98549 10.4052 2.25743C11.0944 2.54671 11.8967 2.36602 12.4009 1.80921C12.5589 1.6346 12.8256 1.61376 13.0089 1.7614C13.4828 2.14188 13.9132 2.57707 14.2885 3.05565C14.4308 3.23991 14.41 3.50126 14.238 3.65944C13.7151 4.1354 13.5183 4.98238 13.7872 5.64076C14.0661 6.32181 14.7518 6.76218 15.4932 6.73533C15.726 6.72753 15.9258 6.8994 15.9514 7.1314C15.9839 7.42413 16 7.71691 16 8.00964C16 8.31802 15.9822 8.62557 15.9465 8.93127C15.9196 9.16494 15.7147 9.33776 15.4792 9.32389C14.7296 9.28138 14.0317 9.71574 13.7429 10.4063C13.4662 11.0655 13.6621 11.922 14.1915 12.4007C14.3666 12.5596 14.3874 12.8263 14.2397 13.0096C13.8636 13.4769 13.4328 13.9035 12.9593 14.2752C12.7765 14.4194 12.5137 14.3977 12.3565 14.2266C11.8549 13.6793 11.0601 13.4995 10.3783 13.7792C9.69548 14.0598 9.26418 14.7287 9.28198 15.4253C9.28198 15.6546 9.11445 15.9248 8.88859 15.9499C8.28268 16.0177 7.67028 16.0168 7.06925 15.9465C6.83862 15.9187 6.66836 15.738 6.67652 15.4844C6.67652 14.7026 6.25271 14.0198 5.59512 13.7436C4.90539 13.4544 4.10326 13.6341 3.59903 14.191C3.44097 14.3647 3.17469 14.3864 2.99097 14.2387C2.51841 13.86 2.08764 13.4239 1.71146 12.9445C1.56865 12.7612 1.58984 12.498 1.76224 12.3407C2.28564 11.8648 2.48025 11.0143 2.21314 10.3603C1.93381 9.67835 1.24807 9.23876 0.506245 9.26483C0.273893 9.27263 0.0741052 9.10064 0.0485066 8.8687C-0.0179902 8.26932 -0.0158471 7.66649 0.0532691 7.0689C0.0805941 6.83428 0.285204 6.66228 0.520533 6.67628C1.26974 6.71878 1.96769 6.28443 2.25743 5.59563C2.53359 4.93374 2.33732 4.07718 1.80921 3.60032C1.63413 3.4422 1.61377 3.17466 1.7601 2.99231C2.13312 2.52664 2.56354 2.10015 3.04003 1.72491C3.22285 1.58066 3.48604 1.60156 3.64326 1.77355C4.14446 2.31994 4.93968 2.49967 5.62156 2.22094C6.2873 1.9481 6.71777 1.29752 6.71777 0.56437ZM8 12C10.2091 12 12 10.2091 12 8C12 5.79086 10.2091 4 8 4C5.79086 4 4 5.79086 4 8C4 10.2091 5.79086 12 8 12Z"
        />
        <circle cx="8" cy="8" r="2" />
      </>
    ),
  },
  settings3: {
    width: 22,
    height: 22,
    viewBox: "0 0 22 22",
    pathSolid: (props: IconPathProps) => (
      <>
        <path d="M10.0076 2.11843L9.95203 1.62153L9.9517 1.62156L10.0076 2.11843ZM8.34283 4.5437L8.53202 5.00653L8.53244 5.00636L8.34283 4.5437ZM6.13271 4.04389L5.76366 4.38124L5.76424 4.38187L6.13271 4.04389ZM5.45878 3.98955L5.76813 4.38237L5.76849 4.38208L5.45878 3.98955ZM4.02886 5.40547L4.41883 5.7184L4.41909 5.71807L4.02886 5.40547ZM4.08373 6.08473L3.74861 6.4558L3.74864 6.45583L4.08373 6.08473ZM4.58447 8.31387L5.04535 8.50774L5.04591 8.5064L4.58447 8.31387ZM2.64403 9.52115L2.61436 10.0203L2.61571 10.0203L2.64403 9.52115ZM2.12201 9.95978L1.62537 9.90194L1.62532 9.90234L2.12201 9.95978ZM2.11669 11.9705L2.61367 11.9156L2.61364 11.9154L2.11669 11.9705ZM2.62807 12.4131L2.64484 12.9128L2.64563 12.9127L2.62807 12.4131ZM4.53499 13.6369L4.99786 13.4478L4.99768 13.4474L4.53499 13.6369ZM4.03126 15.8494L3.69488 15.4795L3.69427 15.48L4.03126 15.8494ZM3.97452 16.5239L3.58015 16.8312L3.58114 16.8325L3.97452 16.5239ZM5.40398 17.9698L5.71727 17.5801L5.71665 17.5796L5.40398 17.9698ZM6.08329 17.9165L6.45312 18.2529L6.45391 18.2521L6.08329 17.9165ZM8.3133 17.4167L8.50691 16.9557L8.50668 16.9556L8.3133 17.4167ZM9.52142 19.3614L10.0212 19.3775L10.0214 19.3695V19.3614H9.52142ZM9.96017 19.8777L9.90046 20.3741L9.90203 20.3743L9.96017 19.8777ZM11.9927 19.8816L11.9374 19.3846L11.9372 19.3846L11.9927 19.8816ZM12.4322 19.2955H12.9324L12.932 19.2827L12.4322 19.2955ZM13.657 17.4565L13.4672 16.9939L13.4669 16.994L13.657 17.4565ZM15.867 17.9562L15.4984 18.2941L15.4988 18.2945L15.867 17.9562ZM16.5405 18.0106L16.2317 17.6173L16.2308 17.618L16.5405 18.0106ZM17.9709 16.5966L17.5816 16.2829L17.5814 16.2831L17.9709 16.5966ZM17.9171 15.9164L18.2531 15.5461L18.2524 15.5455L17.9171 15.9164ZM17.4158 13.6882L17.8769 13.8817L17.8771 13.8812L17.4158 13.6882ZM19.3557 12.479L19.3851 11.9799L19.384 11.9798L19.3557 12.479ZM19.8778 12.0404L19.3811 11.9825L19.3811 11.9832L19.8778 12.0404ZM19.8832 10.0296L19.3862 10.0845L19.3862 10.0849L19.8832 10.0296ZM19.3713 9.58713L19.3546 9.08741L19.3532 9.08746L19.3713 9.58713ZM17.4654 8.36428L17.0025 8.55334L17.0027 8.55373L17.4654 8.36428ZM17.969 6.15078L18.3056 6.52053L18.3074 6.51885L17.969 6.15078ZM18.0254 5.47624L18.4211 5.17056L18.4188 5.1677L18.0254 5.47624ZM16.5959 4.03032L16.2822 4.41971L16.2828 4.4202L16.5959 4.03032ZM15.9166 4.08372L16.2872 4.41935L16.2874 4.4192L15.9166 4.08372ZM13.687 4.58448L13.8805 4.12343L13.8803 4.12334L13.687 4.58448ZM12.4754 2.75606L11.9754 2.74312V2.75606H12.4754ZM12.4783 2.64552L11.9799 2.60504L11.9788 2.61879L11.9785 2.63259L12.4783 2.64552ZM12.0401 2.12242L12.0991 1.62591L12.0983 1.62582L12.0401 2.12242ZM9.9517 1.62156C9.64537 1.65603 9.42188 1.84386 9.2885 2.02786C9.15412 2.21324 9.06751 2.45101 9.06751 2.69301H10.0675C10.0675 2.6905 10.0682 2.67913 10.0743 2.66146C10.0802 2.64408 10.0889 2.6276 10.0981 2.61478C10.1076 2.60168 10.1133 2.59843 10.1104 2.60038C10.1066 2.60291 10.0906 2.61224 10.0635 2.6153L9.9517 1.62156ZM9.06751 2.69301C9.06751 3.31428 8.70453 3.85511 8.15322 4.08105L8.53244 5.00636C9.46865 4.62267 10.0675 3.70986 10.0675 2.69301H9.06751ZM8.15364 4.08088C7.58716 4.31244 6.92083 4.1634 6.50117 3.7059L5.76424 4.38187C6.46445 5.14522 7.57494 5.39776 8.53202 5.00653L8.15364 4.08088ZM6.50175 3.70653C6.14865 3.32026 5.55833 3.27411 5.14907 3.59702L5.76849 4.38208C5.76881 4.38183 5.76833 4.38221 5.76745 4.38247C5.76683 4.38266 5.76653 4.38263 5.76647 4.38263C5.76641 4.38262 5.76598 4.38259 5.76519 4.38221C5.76414 4.3817 5.76353 4.38109 5.76367 4.38124L6.50175 3.70653ZM5.14944 3.59673C4.58753 4.03923 4.07947 4.54255 3.63863 5.09287L4.41909 5.71807C4.81173 5.22792 5.26537 4.77829 5.76813 4.38237L5.14944 3.59673ZM3.6389 5.09254C3.31015 5.50221 3.35591 6.10115 3.74861 6.4558L4.41885 5.71366C4.41879 5.7136 4.41939 5.71423 4.41984 5.71517C4.42015 5.71581 4.4201 5.71598 4.42008 5.7157C4.42005 5.71541 4.42013 5.71552 4.41994 5.71617C4.41966 5.7171 4.41913 5.71803 4.41883 5.7184L3.6389 5.09254ZM3.74864 6.45583C4.18232 6.84743 4.34937 7.57884 4.12302 8.12134L5.04591 8.5064C5.43662 7.56999 5.16512 6.38752 4.41882 5.71363L3.74864 6.45583ZM4.12358 8.12C3.88236 8.69345 3.29923 9.05752 2.67235 9.02195L2.61571 10.0203C3.66284 10.0798 4.63918 9.47332 5.04535 8.50774L4.12358 8.12ZM2.67371 9.02203C2.14507 8.99061 1.68645 9.37747 1.62537 9.90194L2.61865 10.0176C2.61875 10.0168 2.61878 10.0173 2.61832 10.0181C2.618 10.0187 2.61771 10.019 2.61756 10.0191C2.6174 10.0192 2.61694 10.0196 2.61602 10.0199C2.61482 10.0203 2.61398 10.0202 2.61436 10.0203L2.67371 9.02203ZM1.62532 9.90234C1.54382 10.607 1.54126 11.3182 1.61974 12.0256L2.61364 11.9154C2.54354 11.2835 2.54577 10.6478 2.6187 10.0172L1.62532 9.90234ZM1.61971 12.0253C1.67708 12.5452 2.12504 12.9302 2.64484 12.9128L2.6113 11.9133C2.61081 11.9134 2.6113 11.9133 2.61205 11.9136C2.61231 11.9137 2.61247 11.9138 2.61255 11.9138C2.61263 11.9138 2.61265 11.9139 2.61266 11.9139C2.61267 11.9139 2.61271 11.9139 2.6128 11.914C2.6129 11.9142 2.61305 11.9144 2.61322 11.9147C2.61369 11.9156 2.61373 11.9162 2.61367 11.9156L1.61971 12.0253ZM2.64563 12.9127C3.26761 12.8909 3.84053 13.2606 4.07229 13.8264L4.99768 13.4474C4.60534 12.4895 3.64604 11.877 2.61051 11.9134L2.64563 12.9127ZM4.07211 13.826C4.29062 14.3609 4.12522 15.0882 3.69489 15.4795L4.36763 16.2193C5.10676 15.5473 5.37619 14.3741 4.99786 13.4478L4.07211 13.826ZM3.69427 15.48C3.30758 15.8328 3.26106 16.4218 3.58016 16.8312L4.36889 16.2165C4.36862 16.2162 4.36889 16.2165 4.36906 16.2171C4.36915 16.2174 4.3691 16.2174 4.36912 16.2171C4.36913 16.2169 4.36917 16.2171 4.36888 16.2177C4.36845 16.2186 4.36796 16.219 4.36824 16.2188L3.69427 15.48ZM3.58114 16.8325C4.02481 17.398 4.53308 17.9126 5.09131 18.36L5.71665 17.5796C5.219 17.1808 4.76476 16.7211 4.3679 16.2153L3.58114 16.8325ZM5.09069 18.3595C5.50304 18.691 6.09966 18.6414 6.45312 18.2529L5.71346 17.58C5.71315 17.5803 5.71349 17.5799 5.71421 17.5796C5.71445 17.5794 5.71462 17.5794 5.7147 17.5794C5.71473 17.5794 5.71475 17.5794 5.71476 17.5794C5.71477 17.5794 5.71478 17.5794 5.71478 17.5794C5.71478 17.5794 5.71485 17.5793 5.71501 17.5794C5.71517 17.5794 5.71547 17.5794 5.71587 17.5796C5.71695 17.5799 5.71757 17.5804 5.71727 17.5801L5.09069 18.3595ZM6.45391 18.2521C6.87573 17.7863 7.54613 17.6371 8.11992 17.8778L8.50668 16.9556C7.53936 16.5499 6.41749 16.8025 5.71267 17.5808L6.45391 18.2521ZM8.11969 17.8777C8.64649 18.0989 9.02142 18.6642 9.02142 19.3614H10.0214C10.0214 18.312 9.44941 17.3515 8.50691 16.9557L8.11969 17.8777ZM9.02168 19.3454C9.00395 19.8967 9.39436 20.3132 9.90046 20.3741L10.0199 19.3813C10.0123 19.3804 10.0095 19.3787 10.0102 19.3791C10.0109 19.3795 10.0137 19.3812 10.0169 19.3848C10.0202 19.3885 10.0218 19.392 10.0222 19.3933C10.0227 19.3946 10.0208 19.3901 10.0212 19.3775L9.02168 19.3454ZM9.90203 20.3743C10.6118 20.4574 11.3341 20.4583 12.0483 20.3785L11.9372 19.3846C11.2975 19.4562 10.6515 19.4552 10.0183 19.3811L9.90203 20.3743ZM12.048 20.3785C12.3608 20.3437 12.5847 20.1478 12.7156 19.9623C12.8481 19.7746 12.9322 19.5358 12.9322 19.2955H11.9322C11.9322 19.3 11.9311 19.3137 11.9245 19.3337C11.9179 19.3533 11.9085 19.3716 11.8986 19.3857C11.8884 19.4002 11.8823 19.4037 11.8857 19.4014C11.89 19.3984 11.9076 19.3879 11.9374 19.3846L12.048 20.3785ZM12.932 19.2827C12.9176 18.7163 13.2718 18.1553 13.847 17.9189L13.4669 16.994C12.5165 17.3846 11.9071 18.3181 11.9324 19.3082L12.932 19.2827ZM13.8467 17.9191C14.4127 17.6869 15.0782 17.8356 15.4984 18.2941L16.2356 17.6184C15.535 16.8541 14.4248 16.6011 13.4672 16.9939L13.8467 17.9191ZM15.4988 18.2945C15.8507 18.6775 16.4397 18.727 16.8502 18.4031L16.2308 17.618C16.2306 17.6182 16.2312 17.6177 16.2323 17.6174C16.2331 17.6171 16.2335 17.6172 16.2335 17.6172C16.2336 17.6172 16.2336 17.6172 16.2337 17.6172C16.2338 17.6172 16.234 17.6173 16.2343 17.6174C16.2351 17.6178 16.2355 17.6183 16.2352 17.618L15.4988 18.2945ZM16.8492 18.4038C17.4083 17.9649 17.9166 17.4616 18.3604 16.9101L17.5814 16.2831C17.1848 16.7759 16.7306 17.2256 16.2317 17.6173L16.8492 18.4038ZM18.3603 16.9104C18.6919 16.4988 18.6441 15.901 18.2531 15.5461L17.581 16.2866C17.5817 16.2872 17.5814 16.2871 17.581 16.2863C17.5808 16.2858 17.5808 16.2856 17.5808 16.2856C17.5808 16.2856 17.5807 16.2852 17.581 16.2844C17.5813 16.2833 17.5818 16.2827 17.5816 16.2829L18.3603 16.9104ZM18.2524 15.5455C17.8165 15.1514 17.6509 14.4201 17.8769 13.8817L16.9548 13.4947C16.5625 14.4294 16.8347 15.6119 17.5817 16.2873L18.2524 15.5455ZM17.8771 13.8812C18.1175 13.3066 18.7001 12.9427 19.3274 12.9782L19.384 11.9798C18.3364 11.9204 17.3596 12.527 16.9546 13.4953L17.8771 13.8812ZM19.3263 12.9782C19.8548 13.0093 20.3141 12.6216 20.3745 12.0976L19.3811 11.9832C19.381 11.9833 19.3812 11.9825 19.3817 11.9816C19.3819 11.9812 19.382 11.981 19.3821 11.9809C19.3822 11.9808 19.3822 11.9808 19.3823 11.9808C19.3823 11.9808 19.3823 11.9807 19.3825 11.9806C19.3826 11.9805 19.3829 11.9804 19.3833 11.9803C19.3844 11.9799 19.3853 11.9799 19.3851 11.9799L19.3263 12.9782ZM20.3744 12.0983C20.4165 11.7375 20.4375 11.3745 20.4375 11.0108H19.4375C19.4375 11.336 19.4187 11.6603 19.3811 11.9825L20.3744 12.0983ZM20.4375 11.0108C20.4375 10.6654 20.4185 10.3198 20.3801 9.97434L19.3862 10.0849C19.4205 10.3934 19.4375 10.7021 19.4375 11.0108H20.4375ZM20.3801 9.97478C20.3227 9.4544 19.8744 9.06999 19.3546 9.08741L19.3881 10.0868C19.3888 10.0868 19.3885 10.0869 19.3879 10.0867C19.3876 10.0866 19.3875 10.0865 19.3874 10.0865C19.3874 10.0864 19.3873 10.0864 19.3873 10.0864C19.3873 10.0864 19.387 10.0862 19.3867 10.0855C19.3862 10.0845 19.3861 10.0839 19.3862 10.0845L20.3801 9.97478ZM19.3532 9.08746C18.7324 9.10994 18.1595 8.73992 17.9281 8.17483L17.0027 8.55373C17.3944 9.51039 18.3537 10.1243 19.3894 10.0868L19.3532 9.08746ZM17.9283 8.17523C17.7081 7.63612 17.875 6.91251 18.3056 6.52052L17.6324 5.78103C16.8947 6.45253 16.6219 7.62139 17.0025 8.55334L17.9283 8.17523ZM18.3074 6.51885C18.6924 6.16486 18.7373 5.57994 18.4211 5.17056L17.6297 5.78191C17.6307 5.78315 17.6307 5.78352 17.6306 5.78326C17.6306 5.78323 17.6306 5.78329 17.6306 5.78339C17.6306 5.78347 17.6306 5.78321 17.6308 5.78269C17.6312 5.78191 17.6315 5.78187 17.6306 5.7827L18.3074 6.51885ZM18.4188 5.1677C17.9759 4.60288 17.4681 4.08941 16.9089 3.64044L16.2828 4.4202C16.7825 4.82135 17.2363 5.28025 17.6319 5.78477L18.4188 5.1677ZM16.9095 3.64093C16.4986 3.30993 15.9004 3.35647 15.5459 3.74825L16.2874 4.4192C16.2876 4.41901 16.287 4.41959 16.286 4.42009C16.2852 4.42045 16.2848 4.42048 16.2848 4.42048C16.2847 4.42049 16.2844 4.42052 16.2836 4.4203C16.2826 4.41999 16.282 4.41953 16.2822 4.41971L16.9095 3.64093ZM15.546 3.7481C15.1238 4.21433 14.4532 4.36378 13.8805 4.12343L13.4935 5.04552C14.4609 5.45151 15.5828 5.19724 16.2872 4.41935L15.546 3.7481ZM13.8803 4.12334C13.3425 3.89795 12.9754 3.34606 12.9754 2.75606H11.9754C11.9754 3.74619 12.5818 4.66337 13.4937 5.04561L13.8803 4.12334ZM12.9753 2.76899L12.9781 2.65845L11.9785 2.63259L11.9756 2.74313L12.9753 2.76899ZM12.9767 2.686C13.0201 2.15124 12.6172 1.68743 12.0991 1.62591L11.9812 2.61894C11.9799 2.61879 11.9792 2.6184 11.9794 2.61851C11.9799 2.61878 11.9809 2.6195 11.9818 2.62055C11.9827 2.62161 11.9822 2.62145 11.9814 2.61906C11.9804 2.61628 11.9794 2.61127 11.9799 2.60504L12.9767 2.686ZM12.0983 1.62582C11.3879 1.54258 10.6662 1.54167 9.95203 1.62153L10.0632 2.61533C10.7028 2.54381 11.3482 2.54477 11.982 2.61903L12.0983 1.62582Z" />
        <circle cx="11" cy="11" r="3.625" fill="none" stroke={props.fill} />
      </>
    ),
  },
  share: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M8 3.5c-3.92.44-8 3.125-8 10 2.312-5.062 4.75-6 8-6V11L13.5 5.5 8 0v3.5z",
    }),
    viewBox: "0 0 14 16",
    width: 14,
  },
  "share-lowprofile": {
    height: 14,
    width: 16,
    viewBox: "0 0 16 14",
    pathSolid: (
      <path d="M10.0929 2.11869C10.2403 2.05987 10.4117 2.09292 10.5255 2.20035L14.6572 6.15439C14.8123 6.30119 14.8123 6.5418 14.6572 6.68909L10.5249 10.6142C10.4494 10.6871 10.3474 10.7255 10.2448 10.7255C10.1928 10.7255 10.1413 10.7153 10.0923 10.6968C9.94389 10.638 9.85513 10.5005 9.85513 10.3474L9.84611 8.24639C4.76823 8.24639 0.89258 8.82238 1.23076 13.1695C-0.911826 5.92646 4.86311 4.55409 9.84611 4.55409L9.85573 2.4677C9.85625 2.31458 9.94449 2.17702 10.0929 2.11869Z" />
    ),
  },
  shield: {
    height: 8,
    pathSolid: (
      <path d="M3.99999 10.8002C7.41563 9.28707 8 8.31755 8 7.35116C8 6.16348 8 2.63941 8 2.63941C5 3.5 3.99999 0.5 3.99999 0.5C3.99999 0.5 3 3.5 0 2.63944V7.35119C0 8.31755 0.584376 9.28707 3.99999 10.8002Z" />
    ),
    viewBox: "0 0 8 11",
    width: 11,
  },
  "sign-in": {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M7 6.75V12h4V8h1v4c0 .55-.45 1-1 1H7v3l-5.45-2.72c-.33-.17-.55-.52-.55-.91V1c0-.55.45-1 1-1h9c.55 0 1 .45 1 1v3h-1V1H3l4 2v2.25L10 3v2h4v2h-4v2L7 6.75z",
    }),
    viewBox: "0 0 14 16",
    width: 14,
  },
  "sign-out": {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M11.992 8.994V6.996H7.995v-2h3.997V2.999l3.998 2.998-3.998 2.998zm-1.998 2.998H5.996V2.998L2 1h7.995v2.998h1V1c0-.55-.45-.999-1-.999H.999A1.001 1.001 0 0 0 0 1v11.372c0 .39.22.73.55.91L5.996 16v-3.008h3.998c.55 0 1-.45 1-1V7.996h-1v3.998z",
    }),
    viewBox: "0 0 16 16",
    width: 16,
  },
  simulation: {
    height: 16,
    width: 16,
    viewBox: "0 0 16 16",
    pathSolid: (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.0004 1.5C8.0004 1.22386 8.22425 1 8.50039 1H13.5004C13.7765 1 14.0004 1.22386 14.0004 1.5C14.0004 1.77614 13.7765 2 13.5004 2H13.0004V5.1944C14.6906 5.95029 16.0001 7.65198 16.0001 9.63019C16.0001 12.3107 13.8365 14.4836 11.1677 14.4836C10.5701 14.4836 9.99785 14.3746 9.46955 14.1754C9.63787 13.843 9.68502 13.4472 9.56494 13.0607C10.0537 13.2848 10.5969 13.4097 11.1689 13.4097C13.3008 13.4097 15.0349 11.6744 15.0349 9.54101C15.0349 8.58919 14.6846 7.69173 14.083 7.00001H8.25494C7.87192 7.44054 7.59072 7.96451 7.43699 8.53282L6.85059 7.29485C7.30068 6.36872 8.03346 5.62681 9.00039 5.1944V2H8.50039C8.22425 2 8.0004 1.77614 8.0004 1.5ZM2 3.5C2 3.22386 2.22386 3 2.5 3H6.5C6.77614 3 7 3.22386 7 3.5C7 3.77614 6.77614 4 6.5 4H5.99953V7L8.71642 12.885C9.25721 13.825 8.58132 15 7.5 15H1.49961C0.418171 15 -0.257593 13.825 0.283067 12.885L2.99954 7V4H2.5C2.22386 4 2 3.77614 2 3.5ZM6 10L7.5 13C7.5 13 8 14 7 14H2C1 14 1.5 13 1.5 13C1.5 13 2.66663 10.8333 3 10H6ZM9.5 9C9.77614 9 10 8.77614 10 8.5C10 8.22386 9.77614 8 9.5 8C9.22386 8 9 8.22386 9 8.5C9 8.77614 9.22386 9 9.5 9ZM11 10.5C11 10.7761 10.7761 11 10.5 11C10.2239 11 10 10.7761 10 10.5C10 10.2239 10.2239 10 10.5 10C10.7761 10 11 10.2239 11 10.5ZM3.5 13C3.77614 13 4 12.7761 4 12.5C4 12.2239 3.77614 12 3.5 12C3.22386 12 3 12.2239 3 12.5C3 12.7761 3.22386 13 3.5 13Z"
      />
    ),
  },
  skyscraper: {
    height: 22,
    width: 22,
    viewBox: "0 0 22 22",
    pathSolid: (
      <path d="M7.95639 22V17.9156H14.0445V22H18.6326V0H3.36755V22H7.95639ZM14.0445 2.02103H16.6123V4.58883H14.0445V2.02103ZM14.0445 5.75393H16.6123V8.32243H14.0445V5.75393ZM14.0445 9.48753H16.6123V12.0546H14.0445V9.48753ZM14.0445 13.2197H16.6123V15.7889H14.0445V13.2197ZM9.71689 2.02103H12.2854V4.58883H9.71689V2.02103ZM9.71689 5.75393H12.2854V8.32243H9.71689V5.75393ZM9.71689 9.48753H12.2854V12.0546H9.71689V9.48753ZM9.71689 13.2197H12.2854V15.7889H9.71689V13.2197ZM5.38928 2.02103H7.95708V4.58883H5.38928V2.02103ZM5.38928 5.75393H7.95708V8.32243H5.38928V5.75393ZM5.38928 9.48753H7.95708V12.0546H5.38928V9.48753ZM5.38928 13.2197H7.95708V15.7889H5.38928V13.2197Z" />
    ),
  },
  sort: {
    height: 22,
    width: 22,
    viewBox: "0 0 22 22",
    pathSolid: (
      <>
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M6 1.5C6.27614 1.5 6.5 1.72386 6.5 2L6.5 18.5C6.5 18.7761 6.27614 19 6 19C5.72386 19 5.5 18.7761 5.5 18.5L5.5 2C5.5 1.72386 5.72386 1.5 6 1.5Z"
          fill="#003E6B"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M1.16552 14.1284C1.37078 13.9436 1.68692 13.9603 1.87165 14.1655L6 18.7526L10.1284 14.1655C10.3131 13.9603 10.6292 13.9436 10.8345 14.1284C11.0397 14.3131 11.0564 14.6292 10.8717 14.8345L6.37165 19.8345C6.27683 19.9398 6.14175 20 6 20C5.85826 20 5.72318 19.9398 5.62836 19.8345L1.12836 14.8345C0.943628 14.6292 0.960267 14.3131 1.16552 14.1284Z"
          fill="#003E6B"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M15 19.5C14.7239 19.5 14.5 19.2761 14.5 19L14.5 2.5C14.5 2.22386 14.7239 2 15 2C15.2761 2 15.5 2.22386 15.5 2.5L15.5 19C15.5 19.2761 15.2761 19.5 15 19.5Z"
          fill="#003E6B"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M19.8345 6.87164C19.6292 7.05637 19.3131 7.03973 19.1283 6.83448L15 2.24742L10.8716 6.83448C10.6869 7.03973 10.3708 7.05637 10.1655 6.87164C9.96026 6.68691 9.94362 6.37077 10.1283 6.16551L14.6283 1.16551C14.7232 1.06016 14.8583 0.999995 15 0.999995C15.1417 0.999995 15.2768 1.06016 15.3716 1.16551L19.8716 6.16551C20.0564 6.37077 20.0397 6.68691 19.8345 6.87164Z"
          fill="#003E6B"
        />
      </>
    ),
  },
  star: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M14 6l-4.9-.64L7 1 4.9 5.36 0 6l3.6 3.26L2.67 14 7 11.67 11.33 14l-.93-4.74L14 6z",
    }),
    viewBox: "0 0 14 16",
    width: 14,
  },
  stop: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M10 1H4L0 5v6l4 4h6l4-4V5l-4-4zm3 9.5L9.5 14h-5L1 10.5v-5L4.5 2h5L13 5.5v5zM6 4h2v5H6V4zm0 6h2v2H6v-2z",
    }),
    viewBox: "0 0 14 16",
    width: 14,
  },
  subject: {
    height: 16,
    width: 16,
    viewBox: "0 0 16 16",
    pathSolid: (
      <>
        <path
          fillRule="evenodd"
          d="M13.6133 3.80834L11.2004 1.39545L12.303 0.292893C12.6935 -0.0976313 13.3267 -0.097631 13.7172 0.292893L14.7159 1.29157C15.1064 1.68209 15.1064 2.31526 14.7159 2.70578L13.6133 3.80834ZM6.50185 10.9205L4.08896 8.50762L10.4529 2.14366L12.8658 4.55655L6.50185 10.9205ZM5.77162 11.6512L3.35873 9.2383L2.04147 12.3119C1.86367 12.7268 2.28314 13.1462 2.698 12.9685L5.77162 11.6512Z"
        />
        <rect x="1" y="14" width="14" height="1" rx="0.5" />
      </>
    ),
  },
  subjects: {
    viewBox: "0 0 22 22",
    width: 22,
    height: 22,
    pathSolid: (
      <>
        <path
          d="M6.00227 16.7143C6.2605 16.7143 6.47619 16.6269 6.64935 16.4523C6.82251 16.2776 6.90909 16.0611 6.90909 15.8029C6.90909 15.5447 6.82175 15.329 6.64708 15.1558C6.4724 14.9827 6.25595 14.8961 5.99773 14.8961C5.7395 14.8961 5.52381 14.9834 5.35065 15.1581C5.17749 15.3328 5.09091 15.5492 5.09091 15.8075C5.09091 16.0657 5.17825 16.2814 5.35292 16.4545C5.5276 16.6277 5.74405 16.7143 6.00227 16.7143ZM6.00227 11.9091C6.2605 11.9091 6.47619 11.8218 6.64935 11.6471C6.82251 11.4724 6.90909 11.256 6.90909 10.9977C6.90909 10.7395 6.82175 10.5238 6.64708 10.3506C6.4724 10.1775 6.25595 10.0909 5.99773 10.0909C5.7395 10.0909 5.52381 10.1782 5.35065 10.3529C5.17749 10.5276 5.09091 10.744 5.09091 11.0023C5.09091 11.2605 5.17825 11.4762 5.35292 11.6494C5.5276 11.8225 5.74405 11.9091 6.00227 11.9091ZM6.00227 7.1039C6.2605 7.1039 6.47619 7.01656 6.64935 6.84188C6.82251 6.66721 6.90909 6.45076 6.90909 6.19253C6.90909 5.93431 6.82175 5.71861 6.64708 5.54545C6.4724 5.37229 6.25595 5.28571 5.99773 5.28571C5.7395 5.28571 5.52381 5.37305 5.35065 5.54773C5.17749 5.7224 5.09091 5.93885 5.09091 6.19708C5.09091 6.4553 5.17825 6.671 5.35292 6.84416C5.5276 7.01732 5.74405 7.1039 6.00227 7.1039ZM10.026 16.2597H16.7792V15.3506H10.026V16.2597ZM10.026 11.4545H16.7792V10.5455H10.026V11.4545ZM10.026 6.64935H16.7792V5.74026H10.026V6.64935ZM2.94805 21C2.38528 21 1.91991 20.816 1.55195 20.4481C1.18398 20.0801 1 19.6147 1 19.0519V2.94805C1 2.38528 1.18398 1.91991 1.55195 1.55195C1.91991 1.18398 2.38528 1 2.94805 1H19.0519C19.6147 1 20.0801 1.18398 20.4481 1.55195C20.816 1.91991 21 2.38528 21 2.94805V19.0519C21 19.6147 20.816 20.0801 20.4481 20.4481C20.0801 20.816 19.6147 21 19.0519 21H2.94805ZM2.94805 20.0909H19.0519C19.3117 20.0909 19.5498 19.9827 19.7662 19.7662C19.9827 19.5498 20.0909 19.3117 20.0909 19.0519V2.94805C20.0909 2.68831 19.9827 2.45022 19.7662 2.23377C19.5498 2.01732 19.3117 1.90909 19.0519 1.90909H2.94805C2.68831 1.90909 2.45022 2.01732 2.23377 2.23377C2.01732 2.45022 1.90909 2.68831 1.90909 2.94805V19.0519C1.90909 19.3117 2.01732 19.5498 2.23377 19.7662C2.45022 19.9827 2.68831 20.0909 2.94805 20.0909Z"
          fill="#003E6B"
        />
      </>
    ),
  },
  "subjects-bold": {
    height: 18,
    width: 18,
    fill: "none",
    viewBox: "0 0 18 18",
    pathSolid: () => {
      const maskId1 = uuid4();
      return (
        <>
          <mask
            id={maskId1}
            style={{ maskType: "alpha" }}
            width="18"
            height="18"
            x="0"
            y="0"
            maskUnits="userSpaceOnUse"
          >
            <path fill="#D9D9D9" d="M0 0H18V18H0z"></path>
          </mask>
          <g mask={`url(#${maskId1})`}>
            <path
              fill="#003E6B"
              d="M3.75 15.75c-.413 0-.766-.147-1.06-.44a1.445 1.445 0 01-.44-1.06V3.75c0-.413.147-.766.44-1.06.294-.293.647-.44 1.06-.44h10.5c.412 0 .766.147 1.06.44.293.294.44.647.44 1.06v10.5c0 .412-.147.766-.44 1.06-.294.293-.648.44-1.06.44H3.75zm1.5-3h5.25v-1.5H5.25v1.5zm0-3h7.5v-1.5h-7.5v1.5zm0-3h7.5v-1.5h-7.5v1.5z"
            ></path>
          </g>
        </>
      );
    },
  },
  success: {
    height: 125,
    width: 109,
    viewBox: "0 0 109 125",
    pathSolid: (
      <>
        <path d="M54 0L108 31V93L54 124L0 93V31L54 0Z" fill="#84BE60" />
        <path
          d="M54 0L54 30.281V92.7603V124L0 93L-7.62939e-06 31L54 0Z"
          fill="#629941"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M82.0002 41C83.6771 42.4382 83.8707 44.9634 82.4325 46.6403L49.8738 84.604C49.1823 85.4103 48.1986 85.9084 47.1394 85.9886C46.0803 86.0688 45.0328 85.7244 44.2278 85.0315L26.4221 69.7036C24.7479 68.2624 24.559 65.7368 26.0002 64.0625C27.4415 62.3883 29.9671 62.1994 31.6414 63.6407L46.41 76.3541L76.3599 41.4323C77.7981 39.7554 80.3233 39.5619 82.0002 41Z"
          fill="white"
          stroke="white"
          stroke-width="2"
        />
      </>
    ),
  },
  sync: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M10.24 7.4a4.15 4.15 0 0 1-1.2 3.6 4.346 4.346 0 0 1-5.41.54L4.8 10.4.5 9.8l.6 4.2 1.31-1.26c2.36 1.74 5.7 1.57 7.84-.54a5.876 5.876 0 0 0 1.74-4.46l-1.75-.34zM2.96 5a4.346 4.346 0 0 1 5.41-.54L7.2 5.6l4.3.6-.6-4.2-1.31 1.26c-2.36-1.74-5.7-1.57-7.85.54C.5 5.03-.06 6.65.01 8.26l1.75.35A4.17 4.17 0 0 1 2.96 5z",
    }),
    viewBox: "0 0 12 16",
    width: 12,
  },
  tag: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M7.685 1.72a2.49 2.49 0 0 0-1.76-.726H3.48A2.5 2.5 0 0 0 .994 3.48v2.456c0 .656.269 1.292.726 1.76l6.024 6.024a.99.99 0 0 0 1.402 0l4.563-4.563a.99.99 0 0 0 0-1.402L7.685 1.72zM2.366 7.048A1.54 1.54 0 0 1 1.9 5.925V3.48c0-.874.716-1.58 1.58-1.58h2.456c.418 0 .825.159 1.123.467l6.104 6.094-4.702 4.702-6.094-6.114zm.626-4.066h1.989v1.989H2.982V2.982h.01z",
    }),
    viewBox: "0 0 14 16",
    width: 14,
  },
  "teacher-hat": {
    width: 22,
    height: 22,
    viewBox: "0 0 22 22",
    pathSolid: (
      <>
        <g clip-path="url(#clip0_7153_36168)">
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M11.3225 4.54284C11.4483 4.4871 11.5916 4.4857 11.7185 4.53895L21.6935 8.72495C21.8809 8.80361 22.0021 8.98794 22 9.19122C21.9979 9.39449 21.8729 9.57625 21.6838 9.65098L19.375 10.5637V15.5C19.375 15.7 19.2558 15.8808 19.072 15.9596L11.722 19.1096C11.5868 19.1675 11.4329 19.163 11.3014 19.0972L5.00139 15.9472C4.832 15.8625 4.725 15.6894 4.725 15.5V10.8336L2.575 9.93643V13.8103L3.95567 16.8668C4.02557 17.0216 4.01216 17.2012 3.92007 17.3439C3.82798 17.4865 3.66979 17.5727 3.5 17.5727H0.500001C0.326764 17.5727 0.165872 17.483 0.074753 17.3357C-0.0163663 17.1883 -0.0247272 17.0043 0.0526547 16.8493L1.575 13.8001V9.186C1.575 8.98818 1.69163 8.80896 1.8725 8.72884L11.3225 4.54284ZM2.05242 15.0826L1.30848 16.5727H2.7255L2.05242 15.0826ZM3.3405 9.17229L11.5301 12.5897L20.1749 9.17218L11.5304 5.54449L3.3405 9.17229ZM5.725 11.2509V15.191L11.5412 18.0991L18.375 15.1703V10.959L11.7088 13.5943C11.5877 13.6422 11.4527 13.6409 11.3324 13.5908L5.725 11.2509Z"
            fill="#003E6B"
          />
        </g>
        <defs>
          <clipPath id="clip0_7153_36168">
            <rect width="22" height="22" fill="white" />
          </clipPath>
        </defs>
      </>
    ),
  },
  "teacher-hat-bold": {
    height: 18,
    width: 18,
    fill: "none",
    viewBox: "0 0 18 18",
    pathSolid: () => {
      const maskId1 = uuid4();
      return (
        <>
          <mask
            id={maskId1}
            style={{ maskType: "alpha" }}
            width="18"
            height="18"
            x="0"
            y="0"
            maskUnits="userSpaceOnUse"
          >
            <path fill="#D9D9D9" d="M0 0H18V18H0z"></path>
          </mask>
          <g mask={`url(#${maskId1})`}>
            <path
              fill="#003E6B"
              d="M15.75 12.75V7.575L9 11.25.75 6.75 9 2.25l8.25 4.5v6h-1.5zm-6.75 3L3.75 12.9V9.15L9 12l5.25-2.85v3.75L9 15.75z"
            ></path>
          </g>
        </>
      );
    },
  },
  "teacher-hat-white": {
    width: 22,
    height: 22,
    viewBox: "0 0 22 22",
    pathSolid: (
      <>
        <g clip-path="url(#clip0_7153_36170)">
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M11.3225 4.54284C11.4483 4.4871 11.5916 4.4857 11.7185 4.53895L21.6935 8.72495C21.8809 8.80361 22.0021 8.98794 22 9.19122C21.9979 9.39449 21.8729 9.57625 21.6838 9.65098L19.375 10.5637V15.5C19.375 15.7 19.2558 15.8808 19.072 15.9596L11.722 19.1096C11.5868 19.1675 11.4329 19.163 11.3014 19.0972L5.00139 15.9472C4.832 15.8625 4.725 15.6894 4.725 15.5V10.8336L2.575 9.93643V13.8103L3.95567 16.8668C4.02557 17.0216 4.01216 17.2012 3.92007 17.3439C3.82798 17.4865 3.66979 17.5727 3.5 17.5727H0.500001C0.326764 17.5727 0.165872 17.483 0.074753 17.3357C-0.0163663 17.1883 -0.0247272 17.0043 0.0526547 16.8493L1.575 13.8001V9.186C1.575 8.98818 1.69163 8.80896 1.8725 8.72884L11.3225 4.54284ZM2.05242 15.0826L1.30848 16.5727H2.7255L2.05242 15.0826ZM3.3405 9.17229L11.5301 12.5897L20.1749 9.17218L11.5304 5.54449L3.3405 9.17229ZM5.725 11.2509V15.191L11.5412 18.0991L18.375 15.1703V10.959L11.7088 13.5943C11.5877 13.6422 11.4527 13.6409 11.3324 13.5908L5.725 11.2509Z"
            fill="white"
          />
        </g>
        <defs>
          <clipPath id="clip0_7153_36170">
            <rect width="22" height="22" fill="white" />
          </clipPath>
        </defs>
      </>
    ),
  },
  "teaching-guide": {
    height: 16,
    width: 16,
    viewBox: "0 0 16 16",
    pathSolid: () => {
      const maskId = uuid4();
      return (
        <>
          <mask id={maskId} fill="none">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M14 3H2V11H5H6H7.5H8.5H10H11H14V3ZM5.5 12H7.5V14H8.5V12H10.5L12 15H13L11.5 12H15V2H1V12H4.5L3 15H4L5.5 12ZM3 4H13V10H3V4ZM4 9H4.99509L5.2766 8.19412H6.67758L6.95908 9H8L6.50737 5H5.49263L4 9ZM5.51882 7.45882L5.97709 6L6.43535 7.45882H5.51882Z"
            />
          </mask>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M14 3H2V11H5H6H7.5H8.5H10H11H14V3ZM5.5 12H7.5V14H8.5V12H10.5L12 15H13L11.5 12H15V2H1V12H4.5L3 15H4L5.5 12ZM3 4H13V10H3V4ZM4 9H4.99509L5.2766 8.19412H6.67758L6.95908 9H8L6.50737 5H5.49263L4 9ZM5.51882 7.45882L5.97709 6L6.43535 7.45882H5.51882Z"
          />
          <path
            mask={`url(#${maskId})`}
            d="M2 3V2H1V3H2ZM14 3H15V2H14V3ZM2 11H1V12H2V11ZM14 11V12H15V11H14ZM7.5 12H8.5V11H7.5V12ZM5.5 12V11H4.88197L4.60557 11.5528L5.5 12ZM7.5 14H6.5V15H7.5V14ZM8.5 14V15H9.5V14H8.5ZM8.5 12V11H7.5V12H8.5ZM10.5 12L11.3944 11.5528L11.118 11H10.5V12ZM12 15L11.1056 15.4472L11.382 16H12V15ZM13 15V16H14.618L13.8944 14.5528L13 15ZM11.5 12V11H9.88197L10.6056 12.4472L11.5 12ZM15 12V13H16V12H15ZM15 2H16V1H15V2ZM1 2V1H0V2H1ZM1 12H0V13H1V12ZM4.5 12L5.39443 12.4472L6.11803 11H4.5V12ZM3 15L2.10557 14.5528L1.38197 16H3V15ZM4 15V16H4.61803L4.89443 15.4472L4 15ZM13 4H14V3H13V4ZM3 4V3H2V4H3ZM13 10V11H14V10H13ZM3 10H2V11H3V10ZM4.99509 9V10H5.70503L5.93915 9.32977L4.99509 9ZM4 9L3.0631 8.65039L2.55949 10H4V9ZM5.2766 8.19412V7.19412H4.56666L4.33254 7.86434L5.2766 8.19412ZM6.67758 8.19412L7.62164 7.86434L7.38752 7.19412H6.67758V8.19412ZM6.95908 9L6.01502 9.32977L6.24914 10H6.95908V9ZM8 9V10H9.44051L8.93689 8.65039L8 9ZM6.50737 5L7.44426 4.65039L7.20156 4H6.50737V5ZM5.49263 5V4H4.79844L4.55574 4.65039L5.49263 5ZM5.97709 6L6.93112 5.70031L5.97709 2.66327L5.02305 5.70031L5.97709 6ZM5.51882 7.45882L4.56479 7.15913L4.15651 8.45882H5.51882V7.45882ZM6.43535 7.45882V8.45882H7.79766L7.38939 7.15913L6.43535 7.45882ZM2 4H14V2H2V4ZM3 11V3H1V11H3ZM5 10H2V12H5V10ZM6 10H5V12H6V10ZM7.5 10H6V12H7.5V10ZM8.5 10H7.5V12H8.5V10ZM10 10H8.5V12H10V10ZM11 10H10V12H11V10ZM14 10H11V12H14V10ZM13 3V11H15V3H13ZM7.5 11H5.5V13H7.5V11ZM8.5 14V12H6.5V14H8.5ZM8.5 13H7.5V15H8.5V13ZM7.5 12V14H9.5V12H7.5ZM10.5 11H8.5V13H10.5V11ZM12.8944 14.5528L11.3944 11.5528L9.60557 12.4472L11.1056 15.4472L12.8944 14.5528ZM13 14H12V16H13V14ZM10.6056 12.4472L12.1056 15.4472L13.8944 14.5528L12.3944 11.5528L10.6056 12.4472ZM15 11H11.5V13H15V11ZM14 2V12H16V2H14ZM1 3H15V1H1V3ZM2 12V2H0V12H2ZM4.5 11H1V13H4.5V11ZM3.89443 15.4472L5.39443 12.4472L3.60557 11.5528L2.10557 14.5528L3.89443 15.4472ZM4 14H3V16H4V14ZM4.60557 11.5528L3.10557 14.5528L4.89443 15.4472L6.39443 12.4472L4.60557 11.5528ZM13 3H3V5H13V3ZM14 10V4H12V10H14ZM3 11H13V9H3V11ZM2 4V10H4V4H2ZM4.99509 8H4V10H4.99509V8ZM4.33254 7.86434L4.05103 8.67023L5.93915 9.32977L6.22066 8.52389L4.33254 7.86434ZM6.67758 7.19412H5.2766V9.19412H6.67758V7.19412ZM7.90314 8.67023L7.62164 7.86434L5.73352 8.52389L6.01502 9.32977L7.90314 8.67023ZM8 8H6.95908V10H8V8ZM5.57047 5.34961L7.06311 9.34961L8.93689 8.65039L7.44426 4.65039L5.57047 5.34961ZM5.49263 6H6.50737V4H5.49263V6ZM4.9369 9.34961L6.42953 5.34961L4.55574 4.65039L3.0631 8.65039L4.9369 9.34961ZM5.02305 5.70031L4.56479 7.15913L6.47286 7.75852L6.93112 6.29969L5.02305 5.70031ZM7.38939 7.15913L6.93112 5.70031L5.02305 6.29969L5.48132 7.75852L7.38939 7.15913ZM5.51882 8.45882H6.43535V6.45882H5.51882V8.45882Z"
          />
        </>
      );
    },
  },
  text: {
    height: 16,
    width: 16,
    viewBox: "0 0 16 16",
    pathSolid: (
      <>
        <rect x="2" y="2" width="12" height="3" rx="1" />
        <rect x="5" y="13" width="6" height="2" rx="1" />
        <rect x="6" y="13" width="9" height="4" transform="rotate(-90 6 13)" />
        <rect
          x="2"
          y="7"
          width="5"
          height="2"
          rx="1"
          transform="rotate(-90 2 7)"
        />
        <rect
          x="12"
          y="7"
          width="5"
          height="2"
          rx="1"
          transform="rotate(-90 12 7)"
        />
      </>
    ),
  },
  "text-size": {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M13.62 9.08L12.1 3.66h-.06l-1.5 5.42h3.08zM5.7 10.13S4.68 6.52 4.53 6.02h-.08l-1.13 4.11H5.7zM17.31 14h-2.25l-.95-3.25h-4.07L9.09 14H6.84l-.69-2.33H2.87L2.17 14H0l3.3-9.59h2.5l2.17 6.34L10.86 2h2.52l3.94 12h-.01z",
    }),
    viewBox: "0 0 18 16",
    width: 18,
  },
  textbook: {
    height: 29,
    width: 29,
    viewBox: "0 0 29 29",
    pathSolid: (
      <>
        <circle cx="14.5" cy="14.5" r="14" fill="white" stroke="#003E6B" />
        <path
          d="M7 7C7 6.44772 7.44772 6 8 6H21C21.5523 6 22 6.44772 22 7V23C22 23.5523 21.5523 24 21 24H9C7.89543 24 7 23.1046 7 22V7Z"
          fill="#65081F"
        />
        <rect x="8" y="6" width="12" height="17" rx="1" fill="#F7F7F7" />
        <path
          d="M14 9C14 7.89543 14.8954 7 16 7H20C20.5523 7 21 7.44772 21 8V22C21 22.5523 20.5523 23 20 23H14V9Z"
          fill="#E4E4E4"
        />
        <rect x="7" y="5" width="13" height="17" rx="1" fill="#A41034" />
        <path
          d="M14 5H19C19.5523 5 20 5.44772 20 6V21C20 21.5523 19.5523 22 19 22H14V5Z"
          fill="#7F001F"
        />
        <rect x="10" y="11" width="7" height="2" rx="1" fill="white" />
        <rect x="11" y="14" width="5" height="1" rx="0.5" fill="white" />
      </>
    ),
  },
  "thumbs-down": {
    height: 20,
    width: 20,
    fill: "none",
    viewBox: "0 0 20 20",
    pathSolid: () => {
      const maskId1 = uuid4();
      return (
        <>
          <mask
            id={maskId1}
            style={{ maskType: "alpha" }}
            width="20"
            height="20"
            x="-1"
            y="-1"
            maskUnits="userSpaceOnUse"
          >
            <path
              fill="#D9D9D9"
              stroke="#003E6B"
              strokeLinejoin="round"
              strokeWidth="1.3"
              d="M0 0H18V18H0z"
            />
          </mask>
          <g mask={`url(#${maskId1})`}>
            <path
              fill="#F5F7F9"
              d="M4.5 2.7h8.55v9l-5.4 5.4-.6-.413a1.452 1.452 0 01-.497-.619 1.205 1.205 0 01-.065-.768l.712-3.6H2.25c-.375 0-.694-.131-.956-.394A1.302 1.302 0 01.9 10.35V9.28a1.211 1.211 0 01.113-.525l2.23-5.231c.1-.25.266-.45.498-.6.23-.15.484-.225.759-.225zm8.55 9v-9h4.05v9h-4.05z"
            />
            <path
              fill="#003E6B"
              d="M7.65 17.1l-.368.535a.65.65 0 00.828-.076l-.46-.46zm-.6-.413l-.38.527a.372.372 0 00.012.009l.368-.536zM6.488 15.3l.632.15a.661.661 0 00.005-.024l-.637-.126zm.712-3.6l.638.126a.65.65 0 00-.638-.776v.65zM1.013 8.756l.597.256v-.001l-.597-.255zm2.23-5.231l.599.255a.599.599 0 00.005-.014l-.603-.241zM17.1 2.7h.65a.65.65 0 00-.65-.65v.65zm0 9v.65a.65.65 0 00.65-.65h-.65zM4.5 3.35h8.55v-1.3H4.5v1.3zm7.9-.65v9h1.3v-9h-1.3zm.19 8.54l-5.4 5.4.92.92 5.4-5.4-.92-.92zm-4.572 5.324l-.6-.412-.736 1.07.6.413.736-1.07zm-.587-.404a.803.803 0 01-.28-.346l-1.196.509c.154.361.396.66.714.891l.762-1.054zm-.28-.346a.555.555 0 01-.031-.364l-1.265-.3c-.095.398-.06.796.1 1.173l1.196-.509zm-.026-.388l.713-3.6-1.276-.253-.712 3.6 1.275.253zM7.2 11.05H2.25v1.3H7.2v-1.3zm-4.95 0a.653.653 0 01-.497-.204l-.919.92c.386.385.87.584 1.416.584v-1.3zm-.497-.204a.654.654 0 01-.203-.496H.25c0 .545.198 1.03.584 1.416l.92-.92zm-.203-.496V9.28H.25v1.069h1.3zm0-1.069c0-.06.006-.1.012-.126L.295 8.863a1.859 1.859 0 00-.045.418h1.3zm.012-.126a.856.856 0 01.048-.143L.415 8.5a2.15 2.15 0 00-.12.363l1.267.292zm.048-.144L3.842 3.78l-1.196-.51L.415 8.5l1.195.511zm2.237-5.245a.609.609 0 01.247-.296l-.707-1.09a1.908 1.908 0 00-.747.903l1.207.483zm.247-.296a.718.718 0 01.406-.12v-1.3c-.4 0-.777.111-1.113.33l.707 1.09zm8.956-.12h4.05v-1.3h-4.05v1.3zm3.4-.65v9h1.3v-9h-1.3zm.65 8.35h-4.05v1.3h4.05v-1.3z"
            />
          </g>
        </>
      );
    },
  },
  "thumbs-up": {
    height: 22,
    width: 20,
    fill: "none",
    viewBox: "0 0 20 20",
    pathSolid: () => {
      const maskId1 = uuid4();
      return (
        <>
          <mask
            id={maskId1}
            style={{ maskType: "alpha" }}
            width="20"
            height="20"
            x="-1"
            y="-1"
            maskUnits="userSpaceOnUse"
          >
            <path
              fill="#D9D9D9"
              stroke="#003E6B"
              strokeLinejoin="round"
              strokeWidth="1.3"
              d="M18 18H36V36H18z"
              transform="rotate(-180 18 18)"
            />
          </mask>
          <g mask={`url(#${maskId1})`}>
            <path
              fill="#F5F7F9"
              d="M13.5 15.3H4.95v-9l5.4-5.4.6.413c.225.162.39.369.497.619.106.25.128.506.066.768L10.8 6.3h4.95c.375 0 .694.132.956.394.263.263.394.581.394.956V8.72a1.214 1.214 0 01-.113.525l-2.23 5.231c-.1.25-.266.45-.498.6-.23.15-.484.225-.759.225zm-8.55-9v9H.9v-9h4.05z"
            />
            <path
              fill="#003E6B"
              d="M10.35.9l.368-.535A.65.65 0 009.89.44l.46.46zm.6.413l.38-.527a.39.39 0 00-.012-.009l-.368.536zm.563 1.387l-.633-.15a.673.673 0 00-.005.024l.637.126zM10.8 6.3l-.638-.126a.65.65 0 00.638.776V6.3zm6.188 2.944l-.598-.256v.001l.598.255zm-2.232 5.231l-.598-.255a.552.552 0 00-.005.014l.603.241zM.9 15.3H.25a.65.65 0 00.65.65v-.65zm0-9v-.65a.65.65 0 00-.65.65H.9zm12.6 8.35H4.95v1.3h8.55v-1.3zm-7.9.65v-9H4.3v9h1.3zm-.19-8.54l5.4-5.4-.92-.92-5.4 5.4.92.92zm4.572-5.324l.6.412.736-1.07-.6-.413-.736 1.07zm.587.404c.132.094.22.207.28.346l1.196-.509a2.102 2.102 0 00-.714-.891l-.762 1.054zm.28.346a.555.555 0 01.031.364l1.265.3c.095-.398.06-.796-.1-1.173l-1.196.509zm.026.388l-.713 3.6 1.276.252.712-3.6-1.275-.252zM10.8 6.95h4.95v-1.3H10.8v1.3zm4.95 0c.205 0 .357.064.497.204l.919-.92a1.951 1.951 0 00-1.416-.584v1.3zm.497.204c.139.139.203.292.203.496h1.3c0-.545-.198-1.03-.584-1.416l-.92.92zm.203.496V8.72h1.3V7.65h-1.3zm0 1.069c0 .06-.006.1-.011.126l1.266.292c.032-.137.045-.278.045-.418h-1.3zm-.011.126a.86.86 0 01-.049.143l1.195.512c.05-.118.092-.24.12-.363l-1.266-.292zm-.05.144l-2.23 5.231 1.195.51 2.231-5.23-1.195-.511zm-2.236 5.245a.609.609 0 01-.247.296l.707 1.09c.341-.22.596-.525.747-.903l-1.207-.483zm-.247.296a.718.718 0 01-.406.12v1.3c.4 0 .777-.111 1.113-.33l-.707-1.09zm-8.956.12H.9v1.3h4.05v-1.3zm-3.4.65v-9H.25v9h1.3zM.9 6.95h4.05v-1.3H.9v1.3z"
            />
          </g>
        </>
      );
    },
  },
  "thumbs-up-fab": {
    height: 22,
    width: 20,
    fill: "none",
    viewBox: "0 0 20 20",
    pathSolid: () => {
      const maskId1 = uuid4();
      return (
        <>
          <mask
            id={maskId1}
            style={{ maskType: "alpha" }}
            width="20"
            height="20"
            x="-1"
            y="-1"
            maskUnits="userSpaceOnUse"
          >
            <path
              fill="#D9D9D9"
              stroke="#003E6B"
              strokeLinejoin="round"
              strokeWidth="1.3"
              d="M18 18H36V36H18z"
              transform="rotate(-180 18 18)"
            />
          </mask>
          <g mask={`url(#${maskId1})`}>
            <path
              fill="#333"
              d="M10.35.9l.368-.535A.65.65 0 009.89.44l.46.46zm.6.413l.38-.527a.39.39 0 00-.012-.009l-.368.536zm.563 1.387l-.633-.15a.673.673 0 00-.005.024l.637.126zM10.8 6.3l-.638-.126a.65.65 0 00.638.776V6.3zm6.188 2.944l-.598-.256v.001l.598.255zm-2.232 5.231l-.598-.255a.552.552 0 00-.005.014l.603.241zM.9 15.3H.25a.65.65 0 00.65.65v-.65zm0-9v-.65a.65.65 0 00-.65.65H.9zm12.6 8.35H4.95v1.3h8.55v-1.3zm-7.9.65v-9H4.3v9h1.3zm-.19-8.54l5.4-5.4-.92-.92-5.4 5.4.92.92zm4.572-5.324l.6.412.736-1.07-.6-.413-.736 1.07zm.587.404c.132.094.22.207.28.346l1.196-.509a2.102 2.102 0 00-.714-.891l-.762 1.054zm.28.346a.555.555 0 01.031.364l1.265.3c.095-.398.06-.796-.1-1.173l-1.196.509zm.026.388l-.713 3.6 1.276.252.712-3.6-1.275-.252zM10.8 6.95h4.95v-1.3H10.8v1.3zm4.95 0c.205 0 .357.064.497.204l.919-.92a1.951 1.951 0 00-1.416-.584v1.3zm.497.204c.139.139.203.292.203.496h1.3c0-.545-.198-1.03-.584-1.416l-.92.92zm.203.496V8.72h1.3V7.65h-1.3zm0 1.069c0 .06-.006.1-.011.126l1.266.292c.032-.137.045-.278.045-.418h-1.3zm-.011.126a.86.86 0 01-.049.143l1.195.512c.05-.118.092-.24.12-.363l-1.266-.292zm-.05.144l-2.23 5.231 1.195.51 2.231-5.23-1.195-.511zm-2.236 5.245a.609.609 0 01-.247.296l.707 1.09c.341-.22.596-.525.747-.903l-1.207-.483zm-.247.296a.718.718 0 01-.406.12v1.3c.4 0 .777-.111 1.113-.33l-.707-1.09zm-8.956.12H.9v1.3h4.05v-1.3zm-3.4.65v-9H.25v9h1.3zM.9 6.95h4.05v-1.3H.9v1.3z"
            />
          </g>
        </>
      );
    },
  },
  tools: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M4.48 7.27c.26.26 1.28 1.33 1.28 1.33l.56-.58-.88-.91 1.69-1.8s-.76-.74-.43-.45c.32-1.19.03-2.51-.87-3.44C4.93.5 3.66.2 2.52.51l1.93 2-.51 1.96-1.89.52-1.93-2C-.19 4.17.1 5.48 1 6.4c.94.98 2.29 1.26 3.48.87zm6.44 1.94l-2.33 2.3 3.84 3.98c.31.33.73.49 1.14.49.41 0 .82-.16 1.14-.49.63-.65.63-1.7 0-2.35l-3.79-3.93zM16 2.53L13.55 0 6.33 7.46l.88.91-4.31 4.46-.99.53-1.39 2.27.35.37 2.2-1.44.51-1.02L7.9 9.08l.88.91L16 2.53z",
    }),
    viewBox: "0 0 16 16",
    width: 16,
  },
  trashcan: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M11 2H9c0-.55-.45-1-1-1H5c-.55 0-1 .45-1 1H2c-.55 0-1 .45-1 1v1c0 .55.45 1 1 1v9c0 .55.45 1 1 1h7c.55 0 1-.45 1-1V5c.55 0 1-.45 1-1V3c0-.55-.45-1-1-1zm-1 12H3V5h1v8h1V5h1v8h1V5h1v8h1V5h1v9zm1-10H2V3h9v1z",
    }),
    viewBox: "0 0 12 16",
    width: 12,
  },
  "trashcan-navy": {
    height: 18,
    width: 18,
    pathSolid: (
      <path
        fillRule="evenodd"
        fill="#003E6B"
        clipRule="evenodd"
        d="M10 2a1 1 0 011 1H7a1 1 0 011-1h2zM6 3a2 2 0 012-2h2a2 2 0 012 2h3a1 1 0 011 1v1a1 1 0 01-1 1h-1v9a2 2 0 01-2 2H6a2 2 0 01-2-2V6H3a1 1 0 01-1-1V4a1 1 0 011-1h3zm6 1H3v1h12V4h-3zM5 6v9a1 1 0 001 1h6a1 1 0 001-1V6H5zm3.5 8V8a.5.5 0 011 0v6a.5.5 0 01-1 0zm3 0V8a.5.5 0 00-1 0v6a.5.5 0 001 0zM7 7.5a.5.5 0 01.5.5v6a.5.5 0 01-1 0V8a.5.5 0 01.5-.5z"
      />
    ),
    viewBox: "0 0 18 18",
  },
  "trashcan-small": {
    width: 12,
    height: 13,
    pathSolid: (
      <path
        fillRule="evenodd"
        d="M4 1.5C4 1.22386 4.22386 1 4.5 1H7.5C7.77614 1 8 1.22386 8 1.5V2.00024H4V1.5ZM3 2.00024V1.5C3 0.671572 3.67157 0 4.5 0H7.5C8.32843 0 9 0.671573 9 1.5V2.00024H11.5C11.7761 2.00024 12 2.2241 12 2.50024C12 2.77639 11.7761 3.00024 11.5 3.00024L11 3.00026L10.9348 3.96574C10.8938 4.57383 10.8471 5.28826 10.799 6.02658C10.7433 6.88013 10.6855 7.76561 10.6318 8.55561C10.5813 9.29947 10.5333 9.97504 10.4922 10.4886C10.4717 10.7449 10.4526 10.965 10.4354 11.1348C10.4211 11.2754 10.4022 11.4516 10.3734 11.5679C10.3001 11.8641 10.1503 12.1829 9.91023 12.4475C9.67133 12.7106 9.26098 13.0002 8.69237 13.0002L3.30597 13.0002C2.74465 13.0002 2.33096 12.7175 2.08704 12.4536C1.84016 12.1865 1.68087 11.855 1.61629 11.5289C1.59586 11.4258 1.57686 11.2142 1.55958 11.013C1.53957 10.7799 1.51518 10.4696 1.48817 10.1106C1.43412 9.39198 1.36889 8.46922 1.30602 7.55934C1.24312 6.64912 1.18248 5.7504 1.13757 5.07933C1.11511 4.74377 1.09658 4.46507 1.08367 4.27023L1 3.00024H0.5C0.223858 3.00024 0 2.77639 0 2.50024C0 2.2241 0.223858 2.00024 0.5 2.00024H3ZM8 5.13661C8 4.86047 7.77614 4.63661 7.5 4.63661C7.22386 4.63661 7 4.86047 7 5.13661V9.86388C7 10.14 7.22386 10.3639 7.5 10.3639C7.77614 10.3639 8 10.14 8 9.86388V5.13661ZM4.5 4.63661C4.22386 4.63661 4 4.86047 4 5.13661V9.86388C4 10.14 4.22386 10.3639 4.5 10.3639C4.77614 10.3639 5 10.14 5 9.86388V5.13661C5 4.86047 4.77614 4.63661 4.5 4.63661Z"
      />
    ),
    viewBox: "0 0 12 13",
  },
  "triangle-down": {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M0 5l6 6 6-6H0z",
    }),
    viewBox: "0 0 12 16",
    width: 12,
  },
  "triangle-left": {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M6 2L0 8l6 6V2z",
    }),
    viewBox: "0 0 6 16",
    width: 6,
  },
  "triangle-right": {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M0 14l6-6-6-6v12z",
    }),
    viewBox: "0 0 6 16",
    width: 6,
  },
  "triangle-up": {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M12 11L6 5l-6 6h12z",
    }),
    viewBox: "0 0 12 16",
    width: 12,
  },
  twitter: {
    height: 36,
    width: 36,
    viewBox: "0 0 36 36",
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M19.7142 16.6226L26.4162 9H24.8286L19.0068 15.6173L14.3604 9H9L16.0278 19.0074L9 26.9999H10.5876L16.7316 20.0104L21.6396 26.9999H27M11.1606 10.1714H13.5996L24.8274 25.8861H22.3878",
    }),
  },
  unmute: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M12 8.02c0 1.09-.45 2.09-1.17 2.83l-.67-.67c.55-.56.89-1.31.89-2.16 0-.85-.34-1.61-.89-2.16l.67-.67A3.99 3.99 0 0 1 12 8.02zM7.72 2.28L4 6H2c-.55 0-1 .45-1 1v2c0 .55.45 1 1 1h2l3.72 3.72c.47.47 1.28.14 1.28-.53V2.81c0-.67-.81-1-1.28-.53zm5.94.08l-.67.67a6.996 6.996 0 0 1 2.06 4.98c0 1.94-.78 3.7-2.06 4.98l.67.67A7.973 7.973 0 0 0 16 8c0-2.22-.89-4.22-2.34-5.66v.02zm-1.41 1.41l-.69.67a5.05 5.05 0 0 1 1.48 3.58c0 1.39-.56 2.66-1.48 3.56l.69.67A5.97 5.97 0 0 0 14 8.02c0-1.65-.67-3.16-1.75-4.25z",
    }),
    viewBox: "0 0 16 16",
    width: 16,
  },
  unverified: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M15.67 7.066l-1.08-1.34a1.5 1.5 0 0 1-.309-.77l-.19-1.698a1.51 1.51 0 0 0-1.329-1.33l-1.699-.19c-.3-.03-.56-.159-.78-.329L8.945.33a1.504 1.504 0 0 0-1.878 0l-1.34 1.08a1.5 1.5 0 0 1-.77.31l-1.698.19c-.7.08-1.25.63-1.33 1.329l-.19 1.699c-.03.3-.159.56-.329.78L.33 7.055a1.504 1.504 0 0 0 0 1.878l1.08 1.34c.17.22.28.48.31.77l.19 1.698c.08.7.63 1.25 1.329 1.33l1.699.19c.3.03.56.159.78.329l1.339 1.08c.55.439 1.329.439 1.878 0l1.34-1.08c.22-.17.48-.28.77-.31l1.698-.19c.7-.08 1.25-.63 1.33-1.329l.19-1.699c.03-.3.159-.56.329-.78l1.08-1.339a1.504 1.504 0 0 0 0-1.878zm-6.666 4.437c0 .28-.22.5-.5.5h-.999c-.27 0-.5-.22-.5-.5v-1c0-.28.23-.5.5-.5h1c.28 0 .5.22.5.5v1zm1.56-4.887c-.06.17-.17.33-.3.47-.13.16-.14.19-.33.38-.16.17-.31.3-.52.45-.11.089-.2.189-.28.269-.08.08-.14.17-.19.27-.05.1-.08.19-.11.3-.03.11-.03.13-.03.25H7.136c0-.22 0-.31.03-.48.03-.19.08-.36.14-.52.06-.14.14-.28.25-.42.11-.13.23-.25.409-.38.27-.19.36-.3.48-.52.12-.219.2-.379.2-.589 0-.27-.06-.45-.2-.58-.13-.13-.31-.19-.58-.19-.09 0-.19.02-.3.05-.11.03-.17.09-.25.16-.08.07-.14.11-.2.2a.41.41 0 0 0-.09.28H5.028c0-.38.13-.56.27-.83.16-.27.36-.499.61-.669.25-.17.549-.3.879-.38.33-.08.7-.13 1.09-.13.439 0 .829.05 1.168.13.34.09.63.22.88.39.23.17.41.38.55.63.13.25.19.55.19.88 0 .22 0 .419-.08.589l-.02-.01z",
    }),
    viewBox: "0 0 16 16",
    width: 16,
  },
  update: {
    width: 12,
    height: 12,
    viewBox: "0 0 12 12",
    pathSolid: (
      <>
        <path d="M11.852 0.648302C11.7531 0.54946 11.6359 0.499875 11.5004 0.499875C11.365 0.499875 11.248 0.54946 11.1489 0.648302L10.1333 1.65629C9.576 1.13017 8.93788 0.722516 8.21917 0.433515C7.50051 0.144487 6.7607 0 6.00023 0C4.56785 0 3.31788 0.433542 2.2501 1.30082C1.1823 2.16799 0.479183 3.29942 0.140625 4.69538V4.75003C0.140625 4.81773 0.165335 4.87637 0.214838 4.92579C0.264342 4.9753 0.322902 5.00001 0.390603 5.00001H1.94531C2.05994 5.00001 2.13801 4.94008 2.17972 4.82036C2.39844 4.29955 2.53642 3.99487 2.59377 3.90629C2.95833 3.31252 3.44271 2.84633 4.04693 2.50778C4.6512 2.16924 5.30224 1.99991 6.00012 1.99991C7.04713 1.99991 7.95597 2.35672 8.7269 3.07029L7.64856 4.14838C7.54985 4.24736 7.50021 4.36462 7.50021 4.50005C7.50021 4.63545 7.54982 4.75266 7.64856 4.85161C7.74764 4.95059 7.86488 5.00004 8.0003 5.00004H11.5004C11.6359 5.00004 11.7531 4.95059 11.852 4.85161C11.9509 4.75266 12.0003 4.63548 12.0003 4.50005V0.999887C12.0003 0.864458 11.9512 0.747308 11.852 0.648302Z" />
        <path d="M11.5549 6.99935H10.0549C9.94026 6.99935 9.86213 7.0592 9.82056 7.17903C9.60192 7.69989 9.46378 8.00444 9.40656 8.09302C9.04189 8.68665 8.55748 9.153 7.95343 9.49153C7.34913 9.83017 6.6981 9.99931 6.00018 9.99931C5.495 9.99931 5.00796 9.90564 4.53925 9.71819C4.07049 9.53052 3.65116 9.26484 3.2814 8.92105L4.35178 7.85076C4.4507 7.75189 4.50021 7.63455 4.50021 7.4992C4.50021 7.3638 4.4507 7.24646 4.35178 7.14764C4.2528 7.04858 4.13565 6.99902 4.00022 6.99902H0.500065C0.364555 6.99902 0.247351 7.04858 0.1484 7.14764C0.0494483 7.24648 0 7.36382 0 7.49923V10.9994C0 11.1347 0.0494483 11.252 0.148427 11.3509C0.24746 11.4497 0.364664 11.4995 0.500093 11.4995C0.635494 11.4995 0.752643 11.4498 0.851649 11.3509L1.85947 10.343C2.41681 10.8742 3.05099 11.2831 3.76196 11.5696C4.47292 11.8562 5.20863 11.9993 5.96901 11.9993C7.39623 11.9993 8.641 11.5657 9.70352 10.6985C10.766 9.83135 11.464 8.6997 11.7975 7.30387C11.8027 7.29347 11.8052 7.27522 11.8052 7.24928C11.8052 7.18147 11.7804 7.12307 11.7309 7.0734C11.6813 7.02417 11.6227 6.99935 11.5549 6.99935Z" />
      </>
    ),
  },
  verified: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M15.67 7.066l-1.08-1.34a1.5 1.5 0 0 1-.309-.77l-.19-1.698a1.51 1.51 0 0 0-1.329-1.33l-1.699-.19c-.3-.03-.56-.159-.78-.329L8.945.33a1.504 1.504 0 0 0-1.878 0l-1.34 1.08a1.5 1.5 0 0 1-.77.31l-1.698.19c-.7.08-1.25.63-1.33 1.329l-.19 1.699c-.03.3-.159.56-.329.78L.33 7.055a1.504 1.504 0 0 0 0 1.878l1.08 1.34c.17.22.28.48.31.77l.19 1.698c.08.7.63 1.25 1.329 1.33l1.699.19c.3.03.56.159.78.329l1.339 1.08c.55.439 1.329.439 1.878 0l1.34-1.08c.22-.17.48-.28.77-.31l1.698-.19c.7-.08 1.25-.63 1.33-1.329l.19-1.699c.03-.3.159-.56.329-.78l1.08-1.339a1.504 1.504 0 0 0 0-1.878zm-9.164 4.936L3.008 8.505l1.5-1.5 1.998 2 4.997-4.997 1.499 1.549-6.496 6.445z",
    }),
    viewBox: "0 0 16 16",
    width: 16,
  },
  video: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M9 5.625L12.375 2.25H10.125L6.75 5.625H9ZM15.75 6.75V14.6071C15.75 15.2383 15.2383 15.75 14.6071 15.75H3.39286C2.76168 15.75 2.25 15.2383 2.25 14.6071V6.75H15.75ZM9 2.25L5.625 5.625H3.375L6.75 2.25H9ZM3.41666 2.25453C2.75 2.25453 2.25 2.60507 2.25 3.07245V5.62488L5.625 2.25465L3.41666 2.25453ZM12.375 5.625L15.4206 2.5794C15.217 2.37588 14.9358 2.25 14.6252 2.25H13.5L10.125 5.625H12.375ZM15.75 5.625H13.5L15.75 3.375V5.625ZM7.875 9.3737V13.1263C7.875 13.286 8.05303 13.3813 8.18594 13.2927L11.0004 11.4164C11.1191 11.3372 11.1191 11.1628 11.0004 11.0836L8.18594 9.20729C8.05303 9.11869 7.875 9.21396 7.875 9.3737Z",
    }),
    viewBox: "0 0 16 16",
    width: 16,
  },
  "volume-up": {
    height: 32,
    viewBox: "0 0 32 32",
    width: 32,
    fill: "none",
    pathSolid: () => {
      const maskId1 = uuid4();
      return (
        <>
          <g>
            <mask
              id={maskId1}
              style={{ maskType: "alpha" }}
              maskUnits="userSpaceOnUse"
              x="0"
              y="0"
              width="32"
              height="32"
            >
              <rect id="Bounding box" width="32" height="32" fill="#D9D9D9" />
            </mask>
            <g mask={`url(#${maskId1})`}>
              <path
                d="M18.6667 27.6334V24.9C20.6667 24.3223 22.2778 23.2112 23.5 21.5667C24.7222 19.9223 25.3333 18.0556 25.3333 15.9667C25.3333 13.8778 24.7222 12.0112 23.5 10.3667C22.2778 8.72227 20.6667 7.61116 18.6667 7.03338V4.30005C21.4222 4.92227 23.6667 6.31672 25.4 8.48338C27.1333 10.65 28 13.1445 28 15.9667C28 18.7889 27.1333 21.2834 25.4 23.4501C23.6667 25.6167 21.4222 27.0112 18.6667 27.6334ZM4 20V12H9.33333L16 5.33338V26.6667L9.33333 20H4ZM18.6667 21.3334V10.6C19.7111 11.0889 20.5278 11.8223 21.1167 12.8C21.7056 13.7778 22 14.8445 22 16C22 17.1334 21.7056 18.1834 21.1167 19.15C20.5278 20.1167 19.7111 20.8445 18.6667 21.3334ZM13.3333 11.8L10.4667 14.6667H6.66667V17.3334H10.4667L13.3333 20.2V11.8Z"
                fill="white"
              />
            </g>
          </g>
        </>
      );
    },
  },
  "website-link": {
    height: 36,
    width: 36,
    viewBox: "0 0 36 36",
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M20.1444 11.39128C21.5891 9.94656 23.9314 9.94656 25.3761 11.39128C26.8209 12.83599 26.8209 15.1783 25.376 16.6231L22.0124 19.9867C21.5393 20.46 20.9466 20.7961 20.2975 20.9591H20.2685L20.1152 20.9908L20.0307 21.0066L19.8483 21.0331H19.7585C19.7187 21.0331 19.6815 21.0375 19.646 21.0418C19.6202 21.0448 19.5953 21.0478 19.5709 21.0489L19.4124 21.0674H19.3965H19.2116C19.0797 21.0621 18.9483 21.0498 18.8178 21.0304C18.7095 21.0119 18.6038 20.9908 18.4981 20.9643L18.3422 20.9221C18.2867 20.9062 18.2286 20.8877 18.1731 20.8666C18.1528 20.8588 18.1321 20.8515 18.1114 20.844C18.0754 20.8312 18.0392 20.8182 18.004 20.8032C17.9855 20.7953 17.967 20.7876 17.9485 20.78C17.9115 20.7647 17.8746 20.7494 17.8376 20.7318C17.4443 20.5496 17.0865 20.2991 16.7806 19.992C16.5664 19.7773 16.4548 19.4808 16.4741 19.1782C16.4911 18.923 16.6 18.6827 16.7806 18.5017C17.1978 18.1091 17.8485 18.1091 18.2656 18.5017C18.8846 19.1199 19.8873 19.1199 20.5063 18.5017L21.373 17.6403L21.3941 17.6166L23.8726 15.1407C24.4921 14.5212 24.4921 13.51687 23.8726 12.89739C23.2531 12.27792 22.2488 12.27792 21.6293 12.89739L19.9805 14.5441C19.9051 14.62 19.7914 14.6429 19.6925 14.6022C19.1566 14.383 18.583 14.2709 18.004 14.2719H17.9063C17.7983 14.274 17.7 14.2102 17.6579 14.1107C17.6153 14.0116 17.6373 13.89656 17.7134 13.82008L20.1444 11.39128ZM19.3605 15.7247C19.5853 15.8676 19.7936 16.0351 19.9814 16.2241C20.1956 16.4388 20.3072 16.7352 20.2879 17.0379C20.2697 17.2912 20.1609 17.5294 19.9814 17.709C19.5643 18.1016 18.9136 18.1016 18.4965 17.709C17.8775 17.0908 16.8747 17.0908 16.2558 17.709L12.88152 21.0806C12.26204 21.7001 12.26204 22.7044 12.88152 23.3239C13.501 23.9434 14.5054 23.9434 15.1248 23.3239L17.0099 21.6664C17.0853 21.5905 17.199 21.5675 17.2979 21.6082C17.8349 21.8266 18.4093 21.9379 18.989 21.9359H19.0947C19.2406 21.935 19.3596 22.0526 19.3605 22.1986C19.3609 22.2696 19.3327 22.3377 19.2823 22.3877L16.6177 24.8221C15.9252 25.5155 14.9844 25.9036 14.0045 25.9002C11.96144 25.8989 10.30632 24.2416 10.30762 22.1986C10.30824 21.2195 10.69697 20.2807 11.38859 19.5877L14.7496 16.2241C15.4424 15.5288 16.384 15.1389 17.3655 15.1407C18.0726 15.1407 18.7649 15.3433 19.3605 15.7247Z",
    }),
  },
  x: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M7.48 8l3.75 3.75-1.48 1.48L6 9.48l-3.75 3.75-1.48-1.48L4.52 8 .77 4.25l1.48-1.48L6 6.52l3.75-3.75 1.48 1.48L7.48 8z",
    }),
    viewBox: "0 0 12 16",
    width: 12,
  },
  youtube: {
    height: 36,
    width: 36,
    viewBox: "0 0 36 36",
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M25.2775 13.1564C24.8164 12.3142 24.3159 12.1593 23.297 12.1004C22.2791 12.0295 19.7196 12 17.5021 12C15.2804 12 12.7198 12.0295 11.703 12.0993C10.6862 12.1593 10.1847 12.3131 9.71931 13.1564C9.24437 13.9975 9 15.4462 9 17.9967C9 17.9989 9 18 9 18C9 18.0022 9 18.0033 9 18.0033V18.0055C9 20.5451 9.24437 22.0047 9.71931 22.8371C10.1847 23.6793 10.6851 23.832 11.7019 23.9029C12.7198 23.964 15.2804 24 17.5021 24C19.7196 24 22.2791 23.964 23.2981 23.904C24.317 23.8331 24.8174 23.6804 25.2786 22.8382C25.7578 22.0058 26 20.5462 26 18.0065C26 18.0065 26 18.0033 26 18.0011C26 18.0011 26 17.9989 26 17.9978C26 15.4462 25.7577 13.9975 25.2775 13.1564ZM15.375 21.2727V14.7273L20.6875 18L15.375 21.2727Z",
    }),
  },
  zap: {
    height: 16,
    pathSolid: React.createElement("path", {
      fillRule: "evenodd",
      d: "M10 7H6l3-7-9 9h4l-3 7 9-9z",
    }),
    viewBox: "0 0 10 16",
    width: 10,
  },
};
/* tslint:enable:max-line-length */

export type IconSymbol = keyof typeof ICONS;

export interface IconProps {
  /** Fill color for the icon; defaults to 'currentColor' */
  fill?: string;
  name: IconSymbol;
  zoom?: string;
  className?: string;
  title?: MessageDescriptor;
  /**
   * If true and pathOutline is defined for the icon, uses pathOutline,
   * otherwise uses icon.pathSolid. Defaults to false.
   */
  outline?: boolean;
  /**
   * If in default/solid style (not outline style),
   * will use this color for the outline stroke.
   * If not defined, will default to the fill color.
   */
  outlineColor?: string;
  showSkeleton?: boolean;
}

const Icon: React.FC<IconProps> = (props) => {
  const {
    name,
    outline,
    className,
    zoom,
    fill,
    outlineColor,
    title,
    showSkeleton,
  } = props;

  // Ensure the icon exists in the ICONS set
  if (!(name in ICONS)) {
    console.error(`Invalid icon name passed to Icon component: ${name}`);
    return null;
  }

  const icon: IconProperties = {
    ...ICONS[name],
    height: Number(ICONS[name].height),
    width: Number(ICONS[name].width),
  };
  const calculatedFill = fill || "currentColor";

  // Calculate the zoom dimensions
  const { width, height } = React.useMemo(() => {
    if (zoom) {
      if (zoom[0] === "x") {
        const z = parseFloat(zoom.slice(1));
        return {
          width: Math.floor(icon.width * z),
          height: Math.floor(icon.height * z),
        };
      } else {
        return {
          width: zoom,
          height: zoom,
        };
      }
    }
    return { width: icon.width, height: icon.height };
  }, [zoom, icon.width, icon.height]);

  const path = outline && icon.pathOutline ? icon.pathOutline : icon.pathSolid;

  // Render a skeleton if required
  if (showSkeleton) {
    return (
      <Skeleton
        circle={true}
        className={`icon ${className || ""}`}
        width={Number(width)}
        height={Number(height)}
      />
    );
  }

  // Render the SVG
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={`icon ${className || ""}`}
      viewBox={icon.viewBox}
      width={width}
      height={height}
      aria-hidden={title ? undefined : true}
      version="1.1"
      fill={calculatedFill}
    >
      {typeof path === "function"
        ? path({ fill: calculatedFill, outlineColor })
        : path}
      {title ? (
        <title>
          <WrappedMessage message={title} />
        </title>
      ) : null}
    </svg>
  );
};

export { Icon };
